/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactFlow, {
  Background,
  Controls,
  Handle,
  Position,
  MarkerType,
  getSmoothStepPath,
  getViewportForBounds,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
  type ReactFlowInstance,
} from "reactflow";
import {
  Server,
  Network,
  EthernetPort,
  Shield,
  Route,
  Box,
  Cloud,
  Download,
  ChevronDown,
  Search,
  Cloud as CloudIcon,
  Building2,
  Filter,
  Layers,
  Globe,
  MapPin,
  GitBranch,
  ArrowUpRight,
  X,
  ChevronLeft,
  ChevronRight,
  Activity,
} from "lucide-react";

import "reactflow/dist/style.css";
import { toPng, toSvg } from "html-to-image";
import GenericModal from "../../ui-components/Modal/GenericModal";
import { AIAssistantPanel } from "./AIAssistantPanel";
import { useAIAssistant } from "./hooks/useAIAssistant";
import { NetworkSecurityPanel, type SecurityResource } from "./NetworkSecurityPanel";
import { useNetworkSecurity, type NetworkSecurityFinding } from "./hooks/useNetworkSecurity";
import { TrafficAnalyzer } from "./traffic-analyzer";
import { TGWVisualizationModal, TGWVisualizationButton } from "./tgw-visualization";
import { useAppSelector } from "@redux/hooks";
import { RootState } from "@redux/store/store";
import { useInsightDataPath } from "../../insight/hooks/useInsightDataPath";

// --- Types ---

type TopologyPayload = {
  nodes: Node[];
  edges: Edge[];
};

type GenericNodeData = {
  label?: string;
  kind?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

type LaneEdgeData = {
  laneOffset?: number;
  borderRadius?: number;
};

// --- Filter System Types ---

type CloudProvider = {
  id: string;
  name: string;
  icon: string;
  regions: Region[];
};

type Region = {
  id: string;
  name: string;
  accounts: Account[];
};

type Account = {
  id: string;
  name: string;
  topologyFile: string;
  discoveryFile: string;
  isEmpty?: boolean;
  vpcCount?: number;
};

type TopologyIndex = {
  providers: CloudProvider[];
};

type FilterState = {
  provider: CloudProvider | null;
  region: Region | null;
  account: Account | null;
};

// =============================================================================
// SEARCH TYPES
// =============================================================================

type SearchResultType = 'subnet' | 'eni' | 'instance' | 'sg' | 'rtb' | 'natgw' | 'igw' | 'vpc' | 'az';

interface SearchResult {
  id: string;
  type: SearchResultType;
  label: string;
  sublabel?: string;
  nodeId: string;
  matchField: string;
}

const RtbLaneEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  data,
}: EdgeProps<LaneEdgeData>) => {
  const laneOffset = Number(data?.laneOffset ?? 0);
  const borderRadius = Number(data?.borderRadius ?? 15);

  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius,
    offset: laneOffset,
  });

  return (
    <path
      id={id}
      className="react-flow__edge-path"
      d={edgePath}
      style={style}
      markerEnd={markerEnd}
    />
  );
};

// --- Custom Nodes ---

// Helper type to include style which comes from React Flow but might be missing in strict NodeProps depending on version
type CustomNodeProps = NodeProps<GenericNodeData> & { style?: React.CSSProperties };

// Utility to strip unwanted inline styles from ELK/JSON, keeping only layout
const getLayoutStyles = (style?: React.CSSProperties) => {
  if (!style) return {};
  // Fix: Pass through background and border to allow overrides (e.g. transparent containers)
  return {
    width: style.width,
    height: style.height,
    background: style.background,
    backgroundColor: style.backgroundColor,
    border: style.border,
    borderColor: style.borderColor,
    borderRadius: style.borderRadius,
  };
};

/**
 * Group Node (Account / Region)
 * Style: Very subtle, transparent, just a label.
 */
/**
 * Group Node (Account / Region)
 * Style: MINIMALIST. User complained about repetition.
 * We make it invisible by default (layout only), visible on hover for context.
 */
const GroupNode = ({ data, style }: CustomNodeProps) => {
  const isRtbContainer = data.type === 'rtb-container';
  const isRtbWrapper = data.type === 'rtb-wrapper';
  // JSON may set data.type/data.kind, or we set kind in processedNodes; fallback to label (e.g. "ACCOUNT: ..." vs "REGION: ...")
  const label = (data.label ?? '') as string;
  const nodeKind = data.type ?? data.kind ?? (label.toUpperCase().startsWith('ACCOUNT:') ? 'account' : label.toUpperCase().startsWith('REGION:') ? 'region' : null);
  const isAccount = nodeKind === 'account';
  const isRegion = nodeKind === 'region';
  const showBanner = data.label && (isRtbContainer || !data.transparent);
  const layoutStyle = getLayoutStyles(style);
  const minSize = { minWidth: style?.width, minHeight: style?.height };
  // Theme-aware: no inline colors so light/dark mode both work
  const effectiveStyle = data.transparent && !isRtbContainer && !isRtbWrapper
    ? { ...layoutStyle, ...minSize, border: 'none', background: 'transparent', backgroundColor: 'transparent', boxShadow: 'none' }
    : { ...layoutStyle, ...minSize };

  return (
    <div
      style={effectiveStyle}
      className={`relative h-full w-full rounded-3xl p-4 transition-all group ${
        isRtbContainer
          ? 'border-2 border-emerald-200/70 bg-emerald-50/20 dark:border-semantic-border-primary dark:bg-semantic-bg-secondary'
          : data.transparent
            ? '!border-transparent !bg-transparent'
            : isAccount
              ? 'border-2 border-dashed border-slate-200/50 bg-transparent hover:border-slate-400 dark:border-semantic-border-primary dark:bg-semantic-bg-primary dark:hover:border-semantic-border-secondary'
              : isRegion
                ? 'border-2 border-dashed border-slate-200/50 bg-transparent hover:border-slate-400 dark:border-semantic-border-primary dark:bg-semantic-bg-secondary dark:hover:border-semantic-border-secondary'
                : 'border-2 border-dashed border-slate-200/50 bg-transparent hover:border-slate-400 dark:border-semantic-border-primary dark:hover:border-semantic-border-secondary'
      }`}
    >
      {showBanner && (
        <div className={`absolute -top-3 left-6 px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full border transition-colors ${
          isRtbContainer 
            ? 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-semantic-bg-success-secondary dark:text-semantic-text-success dark:border-semantic-border-success-primary' 
            : 'bg-white/80 text-slate-300 group-hover:text-slate-600 border-transparent group-hover:border-slate-200 dark:bg-semantic-bg-secondary dark:text-semantic-text-secondary dark:group-hover:text-semantic-text-primary dark:border-semantic-border-primary'
        }`}>
          {data.label}
        </div>
      )}
      {/* Handles */}
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};

/**
 * VpcNode
 * Style: Solid 2px Green border, rounded-lg, transparent green bg.
 * Header: Floating label top-left.
 */
const VpcNode = ({ data, style }: CustomNodeProps) => {
  // Split label by newlines: [0] = "VPC: NAME", [1] = "VPC_ID", [2] = "CIDR"
  const lines = (data.label || '').split('\n');
  const vpcName = lines[0]?.replace('VPC: ', '').trim() || '';
  const vpcId = lines[1]?.trim() || '';
  const cidr = lines[2]?.trim() || '';

  return (
    <div
      style={{
        ...getLayoutStyles(style),
        minWidth: style?.width,
        minHeight: style?.height
      }}
      className="relative h-full w-full rounded-2xl border-2 border-green-500 bg-green-50/30 shadow-sm dark:border-semantic-border-success-primary dark:bg-semantic-bg-secondary"
    >
      <div className="absolute -top-4 left-6 flex items-center gap-2 rounded-lg bg-green-600 px-4 py-1.5 shadow-md z-10 dark:bg-semantic-bg-success-bold">
        <Cloud className="h-3.5 w-3.5 text-white dark:text-semantic-text-inverse-primary" />
        <span className="text-[11px] font-bold text-white tracking-wide dark:text-semantic-text-inverse-primary">VPC</span>
      </div>
      <div className="pt-6 pl-6 flex flex-col gap-0.5">
        <div className="text-sm font-bold text-green-900/90 dark:text-semantic-text-primary">
          {vpcName}
        </div>
        <div className="text-[10px] text-green-700/70 font-mono dark:text-semantic-text-secondary">
          {vpcId}
        </div>
        <div className="text-[10px] text-green-600/80 font-mono font-medium dark:text-semantic-text-secondary">
          {cidr}
        </div>
      </div>

      {/* Handles */}
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};

/**
 * AzNode (Availability Zone)
 * Style: Dashed Blue/Slate border, lighter than VPC.
 */
const AzNode = ({ data, style }: CustomNodeProps) => {
  return (
    <div
      style={{
        ...getLayoutStyles(style),
        minWidth: style?.width,
        minHeight: style?.height
      }}
      className="relative h-full w-full rounded-xl border-2 border-dotted border-indigo-300 bg-transparent dark:border-semantic-border-secondary dark:bg-semantic-bg-tertiary"
    >
      <div className="absolute -top-3 right-6 bg-indigo-50 px-2 py-0.5 border border-indigo-100 rounded text-[9px] font-bold text-indigo-500 uppercase tracking-wide dark:bg-semantic-bg-tertiary dark:border-semantic-border-primary dark:text-semantic-text-secondary">
        {data.label}
      </div>
    </div>
  );
};

/**
 * SubnetNode
 * Style: Dashed 2px Blue border, blue transparent bg.
 * Header: "Subnet" label inside.
 */
const SubnetNode = ({ id, data, style }: CustomNodeProps) => {
  // Security glow effect for OCI subnets with affected security lists
  // No animate-pulse to avoid confusion with dimmed elements
  const glowEffect = data.isGlowing ? {
    critical: 'ring-4 ring-red-400 ring-offset-2 shadow-[0_0_30px_rgba(239,68,68,0.7)]',
    high: 'ring-4 ring-orange-400 ring-offset-2 shadow-[0_0_30px_rgba(249,115,22,0.7)]',
    medium: 'ring-4 ring-yellow-400 ring-offset-2 shadow-[0_0_30px_rgba(234,179,8,0.7)]',
    low: 'ring-4 ring-green-400 ring-offset-2 shadow-[0_0_30px_rgba(34,197,94,0.7)]',
  }[data.glowSeverity as string] || '' : '';
  
  // Security ring for subnets with findings
  const securityRing = data.securityEnabled && data.severity && !data.isGlowing ? {
    critical: 'ring-2 ring-red-500 ring-offset-1',
    high: 'ring-2 ring-orange-500 ring-offset-1',
    medium: 'ring-1 ring-yellow-500',
    low: 'ring-1 ring-green-500',
  }[data.severity as string] || '' : '';

  // Search highlight effect
  const searchHighlight = data.isSearchHighlighted 
    ? 'ring-4 ring-indigo-500 ring-offset-2 shadow-[0_0_25px_rgba(99,102,241,0.6)] animate-pulse' 
    : '';

  return (
    <div
      style={{ ...getLayoutStyles(style) }}
      className={`relative h-full w-full rounded-lg border-2 border-dashed border-blue-500 bg-blue-50/50 transition-all duration-300 dark:border-semantic-border-brand-primary dark:bg-semantic-bg-secondary ${glowEffect} ${securityRing} ${searchHighlight}`}
    >
      {/* Route Table Badge (Top) - Uses RTB color if available - Clickable! */}
      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-50">
        {data.routeTable ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (typeof data.onRouteTableClick === 'function') {
                data.onRouteTableClick(data.routeTable);
              }
            }}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full shadow-md border-2 cursor-pointer hover:opacity-80 transition-opacity"
            style={{
              backgroundColor: data.routeTable.color?.bg || 'var(--color-semantic-bg-success-secondary)',
              borderColor: data.routeTable.color?.border || 'var(--color-semantic-border-success-primary)',
            }}
            title={`Click to view Route Table: ${data.routeTable.name || data.routeTable.id}`}
          >
            <Route 
              className="w-3 h-3" 
              style={{ color: data.routeTable.color?.text || 'var(--color-semantic-text-success)' }}
            />
            <span 
              className="text-[10px] font-bold font-sans tracking-tight whitespace-nowrap"
              style={{ color: data.routeTable.color?.text || 'var(--color-semantic-text-success)' }}
            >
              {data.routeTable.name}
            </span>
          </button>
        ) : (
          <div className="flex items-center gap-1.5 bg-white px-3 py-1 rounded-full border-2 border-red-400 shadow-md dark:bg-semantic-bg-tertiary dark:border-semantic-border-danger-primary">
            <span className="text-[10px] font-bold text-red-500 font-sans tracking-tight whitespace-nowrap dark:text-semantic-text-danger">
              NO ROUTE TABLE
            </span>
          </div>
        )}
      </div>

      {/* Bottom Badges (OCI Security Lists / AWS NACL) */}
      {( (data.securityLists && Array.isArray(data.securityLists) && data.securityLists.length > 0) || data.networkAcl ) && (
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 z-50 flex flex-col gap-2">
          {/* Security Lists Badge (OCI) */}
          {data.securityLists && Array.isArray(data.securityLists) && data.securityLists.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (typeof data.onSecurityListClick === 'function') {
                  data.onSecurityListClick(data.securityLists);
                }
              }}
              className="flex items-center gap-1.5 bg-white px-3 py-1 rounded-full border border-purple-500 shadow-md hover:bg-purple-50 dark:bg-semantic-bg-tertiary dark:border-semantic-border-brand-primary dark:hover:bg-semantic-bg-brand-secondary"
              title="Click to view Security List details"
            >
              <Shield className="w-3 h-3 text-purple-600 dark:text-semantic-text-brand" />
              <span className="text-[10px] font-bold text-purple-800 font-sans tracking-tight whitespace-nowrap dark:text-semantic-text-brand">
                {(() => {
                  const first = data.securityLists[0];
                  const firstName =
                    (first && typeof first === 'object' && (first.name || first.display_name || first.label)) ||
                    null;
                  const more = data.securityLists.length > 1 ? ` +${data.securityLists.length - 1}` : '';
                  return `${firstName || data.securityLists.length}${more}`;
                })()}
              </span>
            </button>
          )}

          {/* Network ACL Badge (AWS) */}
          {data.networkAcl && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (typeof data.onNetworkAclClick === 'function') {
                  data.onNetworkAclClick(data.networkAcl);
                }
              }}
              className="flex items-center gap-1.5 bg-white px-3 py-1 rounded-full border border-orange-500 shadow-md hover:bg-orange-50 dark:bg-semantic-bg-secondary dark:border-semantic-border-primary dark:hover:bg-semantic-bg-tertiary dark:text-semantic-text-primary"
              title="Click to view Network ACL details"
            >
              <Shield className="w-3 h-3 text-orange-600 dark:text-semantic-text-primary" />
              <span className="text-[10px] font-bold text-orange-800 font-sans tracking-tight whitespace-nowrap dark:text-semantic-text-primary">
                {(() => {
                  const nm =
                    (data.networkAcl && typeof data.networkAcl === 'object' && (data.networkAcl.name || data.networkAcl.id)) ||
                    'NACL';
                  return nm;
                })()}
              </span>
            </button>
          )}
        </div>
      )}

      {/* SUBNET Label */}
      <div className="absolute top-0 left-0 bg-blue-100 px-2 py-1 rounded-br-lg text-[9px] font-bold text-blue-700 tracking-tight border-r border-b border-blue-200/50 z-10 dark:bg-semantic-bg-tertiary dark:text-semantic-text-primary dark:border-semantic-border-primary">
        SUBNET
      </div>

      {/* Main Content */}
      <div className="absolute inset-0 pt-8 px-3 flex flex-col items-start justify-start gap-1 pointer-events-none">
        {/* Name - Bold, slightly larger */}
        <div className="text-[11px] font-mono font-bold text-blue-900 truncate w-full select-text pointer-events-auto leading-tight dark:text-semantic-text-primary" title={data.label?.split('\n')[0]}>
          {data.label?.split('\n')[0] || data.label}
        </div>
        {/* ID - Medium weight */}
        <div className="text-[9px] font-mono font-medium text-blue-800 select-text pointer-events-auto leading-none truncate dark:text-semantic-text-secondary">
          {id}
        </div>
        {/* CIDR - Medium weight */}
        <div className="text-[9px] font-mono font-medium text-blue-700 select-text pointer-events-auto leading-none truncate dark:text-semantic-text-secondary">
          {data.label?.split('\n')[1] || ''}
        </div>
      </div>

      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="source" id="t-src" position={Position.Top} style={{ opacity: 0 }} />
    </div>
  );
};

/**
 * PathNode (Direct Connect path nodes)
 * Style: Dark-mode-friendly card for dx_vif, pop, dc_router (Direct Connect, PoP, DC router).
 * Uses semantic tokens so it fits both light and dark theme.
 */
const PathNode = ({ data, style }: CustomNodeProps) => {
  const lines = String(data?.label ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const title = lines[0] ?? data?.id ?? '';

  return (
    <div
      style={{ ...getLayoutStyles(style) }}
      className="flex h-full w-full flex-col items-center justify-center rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary shadow-sm transition-all duration-300 hover:shadow-md"
    >
      <div className="mb-1.5">
        <Box className="w-7 h-7 text-semantic-text-secondary" />
      </div>
      <div className="w-full px-2 text-center">
        <div className="break-words whitespace-normal text-[10px] font-semibold leading-tight text-semantic-text-primary" title={data?.label}>
          {title}
        </div>
        {lines.length > 1 && (
          <div className="mt-0.5 space-y-0.5">
            {lines.slice(1).map((line, i) => (
              <div key={i} className="text-[9px] font-mono text-semantic-text-secondary truncate" title={line}>
                {line}
              </div>
            ))}
          </div>
        )}
      </div>
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};

/**
 * ResourceNode
 * Style: White card, shadow, large icon + label.
 * Types: instance, eni, sg, rtb
 */
const ResourceNode = ({ data }: CustomNodeProps) => {
  const [subnetsExpanded, setSubnetsExpanded] = useState(false);
  
  let Icon = <Box className="w-8 h-8 text-slate-300 dark:text-semantic-text-secondary" />;
  const kind = data.kind || data.type; // Fallback
  const id = String(data.id || "");

  // Mapping based on 'kind' or 'type' from JSON
  // IMPORTANT: Avoid heuristics based on label text (RTB names can contain 'i-' etc).
  // Use kind or ID prefix only.
  if (kind === 'instance' || id.startsWith('i-')) {
    Icon = <Server className="w-7 h-7 text-orange-500" strokeWidth={1.5} />;
  } else if (kind === 'eni' || kind === 'vnic' || id.startsWith('eni-') || id.startsWith('vnic-')) {
    // ENI/VNIC: interface/port icon
    Icon = <EthernetPort className="w-7 h-7 text-orange-500" strokeWidth={1.5} />;
  } else if (kind === 'sg' || (data.label && data.label.includes('SG'))) {
    Icon = <Shield className="w-7 h-7 text-rose-500" strokeWidth={1.5} />;
  } else if (kind === 'rtb' || kind === 'route_table') {
    Icon = <Route className="w-7 h-7 text-emerald-500" strokeWidth={1.5} />;
  } else if (kind === 'igw') {
    Icon = <Cloud className="w-7 h-7 text-sky-500" strokeWidth={1.5} />;
  } else if (kind === 'nat') {
    Icon = <Network className="w-7 h-7 text-cyan-500" strokeWidth={1.5} />;
  } else if (kind === 'tgw') {
    Icon = <Route className="w-7 h-7 text-purple-500" strokeWidth={1.5} />;
  } else if (kind === 'drg') {
    // OCI DRG
    Icon = <Route className="w-7 h-7 text-purple-500" strokeWidth={1.5} />;
  } else if (kind === 'sgw') {
    // OCI Service Gateway
    Icon = <Cloud className="w-7 h-7 text-indigo-500" strokeWidth={1.5} />;
  } else if (kind === 'vgw') {
    Icon = <Cloud className="w-7 h-7 text-indigo-500" strokeWidth={1.5} />;
  } else if (kind === 'vpc') {
    Icon = <Cloud className="w-7 h-7 text-blue-500" strokeWidth={1.5} />;
  }

  // Determine if it's a "dangling" or "unknown" node to style differently
  const isUnknown = data.id?.includes('unknown') || data.label?.includes('dangling');

  const bottomTargetHandles =
    (kind === 'rtb' || kind === 'route_table')
      ? Math.max(1, Number(data.bottomTargetHandles ?? 1))
      : 0;

  const isRtb = kind === 'rtb' || kind === 'route_table';
  const gatewayTopSourceHandles = isRtb ? Math.max(1, Number(data.gatewayTopSourceHandles ?? 1)) : 0;
  const isGateway = kind === 'igw' || kind === 'nat' || kind === 'tgw' || kind === 'vgw' || kind === 'drg' || kind === 'sgw';

  const renderIfaceLayout = () => {
    const lines = String(data.label || "").split("\n").filter((l) => l !== "");
    const title = lines[0] || id;
    const rest = lines.slice(1);
    
    // Detectar si es AWS
    const isAWS = data.provider === 'aws' || kind === 'eni';

    // Extract the type from metadata for the banner
    // AWS: has explicit "type:" line (interface, nat_gateway, network_load_balancer, etc.)
    // OCI: no type field, but we can infer from "inst:" presence
    const typeMatch = rest.find(line => line.match(/^\s*type:\s*/));
    const instMatch = rest.find(line => line.match(/^\s*inst:\s*/));
    
    // Detect if this is an OCI VNIC (provider === 'oci' or id starts with 'vnic-')
    const isOCI = data.provider === 'oci' || id.startsWith('vnic-');
    
    let eniType: string | null = null;
    if (typeMatch) {
      // AWS - explicit type
      eniType = typeMatch.replace(/^\s*type:\s*/, '').trim();
    } else if (isOCI) {
      // OCI - infer type from instance attachment
      eniType = instMatch ? 'oci_instance' : 'oci_standalone';
    }
    
    // Type banner: original light colors + dark mode semantic
    const typeBannerConfig: Record<string, { label: string; bg: string; text: string; border: string }> = {
      'interface': { label: 'INSTANCE', bg: 'bg-emerald-50 dark:bg-semantic-bg-success-secondary', text: 'text-emerald-700 dark:text-semantic-text-success', border: 'border-emerald-300 dark:border-semantic-border-success-primary' },
      'nat_gateway': { label: 'NAT_GATEWAY', bg: 'bg-cyan-50 dark:bg-semantic-bg-tertiary', text: 'text-cyan-700 dark:text-semantic-text-primary', border: 'border-cyan-300 dark:border-semantic-border-primary' },
      'network_load_balancer': { label: 'NLB', bg: 'bg-blue-50 dark:bg-semantic-bg-brand-secondary', text: 'text-blue-700 dark:text-semantic-text-brand', border: 'border-blue-300 dark:border-semantic-border-brand-primary' },
      'gateway_load_balancer': { label: 'GWLB', bg: 'bg-indigo-50 dark:bg-semantic-bg-tertiary', text: 'text-indigo-700 dark:text-semantic-text-primary', border: 'border-indigo-300 dark:border-semantic-border-primary' },
      'efs': { label: 'EFS', bg: 'bg-amber-50 dark:bg-semantic-bg-warning-secondary', text: 'text-amber-700 dark:text-semantic-text-warning', border: 'border-amber-300 dark:border-semantic-border-warning-primary' },
      'lambda': { label: 'LAMBDA', bg: 'bg-orange-50 dark:bg-semantic-bg-warning-secondary', text: 'text-orange-700 dark:text-semantic-text-warning', border: 'border-orange-300 dark:border-semantic-border-warning-primary' },
      'vpce': { label: 'VPC_ENDPOINT', bg: 'bg-violet-50 dark:bg-semantic-bg-brand-secondary', text: 'text-violet-700 dark:text-semantic-text-brand', border: 'border-violet-300 dark:border-semantic-border-brand-primary' },
      'vpc_endpoint': { label: 'VPC_ENDPOINT', bg: 'bg-violet-50 dark:bg-semantic-bg-brand-secondary', text: 'text-violet-700 dark:text-semantic-text-brand', border: 'border-violet-300 dark:border-semantic-border-brand-primary' },
      'tgw_attachment': { label: 'TRANSIT_GATEWAY', bg: 'bg-violet-50 dark:bg-semantic-bg-brand-secondary', text: 'text-violet-700 dark:text-semantic-text-brand', border: 'border-violet-300 dark:border-semantic-border-brand-primary' },
      'transit_gateway': { label: 'TRANSIT_GATEWAY', bg: 'bg-violet-50 dark:bg-semantic-bg-brand-secondary', text: 'text-violet-700 dark:text-semantic-text-brand', border: 'border-violet-300 dark:border-semantic-border-brand-primary' },
      'rds': { label: 'RDS', bg: 'bg-sky-50 dark:bg-semantic-bg-tertiary', text: 'text-sky-700 dark:text-semantic-text-primary', border: 'border-sky-300 dark:border-semantic-border-primary' },
      'elb': { label: 'ELB', bg: 'bg-blue-50 dark:bg-semantic-bg-brand-secondary', text: 'text-blue-700 dark:text-semantic-text-brand', border: 'border-blue-300 dark:border-semantic-border-brand-primary' },
      'ecs': { label: 'ECS', bg: 'bg-orange-50 dark:bg-semantic-bg-warning-secondary', text: 'text-orange-700 dark:text-semantic-text-warning', border: 'border-orange-300 dark:border-semantic-border-warning-primary' },
      'eks': { label: 'EKS', bg: 'bg-amber-50 dark:bg-semantic-bg-warning-secondary', text: 'text-amber-700 dark:text-semantic-text-warning', border: 'border-amber-300 dark:border-semantic-border-warning-primary' },
      'oci_instance': { label: 'INSTANCE', bg: 'bg-emerald-50 dark:bg-semantic-bg-success-secondary', text: 'text-emerald-700 dark:text-semantic-text-success', border: 'border-emerald-300 dark:border-semantic-border-success-primary' },
      'oci_standalone': { label: 'STANDALONE', bg: 'bg-slate-100 dark:bg-semantic-bg-tertiary', text: 'text-slate-500 dark:text-semantic-text-secondary', border: 'border-slate-300 dark:border-semantic-border-primary' },
    };
    const banner = eniType ? typeBannerConfig[eniType] || { label: eniType.toUpperCase(), bg: 'bg-slate-50 dark:bg-semantic-bg-tertiary', text: 'text-slate-600 dark:text-semantic-text-secondary', border: 'border-slate-300 dark:border-semantic-border-primary' } : null;

    // Reordenar líneas para AWS: desc primero, luego el resto
    // ALSO: filter out "type:" since we show it as a banner now
    let orderedLines = rest.filter(line => !line.match(/^\s*type:\s*/));
    if (isAWS) {
      const descLineIndex = orderedLines.findIndex(line => line.match(/^\s*desc:\s*/));
      if (descLineIndex !== -1) {
        const descLine = orderedLines[descLineIndex];
        orderedLines = [descLine, ...orderedLines.filter((_, idx) => idx !== descLineIndex)];
      }
    }

    const renderRow = (line: string, idx: number) => {
      const m = line.match(/^\s*([a-zA-Z]+):\s*(.*)$/);
      const isContinuation = /^\s{2,}/.test(line) && !m;
      if (!m && !isContinuation) {
        return (
          <div key={idx} className="text-[10px] font-mono text-slate-600 leading-tight dark:text-semantic-text-secondary">
            {line}
          </div>
        );
      }
      const key = m ? m[1].toLowerCase() : "";
      const val = m ? m[2] : line.trim();
      
      // AWS: Ocultar descripción si está vacía
      if (isAWS && key === "desc" && !val.trim()) {
        return null;
      }
      
      // AWS: Descripción sin título, solo el valor
      if (isAWS && key === "desc") {
        return (
          <div key={idx} className="text-[10px] font-mono text-slate-600 leading-tight truncate dark:text-semantic-text-secondary" title={val}>
            {val}
          </div>
        );
      }
      
      const keyClass =
        key === "sg"
          ? "text-slate-600 dark:text-semantic-text-secondary"
          : "text-slate-500 dark:text-semantic-text-secondary";

      return (
        <div key={idx} className="flex items-baseline font-mono leading-tight">
          <span className={`text-[10px] font-semibold shrink-0 ${keyClass}`}>
            {m ? `${key}: ` : ""}
          </span>
          <span
            className={`flex-1 text-[10px] text-slate-600 dark:text-semantic-text-primary ${key === 'sg' || key === 'nsg' ? 'break-all' : 'truncate whitespace-nowrap'} overflow-hidden`}
            title={val}
          >
            {val}
          </span>
        </div>
      );
    };

    return (
      <div className="relative w-full h-full pl-1 pr-2 py-2">
        {/* Type banner at top center */}
        {banner && (
          <div className={`absolute -top-2.5 left-1/2 -translate-x-1/2 z-10 px-2 py-0.5 rounded border text-[9px] font-semibold uppercase tracking-wide ${banner.bg} ${banner.text} ${banner.border}`}>
            {banner.label}
          </div>
        )}
        {/* Icon as floating badge in top-left corner */}
        <div className="absolute -top-2 -left-2 z-10 bg-white rounded-lg p-1 shadow-sm border border-slate-200 dark:bg-semantic-bg-secondary dark:border-semantic-border-primary">
          <EthernetPort className="w-5 h-5 text-orange-500 dark:text-semantic-text-warning" strokeWidth={1.5} />
        </div>
        {/* Content with full width */}
        <div className="flex flex-col justify-center gap-0.5 min-w-0 w-full">
          {/* Title with left margin to avoid icon overlap */}
          <div className="text-[12px] font-semibold text-slate-800 truncate pl-6 dark:text-semantic-text-primary" title={title}>
            {title}
          </div>
          <div className="flex flex-col gap-0.5">{orderedLines.map(renderRow)}</div>
        </div>
      </div>
    );
  };

  // Get RTB color if available
  const rtbColor = isRtb && data.color ? data.color : null;
  
  // Security styling
  const securityRing = data.securityEnabled && data.severity ? {
    critical: 'ring-2 ring-red-500 ring-offset-2 shadow-[0_0_15px_rgba(239,68,68,0.4)]',
    high: 'ring-2 ring-orange-500 ring-offset-1 shadow-[0_0_10px_rgba(249,115,22,0.3)]',
    medium: 'ring-1 ring-yellow-500',
    low: 'ring-1 ring-green-500',
  }[data.severity as string] || '' : '';

  const highlightRing = data.isHighlighted ? 'ring-4 ring-violet-400 ring-offset-2 animate-pulse' : '';
  
  // Special glow effect when a specific SG finding is being highlighted
  // No animate-pulse to avoid confusion with dimmed elements
  const glowEffect = data.isGlowing ? {
    critical: 'ring-4 ring-red-400 ring-offset-2 shadow-[0_0_30px_rgba(239,68,68,0.8)]',
    high: 'ring-4 ring-orange-400 ring-offset-2 shadow-[0_0_30px_rgba(249,115,22,0.8)]',
    medium: 'ring-4 ring-yellow-400 ring-offset-2 shadow-[0_0_30px_rgba(234,179,8,0.8)]',
    low: 'ring-4 ring-green-400 ring-offset-2 shadow-[0_0_30px_rgba(34,197,94,0.8)]',
  }[data.glowSeverity as string] || 'ring-4 ring-violet-400 ring-offset-2 shadow-[0_0_30px_rgba(139,92,246,0.8)]' : '';

  // Dimming effect for non-affected ENIs when highlighting is active
  const dimmedEffect = data.isDimmed ? 'opacity-25 grayscale-[50%] pointer-events-none' : '';

  // Search highlight effect
  const searchHighlight = data.isSearchHighlighted 
    ? 'ring-4 ring-indigo-500 ring-offset-2 shadow-[0_0_25px_rgba(99,102,241,0.6)] animate-pulse' 
    : '';
  
  return (
    <div 
      className={`
        group flex h-full w-full flex-col items-center justify-center 
        rounded-lg border shadow-sm transition-all duration-300
        ${isUnknown ? 'border-dashed border-slate-300 bg-slate-50 dark:border-semantic-border-secondary dark:bg-semantic-bg-tertiary' : 'hover:-translate-y-0.5 hover:shadow-md'}
        ${!isRtb || !rtbColor ? 'bg-white border-slate-200 dark:bg-semantic-bg-secondary dark:border-semantic-border-primary' : ''}
        ${glowEffect || securityRing} ${highlightRing} ${searchHighlight}
        ${dimmedEffect}
      `}
      style={rtbColor ? { 
        backgroundColor: rtbColor.bg, 
        borderColor: rtbColor.border,
        borderWidth: '2px'
      } : undefined}
    >
      {/* Security finding badge */}
      {data.securityEnabled && data.findingCount > 0 && (
        <div className={`absolute -top-2 -right-2 ${
          data.severity === 'critical' ? 'bg-red-500' :
          data.severity === 'high' ? 'bg-orange-500' :
          data.severity === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
        } text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg z-50`}>
          {data.findingCount}
        </div>
      )}
      {kind === "eni" || kind === "vnic" ? (
        renderIfaceLayout()
      ) : (
        <>
          <div className="mb-1 transition-transform group-hover:scale-110">{Icon}</div>
          <div className="w-full px-1.5 text-center">
            {isGateway ? (
              <div className="truncate text-[9px] font-medium leading-tight text-slate-700 px-1 dark:text-semantic-text-primary" title={data.label}>
                {data.label || 'Unknown'}
              </div>
            ) : (
              <div 
                className="break-words whitespace-normal text-[9px] font-semibold leading-tight text-slate-800 dark:text-semantic-text-primary" 
                style={rtbColor ? { color: rtbColor.text } : undefined}
                title={data.label}
              >
                {data.label || 'Unknown'}
              </div>
            )}
            {data.id && (
              <div className="mt-0.5 truncate text-[8px] text-slate-400 font-mono dark:text-semantic-text-secondary">
                {data.id.split('-').pop()}
              </div>
            )}
          </div>
          {/* Associated Subnets for Route Tables */}
          {isRtb && data.associatedSubnets && data.associatedSubnets.length > 0 && (
            <div className="w-full mt-1.5 pt-1 border-t border-slate-200 dark:border-semantic-border-primary">
              <div className="text-[7px] text-slate-400 uppercase tracking-wider text-center mb-1 font-semibold dark:text-semantic-text-secondary">
                Subnets ({data.associatedSubnets.length})
              </div>
              <div className="flex flex-col gap-0.5 px-1.5">
                {(subnetsExpanded ? data.associatedSubnets : data.associatedSubnets.slice(0, 4)).map((subnet: { id: string; name: string }, idx: number) => (
                  <div 
                    key={subnet.id || idx} 
                    className="text-[7px] text-blue-700 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5 truncate text-center font-medium dark:text-semantic-text-brand dark:bg-semantic-bg-brand-secondary dark:border-semantic-border-brand-primary"
                    title={`${subnet.name}\n${subnet.id}`}
                  >
                    {subnet.name.length > 20 ? subnet.name.substring(0, 18) + '...' : subnet.name}
                  </div>
                ))}
                {data.associatedSubnets.length > 4 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSubnetsExpanded(!subnetsExpanded);
                    }}
                    className="text-[7px] text-indigo-600 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 text-center font-semibold cursor-pointer hover:bg-indigo-100 transition-colors dark:text-semantic-text-brand dark:bg-semantic-bg-brand-secondary dark:border-semantic-border-brand-primary dark:hover:opacity-80"
                  >
                    {subnetsExpanded ? 'Show less' : `+${data.associatedSubnets.length - 4} more`}
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Multi-directional handles */}
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      {/* RTB -> Gateway links (source at top). Use multiple lanes when needed. */}
      {isRtb ? (
        gatewayTopSourceHandles <= 1 ? (
          <Handle type="source" id="t-src" position={Position.Top} style={{ opacity: 0 }} />
        ) : (
          Array.from({ length: gatewayTopSourceHandles }).map((_, idx) => {
            const leftPct = ((idx + 1) / (gatewayTopSourceHandles + 1)) * 100;
            return (
              <Handle
                key={`t-src-${idx}`}
                type="source"
                id={`t-src-${idx}`}
                position={Position.Top}
                style={{ opacity: 0, left: `${leftPct}%`, transform: 'translateX(-50%)' }}
              />
            );
          })
        )
      ) : null}
      <Handle type="target" id="l" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" id="r" position={Position.Right} style={{ opacity: 0 }} />
      {/* Needed for RTB -> Gateway links (target at bottom on gateways). RTBs already render bottom targets below. */}
      {!isRtb && <Handle type="target" id="b-tgt" position={Position.Bottom} style={{ opacity: 0 }} />}

      {/* Fix: Bottom Target Handles for RouteTables (multiple lanes to avoid overlapping inbound edges) */}
      {(kind === 'rtb' || kind === 'route_table') && (
        <>
          {bottomTargetHandles <= 1 ? (
            <Handle type="target" id="b-tgt" position={Position.Bottom} style={{ opacity: 0 }} />
          ) : (
            Array.from({ length: bottomTargetHandles }).map((_, idx) => {
              const leftPct = ((idx + 1) / (bottomTargetHandles + 1)) * 100;
              return (
                <Handle
                  key={`b-tgt-${idx}`}
                  type="target"
                  id={`b-tgt-${idx}`}
                  position={Position.Bottom}
                  style={{ opacity: 0, left: `${leftPct}%`, transform: 'translateX(-50%)' }}
                />
              );
            })
          )}
        </>
      )}
    </div>
  );
};

// --- Data Fetching ---

// Legacy function kept for backwards compatibility (now using filter-based loading)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function fetchTopology(): Promise<TopologyPayload> {
  const candidates = ["/data/tenants/9/output-topology.json"];

  let lastErr: unknown;
  for (const url of candidates) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status} ${res.statusText}`);
      const json = (await res.json()) as TopologyPayload;
      return json;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr;
}

export default function TopologyView() {
  const rfRef = useRef<ReactFlowInstance | null>(null);
  const rfWrapRef = useRef<HTMLDivElement | null>(null);
  const [reactFlowReady, setReactFlowReady] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const insightDataPath = useInsightDataPath();

  // Sidebar state from Redux - to adjust filter panel position
  const isSidebarExpanded = useAppSelector(
    (state: RootState) => state.sideMenu.isExpanded
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [exporting, setExporting] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedSecurityGroup, setSelectedSecurityGroup] = useState<string | null>(null);
  const [selectedNetworkSecurityGroup, setSelectedNetworkSecurityGroup] = useState<any>(null);
  const [selectedSecurityLists, setSelectedSecurityLists] = useState<any[] | null>(null);
  const [selectedNetworkAcl, setSelectedNetworkAcl] = useState<any | null>(null);
  const [discoveryData, setDiscoveryData] = useState<any>(null);
  const [showTrafficAnalyzer, setShowTrafficAnalyzer] = useState(false);
  const [selectedTgwForVisualization, setSelectedTgwForVisualization] = useState<string | null>(null);
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);
  const [pendingDeepLink, setPendingDeepLink] = useState<{ vpc?: string; subnet?: string } | null>(null);
  // IMPORTANT: pendingDeepLink is used inside effects that don't depend on it (e.g. topology load).
  // Keep a ref so we can reliably prevent the "post-load fitView" from overriding a deep-link focus.
  const pendingDeepLinkRef = useRef<{ vpc?: string; subnet?: string } | null>(null);

  // Filter states
  const [topologyIndex, setTopologyIndex] = useState<TopologyIndex | null>(null);
  const [filterState, setFilterState] = useState<FilterState>({
    provider: null,
    region: null,
    account: null,
  });
  const [isFilterExpanded, setIsFilterExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [excludeEmpty, setExcludeEmpty] = useState(true); // Toggle to exclude accounts/compartments without VPCs/VCNs

  // =============================================================================
  // TOPOLOGY SEARCH STATE
  // =============================================================================
  const [topologySearchOpen, setTopologySearchOpen] = useState(false);
  const [topologySearchQuery, setTopologySearchQuery] = useState('');
  const [topologySearchResults, setTopologySearchResults] = useState<SearchResult[]>([]);
  const [topologySearchIndex, setTopologySearchIndex] = useState(0);
  const [highlightedSearchNodeId, setHighlightedSearchNodeId] = useState<string | null>(null);
  const topologySearchInputRef = useRef<HTMLInputElement>(null);

  // AI Assistant
  const {
    isOpen: isAIOpen,
    isLoading: isAILoading,
    messages: aiMessages,
    error: aiError,
    sendMessage,
    toggleOpen: toggleAI,
    clearChat,
    globalMode,
    toggleGlobalMode,
    loadingGlobal,
    vpcCount,
  } = useAIAssistant(
    discoveryData,
    filterState.provider?.id || 'aws',
    filterState.region?.id || '',
    filterState.account?.id || '',
    topologyIndex
  );

  // Network Security Module
  const {
    enabled: securityEnabled,
    loading: securityLoading,
    results: securityResults,
    selectedFinding,
    highlightedNodeId,
    highlightedSgId,
    nodeSeverityMap,
    nodeFindingCountMap,
    toggleEnabled: toggleSecurity,
    selectFinding,
    toggleHighlightSg,
  } = useNetworkSecurity(
    filterState.account 
      ? {
          id: filterState.account.id,
          provider: filterState.provider?.id || 'aws',
          region: filterState.region?.id,
          discoveryPath: filterState.account.discoveryFile,
        }
      : null,
    discoveryData,
    insightDataPath
  );

  // High-Fidelity Edge Styles
  const defaultEdgeOptions = useMemo(
    () => ({
      type: "smoothstep",
      pathOptions: { borderRadius: 15 },
      style: { stroke: "#94a3b8", strokeWidth: 1.5 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: "#94a3b8",
        width: 16,
        height: 16,
      },
      animated: false,
    }),
    []
  );

  const nodeTypes = useMemo(
    () => ({
      // Strict mapping
      vpc: VpcNode,
      subnet: SubnetNode,
      az: AzNode,
      account: GroupNode,
      region: GroupNode,

      // Resource types
      instance: ResourceNode,
      eni: ResourceNode,
      vnic: ResourceNode,
      sg: ResourceNode,
      rtb: ResourceNode,
      'rtb-label': ResourceNode, // Same rendering as rtb

      // Gateway types
      igw: ResourceNode,
      nat: ResourceNode,
      tgw: ResourceNode,
      drg: ResourceNode,
      sgw: ResourceNode,
      vgw: ResourceNode,

      // Direct Connect path nodes (dx_vif, pop, dc_router)
      dx_vif: PathNode,
      pop: PathNode,
      dc_router: PathNode,
      pathNode: PathNode,

      // Fallbacks
      vpcNode: VpcNode,
      subnetNode: SubnetNode,
      resourceNode: ResourceNode,
      group: GroupNode, // Default general group
      default: ResourceNode,
    }),
    []
  );

  const edgeTypes = useMemo(
    () => ({
      rtbLane: RtbLaneEdge,
    }),
    []
  );

  // Nodes with security props - dynamically add security state to nodes
  // Also maps SG findings to ENIs that use those security groups
  const nodesWithSecurity = useMemo(() => {
    if (!securityEnabled) return nodes;
    
    // Build a map of SG ID -> severity and finding count from findings
    const sgSeverityMap: Record<string, 'critical' | 'high' | 'medium' | 'low'> = {};
    const sgFindingCountMap: Record<string, number> = {};
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    
    if (securityResults?.findings) {
      for (const f of securityResults.findings) {
        // Process security_group (AWS), security_list (OCI), network_security_group (OCI), and network_acl (AWS) findings
        if (f.resourceType === 'security_group' || f.resourceType === 'security_list' || f.resourceType === 'network_security_group' || f.resourceType === 'network_acl') {
          const sgId = f.visualNodeId; // e.g., "sg-xxx" for AWS SG, "sl-xxx" for OCI SL, "acl-xxx" for AWS NACL
          sgFindingCountMap[sgId] = (sgFindingCountMap[sgId] || 0) + 1;
          const cur = sgSeverityMap[sgId];
          if (!cur || severityOrder[f.severity] < severityOrder[cur]) {
            sgSeverityMap[sgId] = f.severity;
          }
        }
      }
    }
    
    // Build a map of Security List ID -> affected subnets for OCI
    const slToSubnetsMap: Record<string, string[]> = {};
    nodes.forEach((n: Node) => {
      if (n.data?.type === 'subnet' && n.data?.securityLists) {
        const securityLists = n.data.securityLists as Array<{ id: string }>;
        securityLists.forEach(sl => {
          if (!slToSubnetsMap[sl.id]) slToSubnetsMap[sl.id] = [];
          slToSubnetsMap[sl.id].push(n.id);
        });
      }
    });
    
    return nodes.map((n: Node) => {
      const kind = n.data?.kind || n.data?.type;
      let severity = nodeSeverityMap[n.id] || null;
      let findingCount = nodeFindingCountMap[n.id] || 0;
      let isGlowing = false;
      let glowSeverity: string | null = null;
      
      // For AWS ENI nodes, check if they use any affected security groups
      if (kind === 'eni' && n.data?.label) {
        const label = String(n.data.label);
        // Extract SG IDs from the label (format: "sg: sg-xxx sg-yyy sg-zzz")
        const sgMatch = label.match(/sg:\s*([^\n]+)/i);
        if (sgMatch) {
          const sgIds = sgMatch[1].split(/\s+/).filter(id => id.startsWith('sg-'));
          for (const sgId of sgIds) {
            if (sgSeverityMap[sgId]) {
              // This ENI uses an affected security group
              const sgSev = sgSeverityMap[sgId];
              if (!severity || severityOrder[sgSev] < severityOrder[severity]) {
                severity = sgSev;
              }
              findingCount += sgFindingCountMap[sgId] || 0;
            }
            // Check if this SG is the one being highlighted
            if (highlightedSgId && sgId === highlightedSgId) {
              isGlowing = true;
              glowSeverity = sgSeverityMap[sgId] || 'high';
            }
          }
        }
      }
      
      // For OCI subnets, check if they use any affected security lists
      if (kind === 'subnet' && n.data?.securityLists) {
        const securityLists = n.data.securityLists as Array<{ id: string }>;
        for (const sl of securityLists) {
          if (sgSeverityMap[sl.id]) {
            const slSev = sgSeverityMap[sl.id];
            if (!severity || severityOrder[slSev] < severityOrder[severity]) {
              severity = slSev;
            }
            findingCount += sgFindingCountMap[sl.id] || 0;
          }
          // Check if this SL is the one being highlighted
          if (highlightedSgId && sl.id === highlightedSgId) {
            isGlowing = true;
            glowSeverity = sgSeverityMap[sl.id] || 'high';
          }
        }
      }
      
      // For AWS subnets, check if they use any affected NACLs
      if (kind === 'subnet' && n.data?.networkAcl) {
        const nacl = n.data.networkAcl as { id: string };
        const naclId = nacl.id;
        if (naclId && sgSeverityMap[naclId]) {
          const naclSev = sgSeverityMap[naclId];
          if (!severity || severityOrder[naclSev] < severityOrder[severity]) {
            severity = naclSev;
          }
          findingCount += sgFindingCountMap[naclId] || 0;
        }
        // Check if this NACL is the one being highlighted
        if (highlightedSgId && naclId === highlightedSgId) {
          isGlowing = true;
          glowSeverity = sgSeverityMap[naclId] || 'high';
        }
      }
      
      // For OCI VNICs, check TWO things:
      // 1. If their parent subnet uses affected security lists
      // 2. If the VNIC itself uses affected NSGs (from label "nsg: nsg-xxx nsg-yyy")
      if ((kind === 'vnic' || kind === 'eni') && n.data?.label) {
        const label = String(n.data.label);
        
        // Check for OCI NSGs in VNIC labels (format: "nsg: nsg-xxx nsg-yyy nsg-zzz")
        const nsgMatch = label.match(/nsg:\s*([^\n]+)/i);
        if (nsgMatch) {
          const nsgIds = nsgMatch[1].split(/\s+/).filter(id => id.startsWith('nsg-'));
          for (const nsgId of nsgIds) {
            if (sgSeverityMap[nsgId]) {
              // This VNIC uses an affected NSG
              const nsgSev = sgSeverityMap[nsgId];
              if (!severity || severityOrder[nsgSev] < severityOrder[severity]) {
                severity = nsgSev;
              }
              findingCount += sgFindingCountMap[nsgId] || 0;
            }
            // Check if this NSG is the one being highlighted
            if (highlightedSgId && nsgId === highlightedSgId) {
              isGlowing = true;
              glowSeverity = sgSeverityMap[nsgId] || 'high';
            }
          }
        }
      }
      
      // For OCI VNICs, also check if their parent subnet uses affected security lists
      if (kind === 'vnic' && n.parentId) {
        // Find parent subnet
        const parentSubnet = nodes.find(pn => pn.id === n.parentId && pn.data?.type === 'subnet');
        if (parentSubnet?.data?.securityLists) {
          const securityLists = parentSubnet.data.securityLists as Array<{ id: string }>;
          for (const sl of securityLists) {
            if (sgSeverityMap[sl.id]) {
              const slSev = sgSeverityMap[sl.id];
              if (!severity || severityOrder[slSev] < severityOrder[severity]) {
                severity = slSev;
              }
              findingCount += sgFindingCountMap[sl.id] || 0;
            }
            // Check if this SL is the one being highlighted
            if (highlightedSgId && sl.id === highlightedSgId) {
              isGlowing = true;
              glowSeverity = sgSeverityMap[sl.id] || 'high';
            }
          }
        }
      }
      
      // Dim non-glowing ENIs/VNICs when a highlight is active
      // This creates a visual "exclude" effect - highlighted items stand out, others fade
      const isDimmed = highlightedSgId && !isGlowing && (kind === 'eni' || kind === 'vnic');
      
      // Check if this node is highlighted by search
      const isSearchHighlighted = highlightedSearchNodeId === n.id;
      
      return {
        ...n,
        data: {
          ...n.data,
          securityEnabled,
          severity,
          findingCount,
          isHighlighted: highlightedNodeId === n.id,
          isGlowing,
          glowSeverity,
          isDimmed,
          isSearchHighlighted,
        },
      };
    });
  }, [nodes, securityEnabled, securityResults, nodeSeverityMap, nodeFindingCountMap, highlightedNodeId, highlightedSgId, highlightedSearchNodeId]);

  // Compute which SG/SL/NSG IDs have at least one ENI/VNIC associated
  // Used to disable highlight button for findings without affected resources
  const sgsWithEnis = useMemo(() => {
    const sgsSet = new Set<string>();
    
    nodes.forEach((n: Node) => {
      const kind = n.data?.kind || n.data?.type;
      
      // AWS ENIs - parse sg: from label
      if ((kind === 'eni' || kind === 'vnic') && n.data?.label) {
        const label = String(n.data.label);
        
        // Extract AWS SG IDs (format: "sg: sg-xxx sg-yyy")
        const sgMatch = label.match(/sg:\s*([^\n]+)/i);
        if (sgMatch) {
          const sgIds = sgMatch[1].split(/\s+/).filter(id => id.startsWith('sg-'));
          sgIds.forEach(id => sgsSet.add(id));
        }
        
        // Extract OCI NSG IDs (format: "nsg: nsg-xxx nsg-yyy")
        const nsgMatch = label.match(/nsg:\s*([^\n]+)/i);
        if (nsgMatch) {
          const nsgIds = nsgMatch[1].split(/\s+/).filter(id => id.startsWith('nsg-'));
          nsgIds.forEach(id => sgsSet.add(id));
        }
      }
      
      // OCI Subnets - add their security lists
      if (kind === 'subnet' && n.data?.securityLists) {
        const securityLists = n.data.securityLists as Array<{ id: string }>;
        securityLists.forEach(sl => {
          if (sl.id) sgsSet.add(sl.id);
        });
      }
      
      // AWS Subnets - add their NACLs
      if (kind === 'subnet' && n.data?.networkAcl) {
        const nacl = n.data.networkAcl as { id: string };
        if (nacl.id) sgsSet.add(nacl.id);
      }
    });
    
    return sgsSet;
  }, [nodes]);

  // Build list of ALL security resources (SGs, NSGs, SLs, NACLs) from discoveryData
  const allSecurityResources = useMemo((): SecurityResource[] => {
    if (!discoveryData) return [];
    
    const resources: SecurityResource[] = [];
    const isOCI = !!discoveryData.vcns;
    
    if (isOCI) {
      // OCI: Get NSGs and SLs from vcns
      const vcns = discoveryData.vcns || {};
      Object.entries(vcns).forEach(([vcnId, vcn]: [string, any]) => {
        const vcnName = vcn?.display_name || vcn?.name || vcnId;
        
        // Network Security Groups
        const nsgs = vcn?.network_security_groups || [];
        nsgs.forEach((nsg: any) => {
          const nsgId = nsg.id || nsg.ocid;
          const visualId = nsgId ? `nsg-${nsgId.split('.').pop()?.substring(0, 10)}` : '';
          resources.push({
            id: nsgId,
            visualNodeId: visualId,
            name: nsg.display_name || nsg.name || nsgId,
            type: 'network_security_group',
            provider: 'oci',
            vpcId: vcnId,
            vpcName: vcnName,
            rulesCount: (nsg.security_rules || nsg.rules || []).length,
            rawData: nsg,
          });
        });
        
        // Security Lists
        const sls = vcn?.security_lists || [];
        sls.forEach((sl: any) => {
          const slId = sl.id || sl.ocid;
          const visualId = slId ? `sl-${slId.split('.').pop()?.substring(0, 10)}` : '';
          const ingressRules = sl.ingress_security_rules || [];
          const egressRules = sl.egress_security_rules || [];
          resources.push({
            id: slId,
            visualNodeId: visualId,
            name: sl.display_name || sl.name || slId,
            type: 'security_list',
            provider: 'oci',
            vpcId: vcnId,
            vpcName: vcnName,
            rulesCount: ingressRules.length + egressRules.length,
            rawData: sl,
          });
        });
      });
    } else {
      // AWS: Get SGs and NACLs from vpcs
      const vpcs = discoveryData.vpcs || {};
      Object.entries(vpcs).forEach(([vpcId, vpc]: [string, any]) => {
        const vpcName = vpc?.name || vpc?.Name || vpcId;
        
        // Security Groups
        const sgs = vpc?.security_groups || [];
        sgs.forEach((sg: any) => {
          const sgId = sg.id || sg.GroupId;
          resources.push({
            id: sgId,
            visualNodeId: sgId,
            name: sg.name || sg.GroupName || sgId,
            type: 'security_group',
            provider: 'aws',
            vpcId: vpcId,
            vpcName: vpcName,
            rulesCount: (sg.inbound_rules || sg.IpPermissions || []).length + (sg.outbound_rules || sg.IpPermissionsEgress || []).length,
            rawData: sg,
          });
        });
        
        // Network ACLs
        const nacls = vpc?.network_acls || [];
        nacls.forEach((nacl: any) => {
          const naclId = nacl.id || nacl.NetworkAclId;
          const visualId = naclId ? `acl-${naclId.split('-').pop()?.substring(0, 10)}` : '';
          resources.push({
            id: naclId,
            visualNodeId: visualId,
            name: nacl.name || nacl.Name || naclId,
            type: 'nacl',
            provider: 'aws',
            vpcId: vpcId,
            vpcName: vpcName,
            rulesCount: (nacl.entries || nacl.Entries || []).length,
            rawData: nacl,
          });
        });
      });
    }
    
    return resources;
  }, [discoveryData]);

  // Handler to open full rules modal from security panel
  const handleViewAllRules = useCallback((resource: SecurityResource) => {
    if (!resource.rawData) return;
    
    if (resource.type === 'security_list') {
      // OCI Security List - open the security lists modal
      setSelectedSecurityLists([resource.rawData]);
    } else if (resource.type === 'network_security_group') {
      // OCI NSG - open the NSG modal
      setSelectedNetworkSecurityGroup(resource.rawData);
    } else if (resource.type === 'security_group') {
      // AWS SG - we need to find the SG node and set it as selectedNode
      const sgNode = nodes.find((n: Node) => {
        const nodeId = n.id;
        const nodeKind = n.data?.kind || n.data?.type;
        return nodeKind === 'sg' && (nodeId === resource.id || n.data?.sgId === resource.id);
      });
      if (sgNode) {
        setSelectedNode(sgNode);
      } else {
        // Fallback: set security group ID to trigger modal lookup
        setSelectedSecurityGroup(resource.id);
      }
    } else if (resource.type === 'nacl') {
      // AWS NACL - open the NACL modal
      setSelectedNetworkAcl(resource.rawData);
    }
  }, [nodes]);

  // Handlers for security findings
  const handleAskAIAboutFinding = (finding: NetworkSecurityFinding) => {
    if (!isAIOpen) toggleAI();
    const q = `How to remediate this security finding: "${finding.title}"\n\nResource: ${finding.resourceName} (${finding.resourceType})\nViolating Rule: ${finding.violatingRule.protocol} port ${finding.violatingRule.portRange} from ${finding.violatingRule.source}\nSeverity: ${finding.severity}\nFramework: ${finding.frameworks.join(', ')}\n\nPlease provide step-by-step remediation guidance.`;
    sendMessage(q);
  };

  // Load topology index on mount (tenant-scoped)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${insightDataPath}/topology-index.json`);
        if (res.ok) {
          const data = await res.json();
          setTopologyIndex(data);
        }
      } catch (e) {
        console.error("Failed to load topology index:", e);
      }
    })();
  }, [insightDataPath]);

  // Handle URL params to auto-select filters (from Insight Dashboard navigation)
  useEffect(() => {
    if (!topologyIndex || urlParamsProcessed) return;
    
    const providerParam = searchParams.get('provider');
    const regionParam = searchParams.get('region');
    const accountParam = searchParams.get('account');
    const vpcParam = searchParams.get('vpc') || undefined;
    const subnetParam = searchParams.get('subnet') || undefined;
    
    if (!providerParam || !regionParam || !accountParam) return;
    
    // Find the provider
    const provider = topologyIndex.providers.find(p => p.id === providerParam);
    if (!provider) {
      console.warn(`Provider ${providerParam} not found in topology index`);
      return;
    }
    
    // Find the region
    const region = provider.regions.find(r => r.id === regionParam);
    if (!region) {
      console.warn(`Region ${regionParam} not found for provider ${providerParam}`);
      return;
    }
    
    // Find the account - for OCI, compartmentId is passed as OCID, need to match against discoveryFile
    let account = region.accounts.find(a => 
      a.id === accountParam || 
      a.id.includes(accountParam) || 
      accountParam.includes(a.id)
    );
    
    // If not found and it's OCI (OCID format), search in discoveryFile path
    if (!account && accountParam.startsWith('ocid1.')) {
      account = region.accounts.find(a => 
        a.discoveryFile?.includes(accountParam)
      );
    }
    
    if (!account) {
      console.warn(`Account ${accountParam} not found in region ${regionParam}`);
      return;
    }
    
    // Auto-select the filters
    console.log(`[TopologyView] Auto-selecting from URL: ${providerParam}/${regionParam}/${account.name}`);
    setFilterState({
      provider,
      region,
      account
    });
    setIsFilterExpanded(false);
    setUrlParamsProcessed(true);

    // Store deep-link target (VCN/VPC or Subnet) for zoom after nodes load
    if (vpcParam || subnetParam) {
      const dl = { vpc: vpcParam, subnet: subnetParam };
      pendingDeepLinkRef.current = dl;
      setPendingDeepLink(dl);
      console.log('[TopologyView] Deep-link requested:', dl);
    }
    
    // Clear URL params after processing
    setSearchParams({}, { replace: true });
  }, [topologyIndex, searchParams, urlParamsProcessed, setSearchParams]);

  // Deep-link: after topology nodes are loaded, zoom to the requested VPC/VCN or Subnet
  useEffect(() => {
    if (!pendingDeepLink) return;
    if (!nodes || nodes.length === 0) return;
    // NOTE: rfRef is a ref (mutates without rerender). Use reactFlowReady so this runs after ReactFlow onInit.
    if (!reactFlowReady || !rfRef.current) return;

    const { vpc, subnet } = pendingDeepLink;
    console.log('[TopologyView] Deep-link processing:', { vpc, subnet, nodes: nodes.length, reactFlowReady });

    const findByCloudId = (cloudId: string, type: 'vpc' | 'subnet') => {
      const cid = String(cloudId || '').trim();
      if (!cid) return null;
      return nodes.find((n: any) => {
        const t = n?.data?.type || n?.data?.kind || n?.type;
        if (t !== type) return false;
        // Prefer exact match on original cloud ID (OCI)
        if (n?.data?.oci_id && String(n.data.oci_id) === cid) return true;
        // Fallbacks (AWS / legacy)
        if (String(n?.id || '') === cid) return true;
        const label = String(n?.data?.label || '');
        return label.includes(cid);
      }) || null;
    };

    const target =
      (subnet ? findByCloudId(subnet, 'subnet') : null) ||
      (vpc ? findByCloudId(vpc, 'vpc') : null);

    if (target?.id) {
      // Zoom and highlight (avoid referencing functions declared later; keep lint clean)
      const rfInstance = rfRef.current;
      const node = rfInstance.getNode?.(target.id) || target;
      const nodeType = node?.data?.type || node?.data?.kind;
      const measured = (node as any)?.measured;
      const width = Number(measured?.width ?? node?.style?.width ?? 0);
      const height = Number(measured?.height ?? node?.style?.height ?? 0);
      const pos = (node as any)?.positionAbsolute || node?.position || { x: 0, y: 0 };
      const centerX = pos.x + (width > 0 ? width / 2 : 0);
      const centerY = pos.y + (height > 0 ? height / 2 : 0);
      const hasSize = Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0;
      if (hasSize && rfInstance.fitBounds) {
        const bounds = { x: pos.x, y: pos.y, width, height };
        const padding = nodeType === 'subnet' ? 0.25 : 0.18;
        const maxZoom = nodeType === 'subnet' ? 1.35 : 1.15;
        rfInstance.fitBounds(bounds, { padding, duration: 900 });
        // Clamp over-zoom if fitBounds gets too close on huge VPC/Subnet nodes
        const clamp = () => {
          const z = rfInstance.getZoom?.();
          if (typeof z === 'number' && z > maxZoom && rfInstance.setViewport) {
            const vp = rfInstance.getViewport?.();
            rfInstance.setViewport(
              { x: vp?.x ?? 0, y: vp?.y ?? 0, zoom: maxZoom },
              { duration: 200 }
            );
          }
        };
        setTimeout(clamp, 120);
      } else if (Number.isFinite(centerX) && Number.isFinite(centerY) && rfInstance.setCenter) {
        rfInstance.setCenter(centerX, centerY, { zoom: 1, duration: 900 });
      } else {
        rfInstance.fitView({ nodes: [{ id: target.id }], padding: 0.5, duration: 800, maxZoom: 2 });
      }
      setHighlightedSearchNodeId(target.id);
      console.log('[TopologyView] Deep-link success:', { targetId: target.id, targetType: target?.data?.type });
    } else {
      console.warn('[TopologyView] Deep-link target not found in nodes:', { vpc, subnet });
    }

    // Only do it once
    pendingDeepLinkRef.current = null;
    setPendingDeepLink(null);
  }, [pendingDeepLink, nodes, reactFlowReady]);

  // Load discovery data for routing rules and security group details
  useEffect(() => {
    // Only load when account is selected
    if (!filterState.account) {
      setDiscoveryData(null);
      return;
    }

    const account = filterState.account;
    let cancelled = false;
    (async () => {
      try {
        const discoveryPath = `${insightDataPath}/${account.discoveryFile}`;
        const res = await fetch(discoveryPath);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) {
            setDiscoveryData(data);
            console.log("Loaded discovery data from:", discoveryPath);
          }
        } else {
          console.warn("Discovery file not found:", discoveryPath);
        }
      } catch (e) {
        console.error("Failed to load discovery data:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filterState.account]);

  // Legacy: Load discovery data for routing rules and security group details (when no filter)
  useEffect(() => {
    // Skip if we have a filter account (handled above)
    if (filterState.account) return;

    let cancelled = false;
    (async () => {
      try {
        // Try to infer discovery.json path from output-topology.json location
        // If output-topology.json is at /data/output-topology.json, try /data/discovery.json
        // Also try common locations
        const candidates = [
          "/data/tenants/9/discovery.json"
        ];
        
        let loaded = false;
        for (const url of candidates) {
          try {
            const res = await fetch(url);
            if (res.ok) {
              const json = await res.json();
              if (!cancelled) {
                setDiscoveryData(json);
                loaded = true;
                console.log('✅ Discovery data loaded from:', url);
                console.log('Discovery data structure:', {
                  hasVpcs: !!json.vpcs,
                  vpcsType: typeof json.vpcs,
                  isVpcsArray: Array.isArray(json.vpcs),
                  vpcCount: json.vpcs 
                    ? (Array.isArray(json.vpcs) ? json.vpcs.length : Object.keys(json.vpcs).length)
                    : 0,
                  vpcKeys: json.vpcs && typeof json.vpcs === 'object' && !Array.isArray(json.vpcs)
                    ? Object.keys(json.vpcs).slice(0, 3)
                    : null,
                  sampleVpc: json.vpcs && typeof json.vpcs === 'object' && !Array.isArray(json.vpcs)
                    ? (() => {
                        const firstKey = Object.keys(json.vpcs)[0];
                        const firstVpc = json.vpcs[firstKey];
                        return {
                          key: firstKey,
                          hasSecurityGroups: !!(firstVpc && firstVpc.security_groups),
                          securityGroupCount: firstVpc && firstVpc.security_groups ? firstVpc.security_groups.length : 0,
                          sampleSgId: firstVpc && firstVpc.security_groups && firstVpc.security_groups.length > 0
                            ? firstVpc.security_groups[0].id
                            : null
                        };
                      })()
                    : null
                });
              }
              break;
            }
          } catch (e) {
              console.warn('Failed to load discovery from', url, e);
              continue;
          }
        }
        if (!cancelled && !loaded) {
          console.error('❌ No discovery.json found in any of the candidate paths:', candidates);
          console.warn('⚠️  Security groups and routing rules will not be available.');
          console.warn('💡 Make sure discovery.json is copied to public/data/discovery.json');
        }
      } catch (e) {
        console.error('Error loading discovery data:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Only load topology when account is selected
    if (!filterState.account) {
      rfRef.current = null;
      setReactFlowReady(false);
      setNodes([]);
      setEdges([]);
      setLoading(false);
      return;
    }

    const account = filterState.account;
    let cancelled = false;

    (async () => {
      try {
        // Reset ReactFlow instance readiness before loading a new diagram
        rfRef.current = null;
        setReactFlowReady(false);

        setLoading(true);
        setError(null);

        const topologyPath = `${insightDataPath}/${account.topologyFile}`;
        const res = await fetch(topologyPath);
        if (!res.ok) {
          // Handle 404 gracefully - treat as empty topology
          if (res.status === 404) {
            console.warn(`Topology not found: ${topologyPath} - showing empty diagram`);
            if (!cancelled) {
              setNodes([]);
              setEdges([]);
              setLoading(false);
            }
            return;
          }
          throw new Error(`Failed to fetch ${topologyPath}: ${res.status}`);
        }
        
        // Check content type before parsing
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.warn(`Invalid content type for ${topologyPath}: ${contentType} - showing empty diagram`);
          if (!cancelled) {
            setNodes([]);
            setEdges([]);
            setLoading(false);
          }
          return;
        }
        
        const data = await res.json();
        if (cancelled) return;

        const rawNodes: Node[] = Array.isArray(data.nodes) ? data.nodes : [];
        const rawEdges: Edge[] = Array.isArray(data.edges) ? data.edges : [];

        // Post-processing logic
        const processedNodes = rawNodes.map((n: Node) => {
          // 1. Identify the *Real* type. 
          // In generateStaticMap.js, he put the real type in `data.type`. 
          // The ReactFlow `type` might be 'group' purely for layouting.
          const realType = n.data?.type || n.data?.kind;

          let finalType = 'default';

          if (realType === 'vpc') finalType = 'vpc';
          else if (realType === 'subnet') finalType = 'subnet';
          else if (realType === 'az') finalType = 'az';
          else if (realType === 'account') finalType = 'account';
          else if (realType === 'region') finalType = 'region';
          else if (realType === 'rtb-container') finalType = 'group'; // RTB container uses group component
          else if (realType === 'igw' || realType === 'nat' || realType === 'tgw' || realType === 'vgw' || realType === 'drg' || realType === 'sgw') {
            // IMPORTANT: do NOT map gateways to 'default' or ReactFlow will wrap them with default node styling (black border)
            finalType = realType;
          }
          else if (realType === 'instance' || realType === 'eni' || realType === 'vnic' || realType === 'sg' || realType === 'rtb') {
            finalType = realType;
          } else if (realType === 'dx_vif' || realType === 'pop' || realType === 'dc_router') {
            // Direct Connect path nodes: use PathNode for dark-mode-friendly styling
            finalType = realType;
          } else {
            // If we can't find it in data.type, check n.type
            if (n.type === 'group') finalType = 'group'; // Fallback to generic group if unknown
            else finalType = 'default';
          }

          return {
            ...n,
            type: finalType, // Explicitly override React Flow type to force our Component
            // FIX: We MUST preserve width/height from ELK, otherwise nodes collapse to content size.
            // We strip background/border so our custom component handles the visuals.
            style: {
              width: n.style?.width,
              height: n.style?.height,
              ...(n.data?.type === "rtb-wrapper"
                ? {
                    // Keep subtle wrapper background coming from JSON
                    backgroundColor: n.style?.backgroundColor,
                    borderRadius: n.style?.borderRadius,
                    border: "none",
                    boxShadow: "none",
                  }
                : {}),
              // Remove React Flow's default node wrapper styling for invisible layout groups
              ...(n.data?.transparent === true && n.data?.type !== "rtb-container"
                ? {
                    // React Flow applies default styles to group wrapper elements;
                    // force full transparency for invisible layout groups.
                    border: "none",
                    boxShadow: "none",
                  }
                : {}),
              // Also remove the default wrapper border/background for the RTB container;
              // we render the delimiter ourselves inside `GroupNode`.
              ...(n.data?.type === "rtb-container"
                ? {
                    border: "none",
                    background: "transparent",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }
                : {}),
              // Account/region: clear wrapper background so GroupNode's darker fill is visible
              ...(realType === "account" || realType === "region"
                ? { border: "none", background: "transparent", backgroundColor: "transparent", boxShadow: "none" }
                : {}),
            },
            data: {
              ...n.data,
              kind: realType, // Ensure kind is set for icon mapping logic
              ...(realType === 'subnet'
                ? {
                    onSecurityListClick: (lists: any[]) => {
                      setSelectedSecurityLists(Array.isArray(lists) ? lists : []);
                    },
                    onNetworkAclClick: (nacl: any) => {
                      setSelectedNetworkAcl(nacl || null);
                    },
                    onRouteTableClick: (rtbInfo: any) => {
                      // Find the RTB node by ID or name and open its modal
                      const rtbId = rtbInfo?.id || rtbInfo?.rtb_id || rtbInfo?.route_table_id;
                      if (rtbId) {
                        // Search for the RTB node in the topology
                        const rtbNode = processedNodes.find((node: Node) => {
                          const nodeKind = node.data?.kind || node.data?.type;
                          if (nodeKind !== 'rtb' && nodeKind !== 'route_table') return false;
                          return node.id === rtbId || 
                                 node.id.includes(rtbId) || 
                                 node.data?.id === rtbId ||
                                 String(node.id).includes(String(rtbId));
                        });
                        if (rtbNode) {
                          setSelectedNode(rtbNode as Node);
                        }
                      }
                    },
                  }
                : {}),
            }
          };
        });

        // Post-process edges: normalize edge types to valid React Flow types
        const processedEdges = rawEdges.map((edge: Edge) => {
          // React Flow built-in types: 'default', 'straight', 'step', 'smoothstep', 'bezier'
          // Note: 'bezier' may not be available in all React Flow versions, so we normalize it to 'smoothstep'
          // which provides similar curved appearance
          const edgeType = edge.type || 'default';
          
          // Normalize bezier to smoothstep to avoid React Flow warnings
          // Both provide curved edges, smoothstep is more widely supported
          if (edgeType === 'bezier') {
            return {
              ...edge,
              type: 'smoothstep'
            };
          }
          
          // Keep registered custom types (like rtbLane)
          const validTypes = ['default', 'straight', 'step', 'smoothstep', 'rtbLane'];
          if (validTypes.includes(edgeType)) {
            return edge;
          }
          
          // For any other unknown type, use smoothstep as safe fallback
          if (edgeType !== 'default') {
            console.warn(`Edge ${edge.id} has unknown type "${edgeType}", using "smoothstep"`);
          }
          return {
            ...edge,
            type: 'smoothstep'
          };
        });

        setNodes(processedNodes);
        setEdges(processedEdges);

        // Fit view after render (only when not deep-linking to a specific VPC/Subnet)
        // Deep-link navigation should control the viewport so the user lands on the selected resource.
        if (!pendingDeepLinkRef.current) {
          requestAnimationFrame(() => {
            // Add a small delay for nodes to mount
            setTimeout(() => {
              rfRef.current?.fitView({ padding: 0.1, duration: 800 });
            }, 100);
          });
        } else {
          console.log('[TopologyView] Skipping post-load fitView due to deep-link:', pendingDeepLinkRef.current);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load topology");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filterState.account]);

  type RFExportNode = Node & {
    parentNode?: string;
    positionAbsolute?: { x: number; y: number };
    measured?: { width?: number; height?: number };
  };

  const getExportNodes = (): RFExportNode[] => {
    const instNodes = (rfRef.current?.getNodes?.() as RFExportNode[] | undefined) ?? [];
    return instNodes.length > 0 ? instNodes : (nodes as unknown as RFExportNode[]);
  };

  const nodeAbsBox = (n: RFExportNode) => {
    const x = Number(n.positionAbsolute?.x ?? n.position?.x ?? 0);
    const y = Number(n.positionAbsolute?.y ?? n.position?.y ?? 0);
    const w = Number(n.measured?.width ?? (n as any).width ?? (n as any).style?.width ?? 0);
    const h = Number(n.measured?.height ?? (n as any).height ?? (n as any).style?.height ?? 0);
    return { x, y, w, h };
  };

  const boundsForNodeIds = (nodeById: Record<string, RFExportNode>, ids: string[]) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    ids.forEach((id) => {
      const n = nodeById[id];
      if (!n) return;
      const { x, y, w, h } = nodeAbsBox(n);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    });
    if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) return null;
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  };

  const sanitizeFileName = (s: string) =>
    s.replace(/[^\w.-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");

  // Export from scratch: apply export transform to *real* viewport, capture, restore.
  const exportDiagram = async (format: "svg" | "png") => {
    const wrapper = rfWrapRef.current;
    const rfEl = wrapper?.querySelector(".react-flow") as HTMLElement | null;
    if (!rfEl) {
      setError("React Flow root not found");
      return;
    }

    const viewportEl =
      (rfEl.querySelector(".react-flow__viewport") as HTMLElement | null) ||
      (rfEl.querySelector(".react-flow__transform") as HTMLElement | null) ||
      (rfEl.querySelector(".react-flow__transformationpane") as HTMLElement | null);
    if (!viewportEl) {
      setError("React Flow viewport not found");
      return;
    }

    try {
      setExporting(true);
      setError(null);

      const exportNodes = getExportNodes();
      const nodeById: Record<string, RFExportNode> = {};
      exportNodes.forEach((n) => (nodeById[n.id] = n));
      const allIds = exportNodes.map((n) => n.id);
      const bounds = boundsForNodeIds(nodeById, allIds);
      if (!bounds) throw new Error("Could not compute diagram bounds for export");

      const PAD = 320;
      const width = Math.max(1200, Math.ceil(bounds.width + PAD));
      const height = Math.max(900, Math.ceil(bounds.height + PAD));
      const viewport = getViewportForBounds(bounds, width, height, 0.02, 2, 0.2);

      const controls = rfEl.querySelector(".react-flow__controls") as HTMLElement | null;
      const bg = rfEl.querySelector(".react-flow__background") as HTMLElement | null;
      const attr = rfEl.querySelector(".react-flow__attribution") as HTMLElement | null;
      const renderer = rfEl.querySelector(".react-flow__renderer") as HTMLElement | null;
      const pane = rfEl.querySelector(".react-flow__pane") as HTMLElement | null;
      const handles = Array.from(rfEl.querySelectorAll(".react-flow__handle")) as HTMLElement[];

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const prev = {
        rfWidth: rfEl.style.width,
        rfHeight: rfEl.style.height,
        rfBg: rfEl.style.background,
        vpTransform: viewportEl.style.transform,
        vpOrigin: viewportEl.style.transformOrigin,
        rendererOverflow: renderer?.style.overflow,
        paneOverflow: pane?.style.overflow,
        controlsDisplay: controls?.style.display,
        bgDisplay: bg?.style.display,
        attrDisplay: attr?.style.display,
        handleDisplays: handles.map((el) => el.style.display),
      };

      // Apply export styles
      rfEl.style.width = `${width}px`;
      rfEl.style.height = `${height}px`;
      rfEl.style.background = "#f8fafc";
      if (renderer) renderer.style.overflow = "visible";
      if (pane) pane.style.overflow = "visible";
      if (controls) controls.style.display = "none";
      if (bg) bg.style.display = "none";
      if (attr) attr.style.display = "none";
      handles.forEach((el) => (el.style.display = "none"));

      viewportEl.style.transformOrigin = "0 0";
      viewportEl.style.transform = `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`;

      await new Promise((r) => requestAnimationFrame(() => r(null)));
      await new Promise((r) => requestAnimationFrame(() => r(null)));

      const commonOpts = {
        cacheBust: true,
        backgroundColor: "#f8fafc",
        width,
        height,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore html-to-image supports this option
        skipFonts: true,
      } as const;

      let blob: Blob;
      let filename: string;

      if (format === "svg") {
        const dataUrl = await toSvg(rfEl, commonOpts);
        const svgText = await (await fetch(dataUrl)).text();
        blob = new Blob([svgText], { type: "image/svg+xml" });
        filename = `topology-${sanitizeFileName(new Date().toISOString())}.svg`;
      } else {
        const MAX_DIM = 16384;
        const maxRatioW = Math.floor(MAX_DIM / width);
        const maxRatioH = Math.floor(MAX_DIM / height);
        const pixelRatio = Math.max(1, Math.min(6, maxRatioW, maxRatioH));

        const dataUrl = await toPng(rfEl, { ...commonOpts, pixelRatio });
        blob = await (await fetch(dataUrl)).blob();
        filename = `topology-${sanitizeFileName(new Date().toISOString())}.png`;
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      // Restore styles
      rfEl.style.width = prev.rfWidth;
      rfEl.style.height = prev.rfHeight;
      rfEl.style.background = prev.rfBg;
      viewportEl.style.transform = prev.vpTransform;
      viewportEl.style.transformOrigin = prev.vpOrigin;
      if (renderer && prev.rendererOverflow !== undefined) renderer.style.overflow = prev.rendererOverflow;
      if (pane && prev.paneOverflow !== undefined) pane.style.overflow = prev.paneOverflow;
      if (controls && prev.controlsDisplay !== undefined) controls.style.display = prev.controlsDisplay;
      if (bg && prev.bgDisplay !== undefined) bg.style.display = prev.bgDisplay;
      if (attr && prev.attrDisplay !== undefined) attr.style.display = prev.attrDisplay;
      handles.forEach((el, i) => (el.style.display = prev.handleDisplays[i] ?? ""));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // Programmatic export (no download) for batch rendering / automation.
  // Returns a data URL produced by html-to-image.
  const exportDiagramDataUrl = async (
    format: "svg" | "png",
    opts?: { pixelRatio?: number; filename?: string }
  ): Promise<{ dataUrl: string; filename: string } | null> => {
    const wrapper = rfWrapRef.current;
    const rfEl = wrapper?.querySelector(".react-flow") as HTMLElement | null;
    if (!rfEl) {
      setError("React Flow root not found");
      return null;
    }

    const viewportEl =
      (rfEl.querySelector(".react-flow__viewport") as HTMLElement | null) ||
      (rfEl.querySelector(".react-flow__transform") as HTMLElement | null) ||
      (rfEl.querySelector(".react-flow__transformationpane") as HTMLElement | null);
    if (!viewportEl) {
      setError("React Flow viewport not found");
      return null;
    }

    try {
      setExporting(true);
      setError(null);

      const exportNodes = getExportNodes();
      const nodeById: Record<string, RFExportNode> = {};
      exportNodes.forEach((n) => (nodeById[n.id] = n));
      const allIds = exportNodes.map((n) => n.id);
      const bounds = boundsForNodeIds(nodeById, allIds);
      if (!bounds) throw new Error("Could not compute diagram bounds for export");

      const PAD = 320;
      const width = Math.max(1200, Math.ceil(bounds.width + PAD));
      const height = Math.max(900, Math.ceil(bounds.height + PAD));
      const viewport = getViewportForBounds(bounds, width, height, 0.02, 2, 0.2);

      const controls = rfEl.querySelector(".react-flow__controls") as HTMLElement | null;
      const bg = rfEl.querySelector(".react-flow__background") as HTMLElement | null;
      const attr = rfEl.querySelector(".react-flow__attribution") as HTMLElement | null;
      const renderer = rfEl.querySelector(".react-flow__renderer") as HTMLElement | null;
      const pane = rfEl.querySelector(".react-flow__pane") as HTMLElement | null;
      const handles = Array.from(rfEl.querySelectorAll(".react-flow__handle")) as HTMLElement[];

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const prev = {
        rfWidth: rfEl.style.width,
        rfHeight: rfEl.style.height,
        rfBg: rfEl.style.background,
        vpTransform: viewportEl.style.transform,
        vpOrigin: viewportEl.style.transformOrigin,
        rendererOverflow: renderer?.style.overflow,
        paneOverflow: pane?.style.overflow,
        controlsDisplay: controls?.style.display,
        bgDisplay: bg?.style.display,
        attrDisplay: attr?.style.display,
        handleDisplays: handles.map((el) => el.style.display),
      };

      // Apply export styles
      rfEl.style.width = `${width}px`;
      rfEl.style.height = `${height}px`;
      rfEl.style.background = "#f8fafc";
      if (renderer) renderer.style.overflow = "visible";
      if (pane) pane.style.overflow = "visible";
      if (controls) controls.style.display = "none";
      if (bg) bg.style.display = "none";
      if (attr) attr.style.display = "none";
      handles.forEach((el) => (el.style.display = "none"));

      viewportEl.style.transformOrigin = "0 0";
      viewportEl.style.transform = `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`;

      await new Promise((r) => requestAnimationFrame(() => r(null)));
      await new Promise((r) => requestAnimationFrame(() => r(null)));

      const commonOpts = {
        cacheBust: true,
        backgroundColor: "#f8fafc",
        width,
        height,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore html-to-image supports this option
        skipFonts: true,
      } as const;

      let dataUrl: string;
      let filename: string;
      if (format === "svg") {
        dataUrl = await toSvg(rfEl, commonOpts);
        filename = opts?.filename || `topology-${sanitizeFileName(new Date().toISOString())}.svg`;
      } else {
        const MAX_DIM = 16384;
        const maxRatioW = Math.floor(MAX_DIM / width);
        const maxRatioH = Math.floor(MAX_DIM / height);
        const autoRatio = Math.max(1, Math.min(6, maxRatioW, maxRatioH));
        const wanted = Number.isFinite(opts?.pixelRatio) ? Math.floor(Number(opts?.pixelRatio)) : autoRatio;
        const pixelRatio = Math.max(1, Math.min(autoRatio, wanted));
        dataUrl = await toPng(rfEl, { ...commonOpts, pixelRatio });
        filename = opts?.filename || `topology-${sanitizeFileName(new Date().toISOString())}.png`;
      }

      return { dataUrl, filename };
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed");
      return null;
    } finally {
      try {
        // Restore styles
        const wrapper2 = rfWrapRef.current;
        const rfEl2 = wrapper2?.querySelector(".react-flow") as HTMLElement | null;
        const viewportEl2 =
          (rfEl2?.querySelector(".react-flow__viewport") as HTMLElement | null) ||
          (rfEl2?.querySelector(".react-flow__transform") as HTMLElement | null) ||
          (rfEl2?.querySelector(".react-flow__transformationpane") as HTMLElement | null);
        if (rfEl2 && viewportEl2) {
          // best-effort restore; if nodes changed, at least don't leave the UI broken
          rfEl2.style.width = "";
          rfEl2.style.height = "";
          rfEl2.style.background = "";
          viewportEl2.style.transform = "";
          viewportEl2.style.transformOrigin = "";
        }
      } catch (_) {
        // ignore
      }
      setExporting(false);
    }
  };

  // Expose a stable hook for headless batch exports (Playwright).
  useEffect(() => {
    (window as any).__ctxrTopologyExport = async (
      format: "png" | "svg",
      opts?: { pixelRatio?: number; filename?: string }
    ) => {
      return await exportDiagramDataUrl(format, opts);
    };
    (window as any).__ctxrTopologyExportReady = nodes.length > 0;
    return () => {
      try {
        delete (window as any).__ctxrTopologyExport;
        delete (window as any).__ctxrTopologyExportReady;
      } catch (_) {
        // ignore
      }
    };
  }, [nodes.length]);

  // --- Filter Panel Helpers ---
  const getProviderLabel = (providerId: string) => {
    const labels: Record<string, string> = {
      aws: "AWS",
      oci: "OCI",
      azure: "Azure",
      gcp: "GCP",
    };
    return labels[providerId] || providerId.toUpperCase();
  };

  // Filter panel data (must be outside any nested component to avoid focus issues)
  const filterProviders = topologyIndex?.providers || [];
  const filterRegions = filterState.provider?.regions || [];
  const filterAccounts = filterState.region?.accounts || [];
  const filteredAccounts = filterAccounts.filter((acc) => {
    // Filter by search query
    const matchesSearch =
      acc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      acc.id.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filter out empty accounts if toggle is enabled
    const matchesEmpty = !excludeEmpty || !acc.isEmpty;
    
    return matchesSearch && matchesEmpty;
  });
  
  // Count empty accounts for display
  const emptyAccountCount = filterAccounts.filter((acc) => acc.isEmpty).length;

  const handleProviderSelect = (provider: CloudProvider) => {
    setFilterState({ provider, region: null, account: null });
    setSearchQuery("");
  };

  const handleRegionSelect = (region: Region) => {
    setFilterState((prev) => ({ ...prev, region, account: null }));
    setSearchQuery("");
  };

  const handleAccountSelect = (account: Account) => {
    setFilterState((prev) => ({ ...prev, account }));
    setIsFilterExpanded(false);
  };

  // =============================================================================
  // TOPOLOGY SEARCH FUNCTIONS
  // =============================================================================

  // Helper to map node types
  const mapNodeType = (type: string): SearchResultType => {
    const typeMap: Record<string, SearchResultType> = {
      'subnet': 'subnet',
      'eni': 'eni',
      'vnic': 'eni',
      'instance': 'instance',
      'ec2': 'instance',
      'sg': 'sg',
      'security_group': 'sg',
      'nsg': 'sg',
      'rtb': 'rtb',
      'route_table': 'rtb',
      'nat': 'natgw',
      'nat_gateway': 'natgw',
      'igw': 'igw',
      'internet_gateway': 'igw',
      'vpc': 'vpc',
      'vcn': 'vpc',
      'az': 'az',
    };
    return typeMap[type.toLowerCase()] || 'subnet';
  };

  // Build a map of security group ID -> name/description for searching
  const sgNameMap = useMemo(() => {
    const map: Record<string, { name: string; description?: string; groupName?: string }> = {};
    if (!discoveryData) return map;
    
    // AWS VPCs
    const vpcs = discoveryData.vpcs || {};
    Object.values(vpcs).forEach((vpc: any) => {
      const sgs = vpc?.security_groups || [];
      sgs.forEach((sg: any) => {
        const sgId = sg.id || sg.GroupId;
        if (sgId) {
          map[sgId] = {
            name: sg.tags?.Name || sg.group_name || sg.GroupName || sgId,
            description: sg.description || sg.Description,
            groupName: sg.group_name || sg.GroupName,
          };
        }
      });
    });
    
    return map;
  }, [discoveryData]);

  // Search function that searches in React Flow nodes
  const searchInTopology = useCallback((query: string): SearchResult[] => {
    if (!query.trim() || !nodes.length) return [];
    
    const q = query.toLowerCase().trim();
    const results: SearchResult[] = [];
    
    // First, find any security groups that match the query by name/description
    const matchingSgIds: Set<string> = new Set();
    Object.entries(sgNameMap).forEach(([sgId, sgInfo]) => {
      if (
        sgInfo.name?.toLowerCase().includes(q) ||
        sgInfo.description?.toLowerCase().includes(q) ||
        sgInfo.groupName?.toLowerCase().includes(q) ||
        sgId.toLowerCase().includes(q)
      ) {
        matchingSgIds.add(sgId);
      }
    });
    
    // Recursive function to search nodes and their children
    const searchNode = (node: Node) => {
      const data = node.data || {};
      const nodeType = data.kind || data.type || node.type || 'unknown';
      
      // Fields to search based on node type
      const searchableFields: { field: string; value: string | undefined }[] = [
        { field: 'name', value: data.label },
        { field: 'name', value: data.name },
        { field: 'name', value: data.display_name },
        { field: 'id', value: node.id },
        { field: 'cidr', value: data.cidr_block },
        { field: 'cidr', value: data.cidr },
        { field: 'ip', value: data.private_ip },
        { field: 'ip', value: data.private_ip_address },
        { field: 'ip', value: data.public_ip },
        { field: 'description', value: data.description },
        // Route table name
        { field: 'routeTable', value: data.routeTable?.name },
        { field: 'routeTable', value: data.routeTable?.id },
      ];
      
      // Search in AWS Network ACL (attached to subnets)
      if (data.networkAcl) {
        searchableFields.push(
          { field: 'acl', value: data.networkAcl.id },
          { field: 'acl', value: data.networkAcl.name }
        );
      }
      
      // Search in OCI Security Lists (attached to subnets)
      if (data.securityLists && Array.isArray(data.securityLists)) {
        data.securityLists.forEach((sl: { id?: string; name?: string; display_name?: string }) => {
          searchableFields.push(
            { field: 'securityList', value: sl.id },
            { field: 'securityList', value: sl.name },
            { field: 'securityList', value: sl.display_name }
          );
        });
      }
      
      // Check if this ENI uses any matching security groups
      let matchedSgName: string | null = null;
      if ((nodeType === 'eni' || nodeType === 'vnic') && matchingSgIds.size > 0 && data.label) {
        const label = String(data.label);
        const sgMatch = label.match(/sg:\s*([^\n]+)/i);
        if (sgMatch) {
          const sgIds = sgMatch[1].split(/\s+/).filter((id: string) => id.startsWith('sg-'));
          for (const sgId of sgIds) {
            if (matchingSgIds.has(sgId)) {
              matchedSgName = sgNameMap[sgId]?.name || sgId;
              break;
            }
          }
        }
      }
      
      // If this ENI matches via security group, add it
      if (matchedSgName && !results.some(r => r.nodeId === node.id)) {
        results.push({
          id: node.id,
          type: mapNodeType(nodeType),
          label: data.label || data.name || data.display_name || node.id,
          sublabel: `SG: ${matchedSgName}`,
          nodeId: node.id,
          matchField: 'sg'
        });
        return;
      }
      
      // Search all fields
      for (const { field, value } of searchableFields) {
        if (value && String(value).toLowerCase().includes(q)) {
          // Avoid duplicates
          if (!results.some(r => r.nodeId === node.id)) {
            // Determine sublabel based on what matched
            let sublabel = data.cidr_block || data.cidr || data.private_ip_address || data.private_ip;
            if (field === 'acl' && data.networkAcl) {
              sublabel = `ACL: ${data.networkAcl.id}`;
            } else if (field === 'securityList') {
              const matchedSl = data.securityLists?.find((sl: any) => 
                sl.id?.toLowerCase().includes(q) || 
                sl.name?.toLowerCase().includes(q) || 
                sl.display_name?.toLowerCase().includes(q)
              );
              if (matchedSl) {
                sublabel = `SL: ${matchedSl.display_name || matchedSl.name || matchedSl.id}`;
              }
            } else if (field === 'routeTable' && data.routeTable) {
              sublabel = `RTB: ${data.routeTable.name || data.routeTable.id}`;
            }
            
            results.push({
              id: node.id,
              type: mapNodeType(nodeType),
              label: data.label || data.name || data.display_name || node.id,
              sublabel,
              nodeId: node.id,
              matchField: field
            });
          }
          break;
        }
      }
    };
    
    // Search all nodes
    nodes.forEach(node => searchNode(node));
    
    // Sort: exact matches first, then by type
    results.sort((a, b) => {
      const aExact = a.label.toLowerCase() === q;
      const bExact = b.label.toLowerCase() === q;
      if (aExact && !bExact) return -1;
      if (bExact && !aExact) return 1;
      
      const typeOrder: SearchResultType[] = ['subnet', 'eni', 'instance', 'sg', 'rtb', 'natgw', 'igw', 'vpc', 'az'];
      return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
    });
    
    return results.slice(0, 50);
  }, [nodes, sgNameMap]);

  // Zoom to node function
  const zoomToNode = useCallback((nodeId: string) => {
    const rfInstance = rfRef.current;
    if (!rfInstance) return;
    
    const node = nodes.find(n => n.id === nodeId);
    if (!node) {
      console.warn(`Node ${nodeId} not found`);
      return;
    }
    
    // Use fitView with the specific node
    rfInstance.fitView({
      nodes: [{ id: nodeId }],
      padding: 0.5,
      duration: 800,
      maxZoom: 2,
    });
    
    // Highlight the node - stays until user closes mini-bar or opens Security
    setHighlightedSearchNodeId(nodeId);
  }, [nodes]);
  
  // Track previous security state to detect when it's toggled ON
  const prevSecurityEnabled = useRef(securityEnabled);
  
  // Clear search highlight only when Security panel is TOGGLED ON (not when already active)
  useEffect(() => {
    // Only clear if Security was just turned ON (false -> true)
    if (securityEnabled && !prevSecurityEnabled.current && highlightedSearchNodeId) {
      setHighlightedSearchNodeId(null);
      setTopologySearchResults([]);
    }
    prevSecurityEnabled.current = securityEnabled;
  }, [securityEnabled, highlightedSearchNodeId]);

  // Keyboard shortcuts for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K = Open search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setTopologySearchOpen(true);
        setTopologySearchQuery('');
        setTopologySearchResults([]);
        setTopologySearchIndex(0);
        setTimeout(() => topologySearchInputRef.current?.focus(), 50);
      }
      
      // Escape = Close search
      if (e.key === 'Escape' && topologySearchOpen) {
        e.preventDefault();
        setTopologySearchOpen(false);
        setHighlightedSearchNodeId(null);
      }
      
      // If search is open and has results
      if (topologySearchOpen && topologySearchResults.length > 0) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setTopologySearchIndex(prev => Math.max(0, prev - 1));
        }
        
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setTopologySearchIndex(prev => Math.min(topologySearchResults.length - 1, prev + 1));
        }
        
        if (e.key === 'Enter') {
          e.preventDefault();
          const result = topologySearchResults[topologySearchIndex];
          if (result) {
            zoomToNode(result.nodeId);
            setTopologySearchOpen(false);
          }
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [topologySearchOpen, topologySearchResults, topologySearchIndex, zoomToNode]);

  // Real-time search effect
  useEffect(() => {
    if (!topologySearchOpen) return;
    
    const timer = setTimeout(() => {
      const results = searchInTopology(topologySearchQuery);
      setTopologySearchResults(results);
      setTopologySearchIndex(0);
    }, 150);
    
    return () => clearTimeout(timer);
  }, [topologySearchQuery, topologySearchOpen, searchInTopology]);

  // --- Filter Panel JSX (inline to preserve input focus) ---
  // Position adjusts based on sidebar state: expanded = left-80 (320px), collapsed = left-24 (96px)
  const filterPanelJSX = (
    <div className={`fixed top-28 z-[100] opacity-60 hover:opacity-100 transition-all duration-300 ${isSidebarExpanded ? 'left-80' : 'left-24'}`}>
        {/* Collapsed state - minimal floating pill */}
        {!isFilterExpanded && filterState.account && (
          <div className="group relative">
            {/* Compact pill - just icon + account name */}
            <button
              onClick={() => setIsFilterExpanded(true)}
              className="flex items-center gap-2 bg-semantic-bg-secondary backdrop-blur-sm rounded-full shadow-lg border border-semantic-border-primary pl-2 pr-3 py-1.5 hover:shadow-xl transition-all duration-200 hover:scale-105"
              title="Click to edit filters"
            >
              <span className="w-6 h-6 rounded-full bg-semantic-bg-success-bold flex items-center justify-center">
                {filterState.provider && (
                  <span className="text-semantic-fg-base text-[10px] font-bold">
                    {filterState.provider.id === 'aws' ? 'A' : filterState.provider.id === 'oci' ? 'O' : 'C'}
                  </span>
                )}
              </span>
              <span className="text-xs font-semibold text-semantic-text-primary max-w-[120px] truncate">
                {filterState.account.name}
              </span>
              <ChevronDown className="w-3 h-3 text-semantic-text-secondary" />
            </button>
            
            {/* Tooltip on hover showing full filter path */}
            <div className="absolute left-0 top-full mt-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
              <div className="bg-semantic-bg-tertiary text-semantic-text-primary text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap border border-semantic-border-primary">
                <div className="flex items-center gap-2">
                  <span>{filterState.provider && getProviderLabel(filterState.provider.id)}</span>
                  <span className="text-semantic-text-secondary">&gt;</span>
                  <span>{filterState.region?.name}</span>
                  <span className="text-semantic-text-secondary">&gt;</span>
                  <span className="font-semibold">{filterState.account.name}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expanded filter panel */}
        {isFilterExpanded && (
          <div className="w-80 bg-white backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200 overflow-hidden dark:bg-semantic-bg-primary dark:border-semantic-border-primary">
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 dark:border-semantic-border-primary dark:bg-semantic-bg-secondary">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-semantic-text-secondary" />
                  <h3 className="font-semibold text-semantic-text-primary text-sm">Topology Filter</h3>
                </div>
                {filterState.account && (
                  <button
                    onClick={() => setIsFilterExpanded(false)}
                    className="text-xs text-semantic-text-secondary hover:text-semantic-text-primary transition-colors"
                  >
                    Collapse
                  </button>
                )}
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Step 1: Cloud Provider */}
              <div>
                <label className="block text-xs font-semibold text-semantic-text-secondary mb-2 uppercase tracking-wide">
                  1. Cloud Provider
                </label>
                <div className="relative">
                  <select
                    value={filterState.provider?.id || ""}
                    onChange={(e) => {
                      const provider = filterProviders.find((p) => p.id === e.target.value);
                      if (provider) handleProviderSelect(provider);
                    }}
                    className="w-full px-3 py-2.5 pr-10 rounded-xl border-2 border-semantic-border-primary bg-semantic-bg-secondary text-sm font-medium text-semantic-text-primary appearance-none cursor-pointer hover:border-semantic-border-secondary focus:border-semantic-outline-primary focus:ring-2 focus:ring-semantic-bg-brand-secondary transition-all"
                  >
                    <option value="">Select a cloud provider...</option>
                    {filterProviders.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {getProviderLabel(provider.id)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-semantic-text-secondary pointer-events-none" />
                </div>
              </div>

              {/* Step 2: Region */}
              {filterState.provider && (
                <div>
                  <label className="block text-xs font-semibold text-semantic-text-secondary mb-2 uppercase tracking-wide">
                    2. Region
                  </label>
                  <div className="relative">
                    <select
                      value={filterState.region?.id || ""}
                      onChange={(e) => {
                        const region = filterRegions.find((r) => r.id === e.target.value);
                        if (region) handleRegionSelect(region);
                      }}
                      className="w-full px-3 py-2.5 pr-10 rounded-xl border-2 border-semantic-border-primary bg-semantic-bg-secondary text-sm font-medium text-semantic-text-primary appearance-none cursor-pointer hover:border-semantic-border-secondary focus:border-semantic-outline-primary focus:ring-2 focus:ring-semantic-bg-brand-secondary transition-all"
                    >
                      <option value="">Select a region...</option>
                      {filterRegions.map((region) => (
                        <option key={region.id} value={region.id}>
                          {region.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-semantic-text-secondary pointer-events-none" />
                  </div>
                </div>
              )}

              {/* Step 3: Account */}
              {filterState.region && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-semibold text-semantic-text-secondary uppercase tracking-wide">
                      3. Account <span className="text-semantic-text-secondary font-normal">({filteredAccounts.length})</span>
                    </label>
                    {/* Exclude empty toggle */}
                    {emptyAccountCount > 0 && (
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={excludeEmpty}
                          onChange={(e) => setExcludeEmpty(e.target.checked)}
                          className="w-3.5 h-3.5 rounded border-semantic-border-secondary text-semantic-bg-brand-bold focus:ring-semantic-outline-primary focus:ring-offset-0"
                        />
                        <span className="text-xs text-semantic-text-secondary">
                          Hide empty ({emptyAccountCount})
                        </span>
                      </label>
                    )}
                  </div>
                  
                  {/* Search input */}
                  {filterAccounts.length > 5 && (
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-semantic-text-secondary" />
                      <input
                        type="text"
                        placeholder="Search accounts..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary text-semantic-text-primary text-sm placeholder:text-semantic-text-secondary focus:border-semantic-outline-primary focus:ring-2 focus:ring-semantic-bg-brand-secondary transition-all"
                      />
                    </div>
                  )}

                  {/* Account list */}
                  <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 custom-scrollbar">
                    {filteredAccounts.map((account) => (
                      <button
                        key={account.id}
                        onClick={() => handleAccountSelect(account)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 text-left transition-all duration-200 ${
                          filterState.account?.id === account.id
                            ? "border-semantic-border-success-secondary bg-semantic-bg-success-secondary shadow-sm"
                            : "border-semantic-border-primary hover:border-semantic-border-secondary hover:bg-semantic-bg-tertiary"
                        }`}
                      >
                        <Building2 className={`w-4 h-4 flex-shrink-0 ${
                          filterState.account?.id === account.id ? "text-semantic-text-success" : account.isEmpty ? "text-semantic-text-secondary" : "text-semantic-text-secondary"
                        }`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium truncate ${account.isEmpty ? "text-semantic-text-secondary" : "text-semantic-text-primary"}`}>
                              {account.name}
                            </span>
                            {account.vpcCount !== undefined && (
                              <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                account.isEmpty 
                                  ? "bg-semantic-bg-tertiary text-semantic-text-secondary" 
                                  : "bg-semantic-bg-brand-secondary text-semantic-text-brand"
                              }`}>
                                {account.vpcCount} {filterState.provider?.id === 'oci' ? 'VCN' : 'VPC'}{account.vpcCount !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-semantic-text-secondary font-mono truncate">
                            {account.id}
                          </div>
                        </div>
                      </button>
                    ))}
                    {filteredAccounts.length === 0 && (
                      <div className="text-center py-4 text-sm text-semantic-text-secondary">
                        No accounts found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Progress indicator */}
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 dark:bg-semantic-bg-secondary dark:border-semantic-border-primary">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${filterState.provider ? "bg-semantic-bg-success-bold" : "bg-semantic-border-tertiary"}`} />
                <div className={`flex-1 h-0.5 ${filterState.provider ? "bg-semantic-bg-success-bold" : "bg-semantic-border-primary"}`} />
                <div className={`w-2 h-2 rounded-full ${filterState.region ? "bg-semantic-bg-success-bold" : "bg-semantic-border-tertiary"}`} />
                <div className={`flex-1 h-0.5 ${filterState.region ? "bg-semantic-bg-success-bold" : "bg-semantic-border-primary"}`} />
                <div className={`w-2 h-2 rounded-full ${filterState.account ? "bg-semantic-bg-success-bold" : "bg-semantic-border-tertiary"}`} />
              </div>
              <div className="flex justify-between mt-1.5 text-[10px] font-medium text-semantic-text-secondary">
                <span>Provider</span>
                <span>Region</span>
                <span>Account</span>
              </div>
            </div>
          </div>
        )}
      </div>
  );

  // --- Empty Filter State Component ---
  const EmptyFilterState = () => (
    <div className="absolute inset-0 flex items-center justify-center bg-semantic-bg-primary">
      <div className="text-center max-w-md px-6">
        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-semantic-bg-brand-bold flex items-center justify-center shadow-lg">
          <CloudIcon className="w-10 h-10 text-semantic-fg-base" />
        </div>
        <h2 className="text-xl font-bold text-semantic-text-primary mb-2">
          Select a Topology
        </h2>
        <p className="text-semantic-text-secondary text-sm leading-relaxed">
          Use the filter panel on the left to select a cloud provider, region, and account to visualize the network topology.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3 text-xs text-semantic-text-secondary">
          <span className="flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full bg-semantic-bg-warning-secondary flex items-center justify-center font-bold text-[10px] text-semantic-text-warning">A</span>
            AWS
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full bg-semantic-bg-danger-secondary flex items-center justify-center font-bold text-[10px] text-semantic-text-danger">O</span>
            OCI
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full bg-semantic-bg-brand-secondary flex items-center justify-center font-bold text-[10px] text-semantic-text-brand">AZ</span>
            Azure
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full bg-semantic-bg-success-secondary flex items-center justify-center font-bold text-[10px] text-semantic-text-success">G</span>
            GCP
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative flex h-screen w-full flex-col font-sans bg-semantic-bg-primary">
      {/* Filter Panel - Always visible */}
      {filterPanelJSX}

      {/* Show empty state when no account selected */}
      {!filterState.account && !loading && <EmptyFilterState />}

      <div ref={rfWrapRef} className="flex-1 h-full w-full relative">
        {/* Export and Security buttons - only show when topology is visible */}
        {filterState.account && nodes.length > 0 && (
        <div className="fixed right-4 top-28 z-[100] flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity duration-200">
          {/* Inline Search Bar */}
          <button
            type="button"
            onClick={() => {
              setTopologySearchOpen(true);
              setTopologySearchQuery('');
              setTopologySearchResults([]);
              setTopologySearchIndex(0);
              setTimeout(() => topologySearchInputRef.current?.focus(), 50);
            }}
            className="flex items-center gap-2 rounded-lg bg-semantic-bg-secondary px-3 py-2 text-xs font-medium text-semantic-text-secondary shadow-md border border-semantic-border-primary hover:bg-semantic-bg-tertiary hover:text-semantic-text-primary transition-all min-w-[180px]"
            title="Search topology (Cmd+K)"
          >
            <Search className="h-4 w-4" />
            <span>Search...</span>
            <kbd className="ml-auto text-[10px] px-1.5 py-0.5 bg-semantic-bg-tertiary rounded text-semantic-text-secondary">
              {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+K
            </kbd>
          </button>
          
          <div className="w-px h-6 bg-semantic-border-primary" />
          
          {/* Security Toggle */}
          <button
            type="button"
            onClick={toggleSecurity}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold shadow-md border-2 transition-all ${
              securityEnabled
                ? 'border-semantic-border-brand-secondary bg-semantic-bg-brand-secondary text-semantic-text-brand'
                : 'border-semantic-border-primary bg-semantic-bg-secondary text-semantic-text-primary hover:border-semantic-border-secondary'
            }`}
            title="Toggle Network Security Analysis"
          >
            <Shield className="h-4 w-4" />
            <span>Security</span>
            {securityResults && (
              <span className={`ml-0.5 px-1.5 py-0.5 text-[10px] rounded-full font-bold ${
                securityEnabled 
                  ? 'bg-semantic-bg-brand-bold text-semantic-fg-base' 
                  : 'bg-semantic-bg-tertiary text-semantic-text-secondary'
              }`}>
                {securityResults.totalFindings ?? securityResults.summary?.total ?? securityResults.findings?.length ?? 0}
              </span>
            )}
          </button>

          {/* Traffic Analyzer */}
          <button
            type="button"
            onClick={() => setShowTrafficAnalyzer(true)}
            disabled={!discoveryData}
            className="flex items-center gap-2 rounded-lg bg-semantic-bg-secondary px-3 py-2 text-xs font-semibold text-semantic-text-primary shadow-md border border-semantic-border-primary hover:bg-semantic-bg-brand-secondary hover:border-semantic-border-brand-primary hover:text-semantic-text-brand disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            title={discoveryData ? "Analyze VPC Flow Logs traffic paths" : "Load a topology first to analyze traffic"}
          >
            <Activity className="h-4 w-4" />
            <span>Traffic Analyzer</span>
          </button>
          
          <div className="w-px h-6 bg-semantic-border-primary" />
          
          <button
            type="button"
            onClick={() => exportDiagram("svg")}
            disabled={exporting || !!error || nodes.length === 0}
            className="flex items-center gap-2 rounded-lg bg-semantic-bg-secondary px-3 py-2 text-xs font-semibold text-semantic-text-primary shadow-md border border-semantic-border-primary hover:bg-semantic-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export topology as SVG"
          >
            <Download className="h-4 w-4" />
            {exporting ? "Exporting…" : "Export SVG"}
          </button>
          <button
            type="button"
            onClick={() => exportDiagram("png")}
            disabled={exporting || !!error || nodes.length === 0}
            className="flex items-center gap-2 rounded-lg bg-semantic-bg-secondary px-3 py-2 text-xs font-semibold text-semantic-text-primary shadow-md border border-semantic-border-primary hover:bg-semantic-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export topology as PNG"
          >
            <Download className="h-4 w-4" />
            {exporting ? "Exporting..." : "Export PNG"}
          </button>
        </div>
        )}
        
        {/* Loading state */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-semantic-bg-primary/80 backdrop-blur-sm z-40">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 border-semantic-bg-brand-bold border-t-transparent rounded-full animate-spin" />
              <span className="text-sm font-medium text-semantic-text-secondary">Loading topology...</span>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-semantic-bg-danger-secondary border border-semantic-border-danger-primary rounded-xl px-6 py-4 max-w-md">
              <p className="text-semantic-text-danger text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* ReactFlow - only render when we have data */}
        {filterState.account && !loading && !error && nodes.length > 0 && (
          <ReactFlow
            nodes={nodesWithSecurity}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onInit={(instance) => {
              rfRef.current = instance;
              setReactFlowReady(true);
            }}
            onNodeClick={(event, node) => {
              // Show modal for ENI/VNIC, routing tables, security groups, and gateways
              const kind = node.data?.kind || node.data?.type;
              if (kind === 'eni' || kind === 'vnic' || kind === 'rtb' || kind === 'route_table' || kind === 'sg' || 
                  kind === 'nat' || kind === 'tgw' || kind === 'igw' || kind === 'drg' || kind === 'sgw' || kind === 'vgw') {
                setSelectedNode(node);
              }
            }}
            fitView
            fitViewOptions={{ padding: 0.1 }}
            nodesDraggable={false}
            nodesConnectable={false}
            defaultEdgeOptions={defaultEdgeOptions}
            className="bg-slate-100 [&_.react-flow__pane]:!cursor-default [&_.react-flow__node]:!cursor-default dark:bg-semantic-bg-primary"
            minZoom={0.02}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="var(--color-semantic-border-secondary)" gap={24} size={1} />
            {/* Controls (zoom/fit) hidden per UX - use browser zoom or trackpad if needed */}
            <Controls className="!hidden" />
          </ReactFlow>
        )}

        {/* Network Security Panel - inside the topology wrapper */}
        {securityEnabled && filterState.account && (
          <NetworkSecurityPanel
            results={securityResults}
            loading={securityLoading}
            selectedFinding={selectedFinding}
            highlightedSgId={highlightedSgId}
            sgsWithEnis={sgsWithEnis}
            allSecurityResources={allSecurityResources}
            discoveryData={discoveryData}
            onSelectFinding={selectFinding}
            onToggleHighlight={toggleHighlightSg}
            onAskAI={handleAskAIAboutFinding}
            onClose={toggleSecurity}
            onViewAllRules={handleViewAllRules}
          />
        )}
      </div>

      {/* Traffic Analyzer Modal */}
      {showTrafficAnalyzer && discoveryData && (
        <TrafficAnalyzer
          discoveryData={discoveryData}
          onClose={() => setShowTrafficAnalyzer(false)}
        />
      )}

      {/* TGW Visualization Modal */}
      {selectedTgwForVisualization && discoveryData && (
        <TGWVisualizationModal
          discoveryData={discoveryData}
          tgwId={selectedTgwForVisualization}
          onClose={() => setSelectedTgwForVisualization(null)}
        />
      )}

      {/* Resource Details Modal */}
      {selectedNode && (() => {
        const kind = selectedNode.data?.kind || selectedNode.data?.type;
        const isEni = kind === 'eni' || kind === 'vnic';
        const isRtb = kind === 'rtb' || kind === 'route_table';
        const isSg = kind === 'sg';
        const isNat = kind === 'nat';
        const isTgw = kind === 'tgw';
        const isIgw = kind === 'igw';
        const isDrg = kind === 'drg';
        const isSgw = kind === 'sgw';
        const isVgw = kind === 'vgw';
        const isGateway = isNat || isTgw || isIgw || isDrg || isSgw || isVgw;

        // Gateway name helpers
        const getGatewayTypeName = () => {
          if (isNat) return 'NAT Gateway';
          if (isTgw) return 'Transit Gateway';
          if (isIgw) return 'Internet Gateway';
          if (isDrg) return 'Dynamic Routing Gateway';
          if (isSgw) return 'Service Gateway';
          if (isVgw) return 'Virtual Private Gateway';
          return 'Gateway';
        };

        // Helper to get name from AWS Tags array
        const getNameFromTags = (tags: any[]): string | null => {
          if (!Array.isArray(tags)) return null;
          const nameTag = tags.find((t: any) => 
            t.Key === 'Name' || t.key === 'Name' || t.Key === 'name' || t.key === 'name'
          );
          return nameTag?.Value || nameTag?.value || null;
        };

        // Helper to find RTB name by ID - check topology nodes first!
        const getRtbName = (rtbId: string): string | null => {
          if (!rtbId) return null;
          
          // First check topology nodes - they have the correct labels
          // Search for nodes where kind is 'rtb' or 'route_table'
          const rtbNodes = nodes.filter(n => 
            n.data?.kind === 'rtb' || n.data?.kind === 'route_table' || n.data?.type === 'rtb'
          );
          
          // Find the matching RTB node
          const rtbNode = rtbNodes.find(n => {
            const nodeId = String(n.id || '');
            const nodeDataId = String(n.data?.id || '');
            const nodeRtbId = String(n.data?.route_table_id || '');
            
            // Exact match
            if (nodeId === rtbId || nodeDataId === rtbId || nodeRtbId === rtbId) return true;
            
            // Partial match - check if rtbId is contained in any of the node identifiers
            if (nodeId.includes(rtbId) || nodeDataId.includes(rtbId) || nodeRtbId.includes(rtbId)) return true;
            
            // Reverse check - if node ID is contained in rtbId
            if (rtbId.includes(nodeId) && nodeId.startsWith('rtb-')) return true;
            if (rtbId.includes(nodeDataId) && nodeDataId.startsWith('rtb-')) return true;
            
            return false;
          });
          
          if (rtbNode) {
            // Check label first
            if (rtbNode.data?.label) {
              const label = String(rtbNode.data.label);
              // Label might be multi-line, get first line
              const firstLine = label.split('\n')[0].trim();
              if (firstLine && !firstLine.startsWith('rtb-')) return firstLine;
            }
            if (rtbNode.data?.name) return rtbNode.data.name;
            if (rtbNode.data?.display_name) return rtbNode.data.display_name;
          }
          
          return null;
        };

        // Helper to find subnet name by ID
        const getSubnetName = (subnetId: string): string | null => {
          if (!discoveryData) return null;
          
          // Search in topology nodes first (they have labels)
          const subnetNode = nodes.find(n => 
            n.id === subnetId || 
            n.data?.id === subnetId || 
            n.data?.subnet_id === subnetId
          );
          if (subnetNode?.data?.label) {
            // Extract just the name part if label contains multiple lines
            const label = String(subnetNode.data.label);
            const firstLine = label.split('\n')[0].trim();
            if (firstLine && !firstLine.startsWith('subnet-')) return firstLine;
          }
          if (subnetNode?.data?.name) return subnetNode.data.name;
          if (subnetNode?.data?.display_name) return subnetNode.data.display_name;

          // Search in AWS VPCs
          if (discoveryData.vpcs) {
            const searchVpc = (vpc: any) => {
              const subnets = vpc?.subnets || vpc?.info?.subnets || [];
              for (const subnet of subnets) {
                const sId = subnet?.subnet_id || subnet?.SubnetId || subnet?.id;
                if (sId === subnetId) {
                  return subnet.display_name || subnet.name || getNameFromTags(subnet.Tags || subnet.tags) || null;
                }
              }
              return null;
            };
            
            if (typeof discoveryData.vpcs === 'object' && !Array.isArray(discoveryData.vpcs)) {
              for (const vpcId in discoveryData.vpcs) {
                const name = searchVpc(discoveryData.vpcs[vpcId]);
                if (name) return name;
              }
            } else if (Array.isArray(discoveryData.vpcs)) {
              for (const vpc of discoveryData.vpcs) {
                const name = searchVpc(vpc);
                if (name) return name;
              }
            }
          }

          // Search in OCI VCNs
          if (Array.isArray(discoveryData.vcns)) {
            for (const vcn of discoveryData.vcns) {
              const subnets = vcn?.subnets || [];
              for (const subnet of subnets) {
                if (subnet?.id === subnetId || subnet?.subnet_id === subnetId) {
                  return subnet.display_name || subnet.name || null;
                }
              }
            }
          }

          return null;
        };

        // Find route tables that reference this gateway
        const findRouteTablesForGateway = (gatewayId: string): Array<{rtb: any, routes: any[], subnetAssociations: any[]}> => {
          const results: Array<{rtb: any, routes: any[], subnetAssociations: any[]}> = [];
          if (!discoveryData) return results;

          // Extract the base gateway ID (without any suffix like _rtb-xxx)
          // For composite IDs like "nat-d41f5a0ca8_rtb-88409331d2", extract "nat-d41f5a0ca8"
          const extractBaseGatewayId = (id: string): string[] => {
            const ids: string[] = [id];
            // Check for composite ID with underscore
            if (id.includes('_')) {
              const parts = id.split('_');
              ids.push(...parts);
            }
            // Also try just the prefix part (e.g., "nat-d41f5a0ca8" from "nat-d41f5a0ca8_rtb-...")
            const prefixMatch = id.match(/^(nat-[a-f0-9]+|igw-[a-f0-9]+|tgw-[a-f0-9]+|drg-[a-f0-9]+|sgw-[a-f0-9]+)/i);
            if (prefixMatch) {
              ids.push(prefixMatch[1]);
            }
            return Array.from(new Set(ids)); // Remove duplicates
          };
          
          const gatewayIds = extractBaseGatewayId(gatewayId);

          const searchInRouteTables = (routeTables: any[], vpcId?: string) => {
            if (!Array.isArray(routeTables)) return;
            for (const rtb of routeTables) {
              if (!rtb) continue;
              const routes = rtb.routes || rtb.route_rules || rtb.Routes || [];
              const matchingRoutes = routes.filter((r: any) => {
                // AWS: Check all gateway ID fields
                const targets = [
                  r.GatewayId, r.NatGatewayId, r.TransitGatewayId, 
                  r.VpcPeeringConnectionId, r.NetworkInterfaceId,
                  // OCI
                  r.networkEntityId, r.network_entity_id
                ].filter(Boolean).map(String);
                
                // Check if any gateway ID matches any target
                return gatewayIds.some(gid => 
                  targets.some(t => t.includes(gid) || gid.includes(t))
                );
              });
              
              if (matchingRoutes.length > 0) {
                // Get subnet associations for this RTB
                const associations = rtb.associations || rtb.Associations || [];
                const subnetAssociations = associations.filter((a: any) => 
                  a.SubnetId || a.subnet_id
                ).map((a: any) => {
                  const subnetId = a.SubnetId || a.subnet_id;
                  return {
                    subnetId,
                    subnetName: getSubnetName(subnetId),
                    main: a.Main || a.main || false
                  };
                });
                
                // Get RTB name from multiple sources - check topology nodes first!
                const rtbId = rtb.id || rtb.route_table_id || rtb.RouteTableId;
                const rtbName = getRtbName(rtbId) ||
                                rtb.display_name || 
                                rtb.name || 
                                getNameFromTags(rtb.Tags || rtb.tags) ||
                                null;
                
                results.push({
                  rtb: {
                    id: rtb.id || rtb.route_table_id || rtb.RouteTableId,
                    name: rtbName,
                    vpcId
                  },
                  routes: matchingRoutes,
                  subnetAssociations
                });
              }
            }
          };

          // Search in AWS VPCs
          if (discoveryData.vpcs) {
            if (typeof discoveryData.vpcs === 'object' && !Array.isArray(discoveryData.vpcs)) {
              for (const vpcId in discoveryData.vpcs) {
                const vpc = discoveryData.vpcs[vpcId];
                const routeTables = vpc?.route_tables || vpc?.info?.route_tables || [];
                searchInRouteTables(routeTables, vpcId);
              }
            } else if (Array.isArray(discoveryData.vpcs)) {
              for (const vpc of discoveryData.vpcs) {
                const vpcId = vpc?.vpc_id || vpc?.id;
                const routeTables = vpc?.route_tables || vpc?.info?.route_tables || [];
                searchInRouteTables(routeTables, vpcId);
              }
            }
          }

          // Search in OCI VCNs
          if (Array.isArray(discoveryData.vcns)) {
            for (const vcn of discoveryData.vcns) {
              const vcnId = vcn?.id || vcn?.vcn_id;
              const routeTables = vcn?.route_tables || [];
              searchInRouteTables(routeTables, vcnId);
            }
          }

          // IMPORTANT: Also search in topology NODES for route tables
          // This is critical for OCI where the route data is embedded in the nodes
          const rtbNodes = nodes.filter(n => 
            n.data?.kind === 'rtb' || n.data?.kind === 'route_table' || n.data?.type === 'rtb'
          );
          
          for (const rtbNode of rtbNodes) {
            const rtbData = rtbNode.data || {};
            const routes = rtbData.routes || rtbData.route_rules || rtbData.Routes || [];
            
            if (!Array.isArray(routes) || routes.length === 0) continue;
            
            // Check if this RTB was already found in discovery data
            const rtbId = rtbNode.id || rtbData.id || rtbData.route_table_id;
            const alreadyFound = results.some(r => r.rtb.id === rtbId);
            if (alreadyFound) continue;
            
            const matchingRoutes = routes.filter((r: any) => {
              // Get all possible target fields
              const targets = [
                r.GatewayId, r.NatGatewayId, r.TransitGatewayId,
                r.VpcPeeringConnectionId, r.NetworkInterfaceId,
                r.networkEntityId, r.network_entity_id,
                // Also check generic target field
                r.target, r.gateway
              ].filter(Boolean).map(String);
              
              return gatewayIds.some(gid => 
                targets.some(t => t.includes(gid) || gid.includes(t))
              );
            });
            
            if (matchingRoutes.length > 0) {
              // Get subnet associations from node data
              const associations = rtbData.associations || rtbData.subnet_associations || [];
              const subnetAssociations = Array.isArray(associations) ? associations.filter((a: any) => 
                a.SubnetId || a.subnet_id
              ).map((a: any) => {
                const subnetId = a.SubnetId || a.subnet_id;
                return {
                  subnetId,
                  subnetName: getSubnetName(subnetId),
                  main: a.Main || a.main || false
                };
              }) : [];
              
              // Get RTB name from node
              const rtbName = getRtbName(rtbId) || rtbData.name || rtbData.display_name || rtbData.label?.split('\n')[0] || null;
              
              results.push({
                rtb: {
                  id: rtbId,
                  name: rtbName,
                  vpcId: rtbData.vpc_id || rtbData.vcn_id || null
                },
                routes: matchingRoutes,
                subnetAssociations
              });
            }
          }

          // Flat structure
          if (Array.isArray(discoveryData.route_tables)) {
            searchInRouteTables(discoveryData.route_tables);
          }

          return results;
        };

        // Find ENIs/VNICs associated with a NAT Gateway
        const findEnisForNat = (natId: string): any[] => {
          const enis: any[] = [];
          if (!discoveryData) return enis;

          // Search in nodes for ENIs in the same subnet as NAT
          const natNode = nodes.find(n => n.id === natId || n.data?.id === natId);
          if (natNode?.data?.subnet_id || natNode?.data?.SubnetId) {
            const subnetId = natNode.data.subnet_id || natNode.data.SubnetId;
            const subnetEnis = nodes.filter(n => 
              (n.data?.kind === 'eni' || n.data?.kind === 'vnic') &&
              (n.data?.subnet_id === subnetId || n.data?.SubnetId === subnetId)
            );
            enis.push(...subnetEnis.map(n => n.data));
          }

          // Also check discovery data for NAT gateway network interfaces
          if (discoveryData.vpcs) {
            const searchVpc = (vpc: any) => {
              const natGateways = vpc?.nat_gateways || [];
              const nat = natGateways.find((n: any) => 
                n.NatGatewayId === natId || n.id === natId || String(n.NatGatewayId || n.id).includes(natId)
              );
              if (nat?.NatGatewayAddresses) {
                for (const addr of nat.NatGatewayAddresses) {
                  if (addr.NetworkInterfaceId) {
                    enis.push({
                      id: addr.NetworkInterfaceId,
                      private_ip: addr.PrivateIp,
                      public_ip: addr.PublicIp,
                      allocation_id: addr.AllocationId
                    });
                  }
                }
              }
            };
            if (typeof discoveryData.vpcs === 'object' && !Array.isArray(discoveryData.vpcs)) {
              for (const vpcId in discoveryData.vpcs) {
                searchVpc(discoveryData.vpcs[vpcId]);
              }
            } else if (Array.isArray(discoveryData.vpcs)) {
              for (const vpc of discoveryData.vpcs) {
                searchVpc(vpc);
              }
            }
          }

          return enis;
        };

        // Find TGW attachments
        const findTgwAttachments = (tgwId: string): any[] => {
          const attachments: any[] = [];
          if (!discoveryData) return attachments;

          // Check TGW attachments in discovery data
          const tgwAttachments = discoveryData.tgw_attachments || discoveryData.transit_gateway_attachments || [];
          for (const att of tgwAttachments) {
            if (att.TransitGatewayId === tgwId || String(att.TransitGatewayId || '').includes(tgwId)) {
              attachments.push({
                id: att.TransitGatewayAttachmentId,
                type: att.ResourceType,
                resourceId: att.ResourceId,
                state: att.State,
                vpcId: att.ResourceType === 'vpc' ? att.ResourceId : null
              });
            }
          }

          // Also search in VPCs for TGW route table associations
          if (discoveryData.vpcs) {
            const searchVpc = (vpc: any, vpcId: string) => {
              const tgwAtts = vpc?.tgw_attachments || vpc?.transit_gateway_attachments || [];
              for (const att of tgwAtts) {
                if (att.TransitGatewayId === tgwId || String(att.TransitGatewayId || '').includes(tgwId)) {
                  if (!attachments.find(a => a.id === att.TransitGatewayAttachmentId)) {
                    attachments.push({
                      id: att.TransitGatewayAttachmentId,
                      type: att.ResourceType || 'vpc',
                      resourceId: att.ResourceId || vpcId,
                      state: att.State,
                      vpcId
                    });
                  }
                }
              }
            };
            if (typeof discoveryData.vpcs === 'object' && !Array.isArray(discoveryData.vpcs)) {
              for (const vpcId in discoveryData.vpcs) {
                searchVpc(discoveryData.vpcs[vpcId], vpcId);
              }
            } else if (Array.isArray(discoveryData.vpcs)) {
              for (const vpc of discoveryData.vpcs) {
                searchVpc(vpc, vpc?.vpc_id || vpc?.id);
              }
            }
          }

          return attachments;
        };

        // Parse ENI label into individual fields
        const parseEniLabel = (label: string) => {
          const lines = String(label || "").split("\n").filter((l) => l !== "");
          const fields: Record<string, string> = {};
          const title = lines[0] || "";
          
          lines.slice(1).forEach((line) => {
            const m = line.match(/^\s*([a-zA-Z]+):\s*(.*)$/);
            if (m) {
              const key = m[1].toLowerCase();
              const val = m[2].trim();
              if (key === 'sg' || key === 'nsg') {
                // SG/NSG can be multiple, separated by spaces
                fields[key] = (fields[key] ? fields[key] + ' ' : '') + val;
              } else {
                fields[key] = val;
              }
            }
          });
          
          return { title, fields };
        };

        // Find routing rules from discovery data
        const findRoutingRules = (rtbId: string) => {
          // First, check if routes are already in the node data
          if (selectedNode.data?.routes && Array.isArray(selectedNode.data.routes) && selectedNode.data.routes.length > 0) {
            return selectedNode.data.routes;
          }
          if (selectedNode.data?.route_rules && Array.isArray(selectedNode.data.route_rules) && selectedNode.data.route_rules.length > 0) {
            return selectedNode.data.route_rules;
          }
          
          if (!discoveryData) {
            console.warn('findRoutingRules: No discovery data available');
            return null;
          }
          
          // Normalize the RTB ID (remove any prefixes or suffixes that might be added)
          const normalizedRtbId = rtbId.trim();
          
          // Structure 1: { vpcs: { "vpc-id": { route_tables: [...] } } }
          // This is the AWS format where vpcs is an object with vpc-id as keys
          if (discoveryData.vpcs && typeof discoveryData.vpcs === 'object' && !Array.isArray(discoveryData.vpcs)) {
            for (const vpcId in discoveryData.vpcs) {
              const vpc = discoveryData.vpcs[vpcId];
              if (!vpc || typeof vpc !== 'object') continue;
              
              // Check both vpc.route_tables and vpc.info.route_tables
              const routeTables = vpc.route_tables || (vpc.info && vpc.info.route_tables) || [];
              
              for (const rtb of routeTables) {
                if (!rtb || typeof rtb !== 'object') continue;
                
                // Check all possible ID fields
                const rtbIdMatch = 
                  rtb.id === normalizedRtbId ||
                  rtb.route_table_id === normalizedRtbId ||
                  rtb.RouteTableId === normalizedRtbId ||
                  String(rtb.id || '').includes(normalizedRtbId) ||
                  String(rtb.route_table_id || '').includes(normalizedRtbId);
                
                if (rtbIdMatch) {
                  const routes = rtb.routes || rtb.route_rules || rtb.Routes || [];
                  if (routes && routes.length > 0) {
                    return routes;
                  }
                }
              }
            }
          }
          
          // Structure 2: { vpcs: [ { route_tables: [...] } ] }
          if (Array.isArray(discoveryData.vpcs)) {
            for (const vpc of discoveryData.vpcs) {
              if (!vpc || typeof vpc !== 'object') continue;
              
              const routeTables = vpc.route_tables || (vpc.info && vpc.info.route_tables) || [];
              
              for (const rtb of routeTables) {
                if (!rtb || typeof rtb !== 'object') continue;
                
                const rtbIdMatch = 
                  rtb.id === normalizedRtbId ||
                  rtb.route_table_id === normalizedRtbId ||
                  rtb.RouteTableId === normalizedRtbId ||
                  String(rtb.id || '').includes(normalizedRtbId) ||
                  String(rtb.route_table_id || '').includes(normalizedRtbId);
                
                if (rtbIdMatch) {
                  const routes = rtb.routes || rtb.route_rules || rtb.Routes || [];
                  if (routes && routes.length > 0) {
                    return routes;
                  }
                }
              }
            }
          }
          
          // Structure 3: OCI format { vcns: [ { route_tables: [...] } ] }
          if (Array.isArray(discoveryData.vcns)) {
            for (const vcn of discoveryData.vcns) {
              if (!vcn || typeof vcn !== 'object') continue;
              
              const routeTables = vcn.route_tables || [];
              
              for (const rtb of routeTables) {
                if (!rtb || typeof rtb !== 'object') continue;
                
                const rtbIdMatch = 
                  rtb.id === normalizedRtbId ||
                  rtb.route_table_id === normalizedRtbId ||
                  String(rtb.id || '').includes(normalizedRtbId) ||
                  String(rtb.route_table_id || '').includes(normalizedRtbId);
                
                if (rtbIdMatch) {
                  const routes = rtb.route_rules || rtb.routes || [];
                  if (routes && routes.length > 0) {
                    return routes;
                  }
                }
              }
            }
          }
          
          // Structure 4: Flat structure with route_tables at root
          if (Array.isArray(discoveryData.route_tables)) {
            for (const rtb of discoveryData.route_tables) {
              if (!rtb || typeof rtb !== 'object') continue;
              
              const rtbIdMatch = 
                rtb.id === normalizedRtbId ||
                rtb.route_table_id === normalizedRtbId ||
                rtb.RouteTableId === normalizedRtbId ||
                String(rtb.id || '').includes(normalizedRtbId) ||
                String(rtb.route_table_id || '').includes(normalizedRtbId);
              
              if (rtbIdMatch) {
                const routes = rtb.routes || rtb.route_rules || rtb.Routes || [];
                if (routes && routes.length > 0) {
                  return routes;
                }
              }
            }
          }
          
          // Last resort: search recursively through the entire object
          const searchRecursive = (obj: any, depth = 0): any[] | null => {
            if (depth > 5) return null; // Prevent infinite recursion
            if (!obj || typeof obj !== 'object') return null;
            
            // If this object looks like a route table
            if (obj.routes || obj.route_rules || obj.Routes) {
              const rtbIdMatch = 
                obj.id === normalizedRtbId ||
                obj.route_table_id === normalizedRtbId ||
                obj.RouteTableId === normalizedRtbId ||
                String(obj.id || '').includes(normalizedRtbId) ||
                String(obj.route_table_id || '').includes(normalizedRtbId);
              
              if (rtbIdMatch) {
                const routes = obj.routes || obj.route_rules || obj.Routes || [];
                if (routes && routes.length > 0) {
                  return routes;
                }
              }
            }
            
            // Recursively search in arrays and objects
            for (const key in obj) {
              if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const result = searchRecursive(obj[key], depth + 1);
                if (result) return result;
              }
            }
            
            return null;
          };
          
          const recursiveResult = searchRecursive(discoveryData);
          if (recursiveResult) return recursiveResult;
          
          console.warn('findRoutingRules: Could not find routing rules for RTB:', normalizedRtbId);
          return null;
        };


        const eniData = isEni ? parseEniLabel(selectedNode.data?.label || "") : null;
        const routingRules = isRtb ? findRoutingRules(selectedNode.id) : null;

        return (
          <GenericModal
            isOpen={!!selectedNode}
            onClose={() => {
              setSelectedNode(null);
              setSelectedSecurityGroup(null);
            }}
            title={`${isRtb ? 'Route Table' : isSg ? 'Security Group' : isGateway ? getGatewayTypeName() : 'Network Interface'} Details`}
            size="lg"
          >
            <div className="space-y-4">
              {/* Basic Info */}
              <div>
                <h3 className="font-semibold text-semantic-text-primary mb-2">Basic Information</h3>
                <div className="space-y-2 text-sm">
                  {selectedNode.id && (
                    <div className="flex justify-between">
                      <span className="text-semantic-text-secondary">ID:</span>
                      <span className="text-semantic-text-primary font-medium font-mono break-all text-right max-w-[70%]">
                        {selectedNode.id}
                      </span>
                    </div>
                  )}
                  {selectedNode.data?.kind && (
                    <div className="flex justify-between">
                      <span className="text-semantic-text-secondary">Type:</span>
                      <span className="text-semantic-text-primary font-medium">
                        {selectedNode.data.kind.toUpperCase()}
                      </span>
                    </div>
                  )}
                  
                  {/* For ENI: Show parsed fields individually */}
                  {isEni && eniData && (
                    <>
                      {eniData.title && (
                        <div className="flex justify-between">
                          <span className="text-semantic-text-secondary">Name:</span>
                          <span className="text-semantic-text-primary font-medium font-mono break-all text-right max-w-[70%]">
                            {eniData.title}
                          </span>
                        </div>
                      )}
                      {/* Instance association - show ID and Name separately if available */}
                      {(selectedNode.data?.instance_id || selectedNode.data?.attachment?.instance_id) && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-semantic-text-secondary">Instance ID:</span>
                            <span className="text-semantic-text-primary font-medium font-mono break-all text-right max-w-[70%]">
                              {selectedNode.data.instance_id || selectedNode.data.attachment?.instance_id}
                            </span>
                          </div>
                          {selectedNode.data?.instance_name && (
                            <div className="flex justify-between">
                              <span className="text-semantic-text-secondary">Instance Name:</span>
                              <span className="text-semantic-text-primary font-medium font-mono break-all text-right max-w-[70%]">
                                {selectedNode.data.instance_name}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                      {Object.entries(eniData.fields).map(([key, value]) => {
                        // Skip 'inst' - we show Instance ID/Name separately from node.data
                        if (key === 'inst') return null;
                        if (key === 'sg' || key === 'nsg') {
                          const isNsg = key === 'nsg';
                          // SG/NSG - make them clickable
                          // Normalize: split by whitespace, trim each, filter empty, remove any non-SG-ID characters
                          const sgIds = String(value)
                            .split(/\s+/)
                            .map(id => id.trim())
                            .filter(Boolean)
                            .map(id => {
                              // Remove any leading/trailing non-alphanumeric except dash
                              return id.replace(/^[^a-z0-9-]+|[^a-z0-9-]+$/gi, '');
                            })
                            .filter(id => id.length > 0)
                            // IMPORTANT: avoid rendering stray tokens like "+1" or "1"
                            // Only render proper IDs for the current cloud/provider key.
                            .filter((id) => {
                              if (isNsg) return /^nsg-[a-z0-9]+$/i.test(id);
                              return /^sg-[a-z0-9]+$/i.test(id);
                            });
                          
                          return (
                            <div key={key} className="flex flex-col gap-1">
                              <span className="text-semantic-text-secondary font-medium">
                                {isNsg ? 'Network Security Groups:' : 'Security Groups:'}
                              </span>
                              <div className="flex flex-wrap gap-2">
                                {sgIds.map((sgId) => {
                                  // Normalize ID when clicking
                                  const normalizedId = sgId.trim();
                                  const groups = (selectedNode?.data?.security_groups || []) as any[];
                                  const hit = isNsg
                                    ? (Array.isArray(groups)
                                        ? groups.find((g: any) => String(g?.group_id || '') === normalizedId)
                                        : null)
                                    : null;
                                  const displayId = isNsg ? (hit?.group_name || normalizedId) : normalizedId;
                                  return (
                                    <button
                                      key={sgId}
                                      onClick={() => {
                                        console.log(isNsg ? '🔵 CLICKED NSG:' : '🔵 CLICKED SECURITY GROUP:', {
                                          raw: sgId,
                                          normalized: normalizedId,
                                          length: normalizedId.length,
                                          first10: normalizedId.substring(0, 10),
                                          last10: normalizedId.substring(Math.max(0, normalizedId.length - 10))
                                        });
                                        if (isNsg) {
                                          // Resolve from VNIC payload first; fallback to discovery lookup by OCID.
                                          const fromVnic = hit?.nsg_data || hit || null;
                                          if (fromVnic?.nsg_data || fromVnic?.security_rules) {
                                            setSelectedNetworkSecurityGroup(fromVnic);
                                            return;
                                          }

                                          const ocid = fromVnic?.nsg_ocid || hit?.nsg_ocid || null;
                                          if (ocid && discoveryData?.vcns) {
                                            try {
                                              for (const vcn of Object.values(discoveryData.vcns || {})) {
                                                const arr = (vcn as any)?.network_security_groups || [];
                                                const found = Array.isArray(arr)
                                                  ? arr.find((n: any) => String(n?.id || n?.ocid || '') === String(ocid))
                                                  : null;
                                                if (found) {
                                                  setSelectedNetworkSecurityGroup(found);
                                                  return;
                                                }
                                              }
                                            } catch (_) {
                                              // best-effort
                                            }
                                          }
                                          setSelectedNetworkSecurityGroup(fromVnic);
                                        } else {
                                          setSelectedSecurityGroup(normalizedId);
                                        }
                                      }}
                                      className="text-blue-600 hover:text-blue-800 hover:underline font-mono text-xs bg-blue-50 px-2 py-1 rounded border border-blue-200 break-all dark:bg-semantic-bg-tertiary dark:border-semantic-border-primary dark:text-semantic-text-brand dark:hover:text-semantic-text-brand dark:hover:bg-semantic-hover-brand"
                                      title={
                                        isNsg
                                          ? `Click to view network security group ${displayId} details`
                                          : `Click to view security group ${sgId} details`
                                      }
                                    >
                                      {displayId}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={key} className="flex justify-between">
                            <span className="text-semantic-text-secondary capitalize">{key}:</span>
                            <span className="text-semantic-text-primary font-medium font-mono break-all text-right max-w-[70%]">
                              {value}
                            </span>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* For RTB: Show label as name */}
                  {isRtb && selectedNode.data?.label && (
                    <div className="flex justify-between">
                      <span className="text-semantic-text-secondary">Name:</span>
                      <span className="text-semantic-text-primary font-medium break-all text-right max-w-[70%]">
                        {selectedNode.data.label}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Routing Rules for RTB */}
              {isRtb && (
                <div>
                  <h3 className="font-semibold text-semantic-text-primary mb-2">Routing Rules (Reglas de Enrutamiento)</h3>
                  {routingRules && routingRules.length > 0 ? (
                    <div className="space-y-3 text-sm max-h-96 overflow-y-auto">
                      {routingRules.map((rule: any, idx: number) => {
                        // Determine the gateway/target value (AWS format)
                        let gatewayTarget: string | null = null;
                        if (rule.GatewayId) {
                          gatewayTarget = rule.GatewayId;
                        } else if (rule.NatGatewayId) {
                          gatewayTarget = rule.NatGatewayId;
                        } else if (rule.TransitGatewayId) {
                          gatewayTarget = rule.TransitGatewayId;
                        } else if (rule.VpcPeeringConnectionId) {
                          gatewayTarget = rule.VpcPeeringConnectionId;
                        } else if (rule.InstanceId) {
                          gatewayTarget = rule.InstanceId;
                        } else if (rule.NetworkInterfaceId) {
                          gatewayTarget = rule.NetworkInterfaceId;
                        }
                        
                        // OCI format
                        if (!gatewayTarget && rule.networkEntityId) {
                          gatewayTarget = rule.networkEntityId;
                        }
                        
                        // Destination (can be DestinationCidrBlock for AWS or destination for OCI)
                        const destination = rule.DestinationCidrBlock || rule.DestinationIpv6CidrBlock || rule.destination || null;
                        
                        return (
                          <div key={idx} className="border border-blue-200 rounded-lg p-4 bg-blue-50/50 shadow-sm dark:border-semantic-border-primary dark:bg-semantic-bg-tertiary">
                            <div className="font-mono text-xs space-y-2">
                              {destination && (
                                <div className="flex justify-between items-start">
                                  <span className="text-semantic-text-secondary font-semibold min-w-[140px]">Destination CIDR:</span>
                                  <span className="text-semantic-text-primary font-bold text-sm break-all text-right flex-1">
                                    {destination}
                                  </span>
                                </div>
                              )}
                              {gatewayTarget && (
                                <div className="flex justify-between items-start">
                                  <span className="text-semantic-text-secondary font-semibold min-w-[140px]">Gateway/Target:</span>
                                  <span className="text-semantic-text-primary font-medium font-mono break-all text-right flex-1">
                                    {gatewayTarget}
                                  </span>
                                </div>
                              )}
                              {rule.State && (
                                <div className="flex justify-between items-start">
                                  <span className="text-semantic-text-secondary font-semibold min-w-[140px]">State:</span>
                                  <span className={`font-medium ${rule.State === 'active' ? 'text-green-600 dark:text-semantic-text-success' : 'text-orange-600 dark:text-semantic-text-warning'}`}>
                                    {rule.State}
                                  </span>
                                </div>
                              )}
                              {rule.Origin && (
                                <div className="flex justify-between items-start">
                                  <span className="text-semantic-text-secondary font-semibold min-w-[140px]">Origin:</span>
                                  <span className="text-semantic-text-primary font-medium break-all text-right flex-1">
                                    {rule.Origin}
                                  </span>
                                </div>
                              )}
                              
                              {/* Show all other fields that we haven't explicitly handled */}
                              {Object.keys(rule).filter(k => 
                                !['DestinationCidrBlock', 'DestinationIpv6CidrBlock', 'destination', 'GatewayId', 'NatGatewayId', 
                                  'TransitGatewayId', 'VpcPeeringConnectionId', 'InstanceId', 'NetworkInterfaceId', 'State', 
                                  'Origin', 'destinationType', 'networkEntityId', 'description'].includes(k)
                              ).length > 0 && (
                                <details className="mt-2 border-t border-slate-300 pt-2 dark:border-semantic-border-primary">
                                  <summary className="text-semantic-text-secondary cursor-pointer text-xs font-semibold hover:text-semantic-text-primary">
                                    Ver todos los campos adicionales
                                  </summary>
                                  <pre className="mt-2 text-xs bg-white p-3 rounded border overflow-auto max-h-48 dark:bg-semantic-bg-tertiary dark:border-semantic-border-primary dark:text-semantic-text-primary">
                                    {JSON.stringify(rule, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                <div className="text-sm text-semantic-text-secondary italic p-4 bg-yellow-50 border border-yellow-200 rounded dark:bg-semantic-bg-warning-secondary/20 dark:border-semantic-border-warning-primary">
                  <strong>No se encontraron reglas de enrutamiento.</strong>
                  <br />
                  {discoveryData 
                    ? 'Las reglas pueden no estar disponibles en los datos de discovery, o la estructura de datos es diferente.'
                    : 'Los datos de discovery no se han cargado. Asegúrate de que el archivo discovery.json esté disponible.'}
                  <br />
                  <span className="text-xs mt-2 block font-mono break-all">Route Table ID: {selectedNode.id}</span>
                </div>
                  )}
                </div>
              )}

              {/* Gateway Details - Route Tables, ENIs, Attachments */}
              {isGateway && (() => {
                const gatewayId = selectedNode.id || selectedNode.data?.id;
                const routeTableRefs = findRouteTablesForGateway(gatewayId);
                const natEnis = isNat ? findEnisForNat(gatewayId) : [];
                const tgwAttachments = isTgw ? findTgwAttachments(gatewayId) : [];

                return (
                  <div className="space-y-4">
                    {/* For NAT Gateway - Show ENIs */}
                    {isNat && (
                      <div>
                        <h3 className="font-semibold text-semantic-text-primary mb-2 flex items-center gap-2">
                          <Network className="w-4 h-4" />
                          Network Interfaces (ENIs)
                        </h3>
                        {natEnis.length > 0 ? (
                          <div className="space-y-2">
                            {natEnis.map((eni: any, idx: number) => (
                              <div key={idx} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 text-sm dark:border-semantic-border-primary dark:bg-semantic-bg-tertiary">
                                <div className="font-mono text-xs space-y-1">
                                  {eni.id && (
                                    <div className="flex justify-between">
                                      <span className="text-semantic-text-secondary">ENI ID:</span>
                                      <span className="text-semantic-text-primary font-medium">{eni.id}</span>
                                    </div>
                                  )}
                                  {eni.private_ip && (
                                    <div className="flex justify-between">
                                      <span className="text-semantic-text-secondary">Private IP:</span>
                                      <span className="text-semantic-text-primary font-medium">{eni.private_ip}</span>
                                    </div>
                                  )}
                                  {eni.public_ip && (
                                    <div className="flex justify-between">
                                      <span className="text-semantic-text-secondary">Public IP (Elastic):</span>
                                      <span className="text-semantic-text-primary font-medium">{eni.public_ip}</span>
                                    </div>
                                  )}
                                  {eni.subnet_id && (
                                    <div className="flex justify-between">
                                      <span className="text-semantic-text-secondary">Subnet:</span>
                                      <span className="text-semantic-text-primary font-medium">{eni.subnet_id}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-semantic-text-secondary italic p-3 bg-slate-50 border border-slate-200 rounded dark:bg-semantic-bg-tertiary dark:border-semantic-border-primary">
                            No se encontraron interfaces de red asociadas.
                          </div>
                        )}
                      </div>
                    )}

                    {/* For TGW - Show Visualization Button and Attachments */}
                    {isTgw && (
                      <div className="space-y-4">
                        {/* TGW Visualization Button */}
                        {discoveryData?.transit_gateways?.length > 0 && (() => {
                          // Extract TGW ID from the node - try multiple sources
                          const nodeId = selectedNode.data?.id || selectedNode.id || '';
                          const nodeLabel = selectedNode.data?.label || '';
                          
                          // Try to find tgw-xxxx pattern in the ID or label
                          const tgwMatch = String(nodeId + ' ' + nodeLabel).match(/tgw-[a-z0-9]+/i);
                          const extractedTgwId = tgwMatch ? tgwMatch[0] : nodeId;
                          
                          // Check if this TGW exists in discovery data
                          const tgwExists = discoveryData.transit_gateways.some(
                            (tgw: any) => tgw.id === extractedTgwId
                          );
                          
                          if (!tgwExists) return null;
                          
                          return (
                            <div className="flex justify-end">
                              <TGWVisualizationButton
                                onClick={() => {
                                  setSelectedTgwForVisualization(extractedTgwId);
                                  setSelectedNode(null); // Close this modal
                                }}
                              />
                            </div>
                          );
                        })()}
                        
                        <div>
                          <h3 className="font-semibold text-semantic-text-primary mb-2 flex items-center gap-2">
                            <GitBranch className="w-4 h-4" />
                            Transit Gateway Attachments
                          </h3>
                        {tgwAttachments.length > 0 ? (
                          <div className="space-y-2">
                            {tgwAttachments.map((att: any, idx: number) => (
                              <div key={idx} className="border border-purple-200 rounded-lg p-3 bg-purple-50/50 text-sm dark:border-semantic-border-primary dark:bg-semantic-bg-tertiary">
                                <div className="font-mono text-xs space-y-1">
                                  {att.id && (
                                    <div className="flex justify-between">
                                      <span className="text-semantic-text-secondary">Attachment ID:</span>
                                      <span className="text-semantic-text-primary font-medium">{att.id}</span>
                                    </div>
                                  )}
                                  {att.type && (
                                    <div className="flex justify-between">
                                      <span className="text-semantic-text-secondary">Type:</span>
                                      <span className="text-semantic-text-primary font-medium uppercase">{att.type}</span>
                                    </div>
                                  )}
                                  {att.resourceId && (
                                    <div className="flex justify-between">
                                      <span className="text-semantic-text-secondary">Resource ID:</span>
                                      <span className="text-semantic-text-primary font-medium">{att.resourceId}</span>
                                    </div>
                                  )}
                                  {att.state && (
                                    <div className="flex justify-between">
                                      <span className="text-semantic-text-secondary">State:</span>
<span className={`font-medium ${att.state === 'available' ? 'text-green-600 dark:text-semantic-text-success' : 'text-orange-600 dark:text-semantic-text-warning'}`}>
                                    {att.state}
                                  </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-semantic-text-secondary italic p-3 bg-slate-50 border border-slate-200 rounded dark:bg-semantic-bg-tertiary dark:border-semantic-border-primary">
                            No se encontraron attachments de TGW.
                          </div>
                        )}
                        </div>
                      </div>
                    )}

                    {/* Route Tables referencing this gateway */}
                    <div>
                      <h3 className="font-semibold text-semantic-text-primary mb-2 flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        Route Tables Using This Gateway
                      </h3>
                      {routeTableRefs.length > 0 ? (
                        <div className="space-y-3">
                          {routeTableRefs.map((ref, idx) => (
                            <div key={idx} className="border border-blue-200 rounded-lg p-3 bg-blue-50/50 dark:border-semantic-border-primary dark:bg-semantic-bg-tertiary">
                              <div className="font-semibold text-sm text-semantic-text-primary mb-1">
                                {ref.rtb.name || <span className="text-semantic-text-secondary italic">(Sin nombre)</span>}
                              </div>
                              <div className="font-mono text-xs space-y-1 mb-3">
                                <div className="flex justify-between">
                                  <span className="text-semantic-text-secondary">RTB ID:</span>
                                  <span className="text-semantic-text-primary">{ref.rtb.id}</span>
                                </div>
                                {ref.rtb.vpcId && (
                                  <div className="flex justify-between">
                                    <span className="text-semantic-text-secondary">VPC/VCN:</span>
                                    <span className="text-semantic-text-primary">{ref.rtb.vpcId}</span>
                                  </div>
                                )}
                              </div>

                              {/* Routes pointing to this gateway */}
                              <div className="mb-3">
                                <div className="text-xs font-semibold text-semantic-text-secondary mb-1">
                                  Routes to this gateway:
                                </div>
                                <div className="space-y-1">
                                  {ref.routes.map((route: any, rIdx: number) => {
                                    const dest = route.DestinationCidrBlock || route.destination || route.DestinationIpv6CidrBlock || 'N/A';
                                    return (
                                      <div key={rIdx} className="bg-white border border-blue-100 rounded px-2 py-1 font-mono text-xs dark:bg-semantic-bg-secondary dark:border-semantic-border-primary dark:text-semantic-text-primary">
                                        <span className="text-blue-700 font-semibold dark:text-semantic-text-brand">{dest}</span>
                                        {route.State && (
                                          <span className={`ml-2 ${route.State === 'active' ? 'text-green-600 dark:text-semantic-text-success' : 'text-orange-600 dark:text-semantic-text-warning'}`}>
                                            ({route.State})
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Associated Subnets */}
                              {ref.subnetAssociations.length > 0 && (
                                <div>
                                  <div className="text-xs font-semibold text-semantic-text-secondary mb-1">
                                    Subnets using this route table:
                                  </div>
                                  <div className="space-y-2">
                                    {ref.subnetAssociations.map((assoc: any, sIdx: number) => (
                                      <div 
                                        key={sIdx}
                                        className="bg-green-100 border border-green-200 rounded px-2 py-1.5 text-xs text-green-800"
                                      >
                                        <div className="font-semibold">
                                          {assoc.subnetName || <span className="italic text-green-600">(Sin nombre)</span>}
                                          {assoc.main && <span className="ml-1 text-green-600 font-normal">(main)</span>}
                                        </div>
                                        <div className="font-mono text-green-700 text-[10px] mt-0.5">{assoc.subnetId}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-semantic-text-secondary italic p-3 bg-yellow-50 border border-yellow-200 rounded dark:bg-semantic-bg-warning-secondary/20 dark:border-semantic-border-warning-primary">
                          <strong>Este gateway no esta siendo utilizado por ninguna tabla de rutas.</strong>
                          <br />
                          <span className="text-xs">Esto puede indicar un gateway huerfano o no configurado.</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* All Data Fields (for other types or additional info) */}
              {selectedNode.data && Object.keys(selectedNode.data).length > 0 && !isEni && !isGateway && (
                <div>
                  <h3 className="font-semibold text-semantic-text-primary mb-2">Additional Data</h3>
                  <div className="space-y-2 text-sm max-h-96 overflow-y-auto">
                    {Object.entries(selectedNode.data)
                      .filter(([key]) => key !== 'label' && key !== 'kind' && key !== 'type')
                      .map(([key, value]) => (
                        <div key={key} className="flex flex-col gap-1 border-b border-slate-200 pb-2 dark:border-semantic-border-primary">
                          <span className="text-semantic-text-secondary font-medium">{key}:</span>
                          <div className="text-semantic-text-primary font-mono text-xs bg-slate-50 p-2 rounded border break-all dark:bg-semantic-bg-tertiary dark:border-semantic-border-primary">
                            {typeof value === 'object' && value !== null ? (
                              <pre className="whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</pre>
                            ) : (
                              String(value)
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </GenericModal>
        );
      })()}

      {/* Security Group Details Modal */}
      {selectedSecurityGroup && (() => {
        const findSecurityGroup = (sgId: string) => {
          // CRITICAL: Normalize the ID - remove all whitespace, trim, and ensure it's a string
          const normalizedSgId = String(sgId || '').trim().replace(/\s+/g, '');
          
          if (!normalizedSgId) {
            console.error('findSecurityGroup: Empty or invalid security group ID provided');
            return null;
          }
          
          console.log('🔍 findSecurityGroup: Starting search for:', {
            original: sgId,
            normalized: normalizedSgId,
            length: normalizedSgId.length,
            first10: normalizedSgId.substring(0, 10),
            last10: normalizedSgId.substring(Math.max(0, normalizedSgId.length - 10)),
            startsWithSg: normalizedSgId.startsWith('sg-')
          });
          console.log('findSecurityGroup: selectedNode:', selectedNode ? {
            id: selectedNode.id,
            type: selectedNode.type,
            kind: selectedNode.data?.kind,
            provider: selectedNode.data?.provider,
            hasGroups: !!(selectedNode.data?.groups || selectedNode.data?.security_groups),
            groupsCount: (selectedNode.data?.groups || selectedNode.data?.security_groups || []).length,
            hasNsgOcid: !!selectedNode.data?.nsg_ocid,
            hasNsgData: !!selectedNode.data?.nsg_data
          } : 'null');
          
          // Detect provider from topology (nodes) - this tells us what the topology is, not the discovery data
          const topologyProvider = selectedNode?.data?.provider || 
            nodes.find(n => n.data?.provider)?.data?.provider || 
            null;
          const isTopologyOCI = topologyProvider === 'oci';
          const isTopologyAWS = topologyProvider === 'aws' || !isTopologyOCI;
          
          console.log(`findSecurityGroup: Topology provider - AWS: ${isTopologyAWS}, OCI: ${isTopologyOCI}`);
          
          // First, check if the selected node IS a security group node (clicked directly on SG node)
          if (selectedNode && selectedNode.data && (selectedNode.data.kind === 'sg' || selectedNode.data.type === 'sg')) {
            console.log('findSecurityGroup: Selected node is an SG node');
            // If we have nsg_data directly in the node, return it
            if (selectedNode.data.nsg_data) {
              console.log('findSecurityGroup: Found nsg_data in SG node');
              return selectedNode.data.nsg_data;
            }
            // If we have nsg_ocid in the node, use it for searching
            if (selectedNode.data.nsg_ocid) {
              console.log('findSecurityGroup: Found nsg_ocid in SG node:', selectedNode.data.nsg_ocid);
              // We'll use this below to search in discoveryData
            }
          }
          
          if (!discoveryData) {
            console.warn('findSecurityGroup: No discovery data available');
            // Fallback: check if security group data is in the selected node
            if (selectedNode && selectedNode.data) {
              // If it's an SG node, return its data
              if (selectedNode.data.nsg_data) {
                return selectedNode.data.nsg_data;
              }
              // If it's an ENI node, check groups
              const eniGroups = selectedNode.data.groups || selectedNode.data.security_groups || [];
              const matchingGroup = eniGroups.find((g: any) => {
                const gId = g.group_id || g.GroupId || g.id || g;
                return String(gId) === normalizedSgId || String(gId).includes(normalizedSgId);
              });
              if (matchingGroup && typeof matchingGroup === 'object') {
                return matchingGroup.nsg_data || matchingGroup;
              }
            }
            return null;
          }
          
          // Get nsg_ocid from selected node (either SG node or ENI node)
          let nsgOcidToSearch: string | null = null;
          if (selectedNode && selectedNode.data) {
            // If it's an SG node, get nsg_ocid directly
            if (selectedNode.data.nsg_ocid) {
              nsgOcidToSearch = String(selectedNode.data.nsg_ocid);
              console.log('findSecurityGroup: Using nsg_ocid from SG node:', nsgOcidToSearch);
            } else {
              // If it's an ENI node, get nsg_ocid from groups
              const eniGroups = selectedNode.data.groups || selectedNode.data.security_groups || [];
              const matchingGroup = eniGroups.find((g: any) => {
                const gId = g.group_id || g.GroupId || g.id || g;
                return String(gId) === normalizedSgId || String(gId).includes(normalizedSgId);
              });
              if (matchingGroup) {
                console.log('findSecurityGroup: Found matching group in ENI data:', {
                  group_id: matchingGroup.group_id,
                  nsg_ocid: matchingGroup.nsg_ocid,
                  has_nsg_data: !!matchingGroup.nsg_data
                });
                // If the group has full NSG data, return it immediately
                if (matchingGroup.nsg_data) {
                  console.log('findSecurityGroup: Returning nsg_data from ENI');
                  return matchingGroup.nsg_data;
                }
                // Store the OCID for searching in discoveryData
                if (matchingGroup.nsg_ocid) {
                  nsgOcidToSearch = String(matchingGroup.nsg_ocid);
                  console.log('findSecurityGroup: Will search by OCID:', nsgOcidToSearch);
                }
              }
            }
          }
          
          // Helper function to check if an SG object matches the ID
          const matchesSgId = (sg: any): boolean => {
            if (!sg || typeof sg !== 'object') return false;
            
            // Try ALL possible ID fields
            const possibleIds = [
              sg.id,
              sg.group_id,
              sg.GroupId,
              sg.groupId,
              sg.security_group_id,
              sg.SecurityGroupId,
              sg.oci_id,
              sg.ocid
            ].filter(Boolean);
            
            if (possibleIds.length === 0) return false;
            
            const searchIdStr = normalizedSgId.trim().toLowerCase();
            
            // Check each possible ID
            for (const sgIdToCheck of possibleIds) {
              const sgIdStr = String(sgIdToCheck).trim().toLowerCase();
              
              // Exact match (most reliable)
              if (sgIdStr === searchIdStr) {
                console.log(`findSecurityGroup: EXACT MATCH found: "${sgIdStr}" === "${searchIdStr}"`);
                return true;
              }
              
              // For AWS: if both are sg-xxx format, compare suffixes
              if (searchIdStr.startsWith('sg-') && sgIdStr.startsWith('sg-')) {
                const suffix1 = searchIdStr.replace(/^sg-/, '').trim();
                const suffix2 = sgIdStr.replace(/^sg-/, '').trim();
                
                // Exact suffix match
                if (suffix1 === suffix2) {
                  console.log(`findSecurityGroup: SUFFIX MATCH found: "${suffix1}" === "${suffix2}"`);
                  return true;
                }
                
                // Partial match: check if one contains the other
                if (suffix1 && suffix2) {
                  if (suffix1.includes(suffix2) || suffix2.includes(suffix1)) {
                    // Only allow if the shorter one is at least 8 characters
                    const shorter = suffix1.length < suffix2.length ? suffix1 : suffix2;
                    if (shorter.length >= 8) {
                      console.log(`findSecurityGroup: PARTIAL MATCH found: "${suffix1}" contains "${suffix2}" or vice versa`);
                      return true;
                    }
                  }
                }
              }
              
              // Contains match (more lenient for debugging)
              if (sgIdStr.includes(searchIdStr) || searchIdStr.includes(sgIdStr)) {
                // Only if both are reasonable length to avoid false positives
                if (sgIdStr.length >= 10 && searchIdStr.length >= 10) {
                  console.log(`findSecurityGroup: CONTAINS MATCH found: "${sgIdStr}" contains "${searchIdStr}" or vice versa`);
                  return true;
                }
              }
            }
            
            // For OCI: if we have nsg_ocid, also match by original OCID
            if (nsgOcidToSearch && !searchIdStr.startsWith('sg-')) {
              const ocidStr = String(nsgOcidToSearch).trim().toLowerCase();
              for (const sgIdToCheck of possibleIds) {
                const sgIdStr = String(sgIdToCheck).trim().toLowerCase();
                if (sgIdStr === ocidStr || sgIdStr.includes(ocidStr) || ocidStr.includes(sgIdStr)) {
                  console.log(`findSecurityGroup: OCID MATCH found: "${sgIdStr}" matches "${ocidStr}"`);
                  return true;
                }
              }
            }
            
            return false;
          };
          
          // Structure 1: AWS format - { vpcs: { "vpc-id": { security_groups: [...] } } }
          // Search in AWS format regardless of topology provider (discovery data might be AWS even if topology is OCI)
          if (discoveryData.vpcs && typeof discoveryData.vpcs === 'object' && !Array.isArray(discoveryData.vpcs)) {
            console.log(`findSecurityGroup: Searching in AWS format (vpcs structure)`);
            const vpcKeys = Object.keys(discoveryData.vpcs);
            console.log(`findSecurityGroup: Found ${vpcKeys.length} VPCs to search`);
            
            for (const vpcId of vpcKeys) {
              const vpc = discoveryData.vpcs[vpcId];
              if (!vpc || typeof vpc !== 'object') {
                console.log(`findSecurityGroup: VPC ${vpcId} is not an object, skipping`);
                continue;
              }
              
              // Check AWS format: vpc.security_groups
              const sgs = vpc.security_groups;
              if (!Array.isArray(sgs)) {
                console.log(`findSecurityGroup: VPC ${vpcId} security_groups is not an array (type: ${typeof sgs})`);
                continue;
              }
              
              if (sgs.length === 0) {
                console.log(`findSecurityGroup: VPC ${vpcId} has 0 security groups`);
                continue;
              }
              
              console.log(`findSecurityGroup: VPC ${vpcId} has ${sgs.length} security groups (AWS format)`);
              
              for (let i = 0; i < sgs.length; i++) {
                const sg = sgs[i];
                if (!sg || typeof sg !== 'object' || Array.isArray(sg)) {
                  console.log(`findSecurityGroup: SG[${i}] in VPC ${vpcId} is invalid (type: ${typeof sg})`);
                  continue;
                }
                
                // Get ALL possible ID fields and normalize them
                const rawIds = [
                  sg.id,
                  sg.group_id,
                  sg.GroupId,
                  sg.groupId,
                  sg.security_group_id,
                  sg.SecurityGroupId
                ].filter(Boolean);
                
                const normalizedIds = rawIds.map(id => String(id).trim().replace(/\s+/g, ''));
                
                console.log(`findSecurityGroup: Checking SG[${i}] in VPC ${vpcId}:`, {
                  rawIds: rawIds,
                  normalizedIds: normalizedIds,
                  searchingFor: normalizedSgId
                });
                
                // Check if any normalized ID matches
                const hasExactMatch = normalizedIds.some(id => id === normalizedSgId);
                if (hasExactMatch) {
                  console.log(`findSecurityGroup: ✅✅✅ EXACT MATCH FOUND in AWS format! VPC: ${vpcId}, SG IDs: ${normalizedIds.join(', ')}`);
                  return sg;
                }
                
                // Also try the matchesSgId function
                if (matchesSgId(sg)) {
                  console.log(`findSecurityGroup: ✅✅✅ MATCH FOUND via matchesSgId in AWS format! VPC: ${vpcId}`);
                  return sg;
                } else {
                  console.log(`findSecurityGroup: No match for SG[${i}] in VPC ${vpcId}`);
                }
              }
            }
          }
          
          // Structure 1b: OCI format - { vcns: { "vcn-id": { network_security_groups: [...] } } }
          // Search in OCI format regardless of topology provider (discovery data might be OCI even if topology is AWS)
          if (discoveryData.vcns && typeof discoveryData.vcns === 'object' && !Array.isArray(discoveryData.vcns)) {
            console.log(`findSecurityGroup: Searching in OCI format (vcns structure)`);
            for (const vcnId in discoveryData.vcns) {
              const vcn = discoveryData.vcns[vcnId];
              if (!vcn || typeof vcn !== 'object') continue;
              
              // Check OCI format: vcn.network_security_groups
              const nsgs = vcn.network_security_groups;
              if (Array.isArray(nsgs) && nsgs.length > 0) {
                console.log(`findSecurityGroup: VCN ${vcnId} has ${nsgs.length} network security groups (OCI format)`);
                for (const nsg of nsgs) {
                  if (!nsg || typeof nsg !== 'object' || Array.isArray(nsg)) continue;
                  const nsgId = nsg.id || nsg.oci_id || '';
                  if (matchesSgId(nsg)) {
                    console.log(`findSecurityGroup: MATCH FOUND in OCI format! ${nsgId}`);
                    return nsg;
                  }
                }
              }
            }
          }
          
          // Structure 2: Array format for vpcs (less common)
          if (Array.isArray(discoveryData.vpcs)) {
            console.log(`findSecurityGroup: Searching in AWS array format (vpcs array)`);
            for (const vpc of discoveryData.vpcs) {
              if (!vpc || typeof vpc !== 'object') continue;
              const sgs = vpc.security_groups || [];
              if (Array.isArray(sgs)) {
                for (const sg of sgs) {
                  if (!sg || typeof sg !== 'object') continue;
                  if (matchesSgId(sg)) {
                    console.log('findSecurityGroup: MATCH FOUND in AWS array format!');
                    return sg;
                  }
                }
              }
            }
          }
          
          // Structure 3: OCI format with array of vcns
          if (Array.isArray(discoveryData.vcns)) {
            console.log(`findSecurityGroup: Searching in OCI array format (vcns array)`);
            for (const vcn of discoveryData.vcns) {
              if (!vcn || typeof vcn !== 'object') continue;
              const nsgs = vcn.network_security_groups || vcn.security_groups || [];
              if (Array.isArray(nsgs)) {
                for (const nsg of nsgs) {
                  if (!nsg || typeof nsg !== 'object') continue;
                  // Match by OCID if available
                  if (nsgOcidToSearch) {
                    const sgOcid = nsg.id || nsg.oci_id;
                    if (sgOcid && (String(sgOcid) === nsgOcidToSearch || String(sgOcid).includes(nsgOcidToSearch) || nsgOcidToSearch.includes(String(sgOcid)))) {
                      console.log('findSecurityGroup: MATCH FOUND in OCI array format by OCID!');
                      return nsg;
                    }
                  }
                  if (matchesSgId(nsg)) {
                    console.log('findSecurityGroup: MATCH FOUND in OCI array format!');
                    return nsg;
                  }
                }
              }
            }
          }
          
          // Structure 4: Top-level network_security_groups array (OCI)
          if (Array.isArray(discoveryData.network_security_groups)) {
            console.log(`findSecurityGroup: Searching in top-level network_security_groups array`);
            for (const sg of discoveryData.network_security_groups) {
              if (!sg || typeof sg !== 'object') continue;
              if (matchesSgId(sg)) {
                console.log('findSecurityGroup: MATCH FOUND in top-level network_security_groups!');
                return sg;
              }
              // Match by OCID if available
              if (nsgOcidToSearch) {
                const sgOcid = sg.id || sg.oci_id;
                if (sgOcid && (String(sgOcid) === nsgOcidToSearch || String(sgOcid).includes(nsgOcidToSearch) || nsgOcidToSearch.includes(String(sgOcid)))) {
                  console.log('findSecurityGroup: MATCH FOUND in top-level network_security_groups by OCID!');
                  return sg;
                }
              }
            }
          }
          
          // Structure 4: Flat structure with security_groups at root
          if (Array.isArray(discoveryData.security_groups)) {
            for (const sg of discoveryData.security_groups) {
              if (matchesSgId(sg)) {
                console.log('findSecurityGroup: Found in root security_groups array');
                return sg;
              }
            }
          }
          
          // Structure 5: EC2-Classic security groups (not associated with a VPC)
          if (Array.isArray(discoveryData.ec2_classic_security_groups)) {
            console.log(`findSecurityGroup: Checking ${discoveryData.ec2_classic_security_groups.length} EC2-Classic security groups`);
            for (const sg of discoveryData.ec2_classic_security_groups) {
              if (!sg || typeof sg !== 'object') continue;
              if (matchesSgId(sg)) {
                console.log('findSecurityGroup: Found in ec2_classic_security_groups');
                return sg;
              }
            }
          }
          
          // Last resort: search recursively ONLY in security_groups arrays
          const searchRecursive = (obj: any, depth = 0): any | null => {
            if (depth > 3) return null; // Limit depth
            if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
            
            // Only search in security_groups, network_security_groups, or ec2_classic_security_groups arrays
            const sgArrays = ['security_groups', 'network_security_groups', 'ec2_classic_security_groups'];
            for (const arrayKey of sgArrays) {
              if (Array.isArray(obj[arrayKey])) {
                for (const sg of obj[arrayKey]) {
                  if (sg && typeof sg === 'object' && matchesSgId(sg)) {
                    console.log(`findSecurityGroup: Found via recursive search in ${arrayKey}`);
                    return sg;
                  }
                }
              }
            }
            
            // Recursively search in nested objects (but skip arrays that aren't security_groups)
            for (const key in obj) {
              if (Object.prototype.hasOwnProperty.call(obj, key)) {
                // Skip arrays that aren't security_groups
                if (Array.isArray(obj[key]) && !sgArrays.includes(key)) {
                  continue;
                }
                const result = searchRecursive(obj[key], depth + 1);
                if (result) return result;
              }
            }
            
            return null;
          };
          
          const recursiveResult = searchRecursive(discoveryData);
          if (recursiveResult) {
            console.log('findSecurityGroup: Found via recursive search:', normalizedSgId);
            return recursiveResult;
          }
          
          // Debug: Log what we found
          console.warn('findSecurityGroup: Could not find security group:', normalizedSgId);
          console.log('Discovery data structure:', {
            hasVpcs: !!discoveryData.vpcs,
            vpcsType: typeof discoveryData.vpcs,
            isVpcsArray: Array.isArray(discoveryData.vpcs),
            vpcCount: discoveryData.vpcs 
              ? (Array.isArray(discoveryData.vpcs) ? discoveryData.vpcs.length : Object.keys(discoveryData.vpcs).length)
              : 0,
            firstVpcKey: discoveryData.vpcs && typeof discoveryData.vpcs === 'object' && !Array.isArray(discoveryData.vpcs)
              ? Object.keys(discoveryData.vpcs)[0]
              : null
          });
          
          // Debug: Collect all security group IDs from both AWS and OCI formats
          const allSgIds: string[] = [];
          
          // Check AWS format (vpcs)
          if (discoveryData.vpcs) {
            const vpcKeys = typeof discoveryData.vpcs === 'object' && !Array.isArray(discoveryData.vpcs)
              ? Object.keys(discoveryData.vpcs)
              : [];
            let vpcsWithSgs = 0;
            let totalSgs = 0;
            
            console.log(`findSecurityGroup: Debug - Checking AWS format: ${vpcKeys.length} VPCs`);
            for (const vpcId of vpcKeys) {
              const vpc = discoveryData.vpcs[vpcId];
              if (vpc && typeof vpc === 'object' && Array.isArray(vpc.security_groups)) {
                vpcsWithSgs++;
                totalSgs += vpc.security_groups.length;
                vpc.security_groups.forEach((sg: any) => {
                  if (sg && typeof sg === 'object') {
                    const sgId = sg.id || sg.group_id || sg.GroupId;
                    if (sgId) allSgIds.push(String(sgId));
                  }
                });
              }
            }
            console.log(`findSecurityGroup: Debug - AWS format: ${vpcsWithSgs} VPCs with SGs, total: ${totalSgs} SGs`);
          }
          
          // Check OCI format (vcns)
          if (discoveryData.vcns) {
            const vcnKeys = typeof discoveryData.vcns === 'object' && !Array.isArray(discoveryData.vcns)
              ? Object.keys(discoveryData.vcns)
              : [];
            let vcnsWithNsgs = 0;
            let totalNsgs = 0;
            
            console.log(`findSecurityGroup: Debug - Checking OCI format: ${vcnKeys.length} VCNs`);
            for (const vcnId of vcnKeys) {
              const vcn = discoveryData.vcns[vcnId];
              if (vcn && typeof vcn === 'object' && Array.isArray(vcn.network_security_groups)) {
                vcnsWithNsgs++;
                totalNsgs += vcn.network_security_groups.length;
                vcn.network_security_groups.forEach((nsg: any) => {
                  if (nsg && typeof nsg === 'object') {
                    const nsgId = nsg.id || nsg.oci_id;
                    if (nsgId) allSgIds.push(String(nsgId));
                  }
                });
              }
            }
            console.log(`findSecurityGroup: Debug - OCI format: ${vcnsWithNsgs} VCNs with NSGs, total: ${totalNsgs} NSGs`);
          }
          
          // Check root-level arrays
          if (Array.isArray(discoveryData.security_groups)) {
            console.log(`findSecurityGroup: Debug - Found ${discoveryData.security_groups.length} security groups at root level (AWS)`);
            discoveryData.security_groups.forEach((sg: any) => {
              if (sg && typeof sg === 'object') {
                const sgId = sg.id || sg.group_id || sg.GroupId;
                if (sgId) allSgIds.push(String(sgId));
              }
            });
          }
          if (Array.isArray(discoveryData.ec2_classic_security_groups)) {
            console.log(`findSecurityGroup: Debug - Found ${discoveryData.ec2_classic_security_groups.length} EC2-Classic security groups`);
            discoveryData.ec2_classic_security_groups.forEach((sg: any) => {
              if (sg && typeof sg === 'object') {
                const sgId = sg.id || sg.group_id || sg.GroupId;
                if (sgId) allSgIds.push(String(sgId));
              }
            });
          }
          if (Array.isArray(discoveryData.network_security_groups)) {
            console.log(`findSecurityGroup: Debug - Found ${discoveryData.network_security_groups.length} network security groups at root level (OCI)`);
            discoveryData.network_security_groups.forEach((nsg: any) => {
              if (nsg && typeof nsg === 'object') {
                const nsgId = nsg.id || nsg.oci_id;
                if (nsgId) allSgIds.push(String(nsgId));
              }
            });
          }
          
          console.log(`findSecurityGroup: Debug - Total unique security group IDs found: ${allSgIds.length}`);
          console.log('=== ALL SECURITY GROUP IDs IN DISCOVERY DATA ===');
          allSgIds.forEach((id, idx) => {
            console.log(`  [${idx}] ${id}`);
          });
          console.log('=== END OF SECURITY GROUP IDs ===');
          console.log('Looking for:', normalizedSgId);
          console.log('Exact match found:', allSgIds.includes(normalizedSgId));
          
          // Check for partial matches - be VERY lenient
          const partialMatches = allSgIds.filter(id => {
            const idLower = id.toLowerCase();
            const searchLower = normalizedSgId.toLowerCase();
            return idLower.includes(searchLower) || searchLower.includes(idLower);
          });
          if (partialMatches.length > 0) {
            console.log('⚠️ PARTIAL MATCHES FOUND (should have matched!):', partialMatches);
          } else {
            console.log('❌ NO PARTIAL MATCHES FOUND');
          }
          
          // Show the actual ID we're searching for vs what we found
          console.log('=== SEARCH COMPARISON ===');
          console.log(`Searching for: "${normalizedSgId}"`);
          console.log(`Length: ${normalizedSgId.length}`);
          console.log(`First 10 chars: "${normalizedSgId.substring(0, 10)}"`);
          console.log(`Last 10 chars: "${normalizedSgId.substring(Math.max(0, normalizedSgId.length - 10))}"`);
          if (allSgIds.length > 0) {
            console.log(`Sample found ID: "${allSgIds[0]}"`);
            console.log(`Sample ID length: ${allSgIds[0].length}`);
            console.log(`Sample ID first 10: "${allSgIds[0].substring(0, 10)}"`);
            console.log(`Sample ID last 10: "${allSgIds[0].substring(Math.max(0, allSgIds[0].length - 10))}"`);
          }
          console.log('=== END SEARCH COMPARISON ===');
          
          // LAST RESORT: Try to find by any means necessary - search ALL possible locations again with more lenient matching
          console.log('findSecurityGroup: LAST RESORT - doing exhaustive search with lenient matching');
          
          // Search all VPCs again with case-insensitive contains
          if (discoveryData.vpcs && typeof discoveryData.vpcs === 'object' && !Array.isArray(discoveryData.vpcs)) {
            for (const vpcId in discoveryData.vpcs) {
              const vpc = discoveryData.vpcs[vpcId];
              if (!vpc || typeof vpc !== 'object') continue;
              
              const sgs = vpc.security_groups;
              if (Array.isArray(sgs)) {
                for (const sg of sgs) {
                  if (!sg || typeof sg !== 'object') continue;
                  
                  // Try ALL possible ID fields with case-insensitive contains
                  const allPossibleIds = [
                    sg.id, sg.group_id, sg.GroupId, sg.groupId,
                    sg.security_group_id, sg.SecurityGroupId
                  ].filter(Boolean).map(id => String(id).toLowerCase());
                  
                  const searchLower = normalizedSgId.toLowerCase();
                  
                  for (const possibleId of allPossibleIds) {
                    if (possibleId === searchLower || 
                        possibleId.includes(searchLower) || 
                        searchLower.includes(possibleId)) {
                      console.log(`findSecurityGroup: ✅✅✅ LAST RESORT MATCH! Found by lenient matching: "${possibleId}" matches "${searchLower}"`);
                      return sg;
                    }
                  }
                }
              }
            }
          }
          
          // Search all VCNs again
          if (discoveryData.vcns && typeof discoveryData.vcns === 'object' && !Array.isArray(discoveryData.vcns)) {
            for (const vcnId in discoveryData.vcns) {
              const vcn = discoveryData.vcns[vcnId];
              if (!vcn || typeof vcn !== 'object') continue;
              
              const nsgs = vcn.network_security_groups;
              if (Array.isArray(nsgs)) {
                for (const nsg of nsgs) {
                  if (!nsg || typeof nsg !== 'object') continue;
                  
                  const allPossibleIds = [
                    nsg.id, nsg.oci_id, nsg.ocid, nsg.group_id
                  ].filter(Boolean).map(id => String(id).toLowerCase());
                  
                  const searchLower = normalizedSgId.toLowerCase();
                  
                  for (const possibleId of allPossibleIds) {
                    if (possibleId === searchLower || 
                        possibleId.includes(searchLower) || 
                        searchLower.includes(possibleId)) {
                      console.log(`findSecurityGroup: ✅✅✅ LAST RESORT MATCH in OCI! Found: "${possibleId}" matches "${searchLower}"`);
                      return nsg;
                    }
                  }
                }
              }
            }
          }
          
          console.error(`findSecurityGroup: ❌❌❌ FINAL FAILURE - Could not find security group "${normalizedSgId}" after exhaustive search`);
          console.error(`findSecurityGroup: Discovery data keys:`, Object.keys(discoveryData || {}));
          console.error(`findSecurityGroup: Has vpcs:`, !!discoveryData?.vpcs);
          console.error(`findSecurityGroup: Has vcns:`, !!discoveryData?.vcns);
          
          return null;
        };

        const sgData = findSecurityGroup(selectedSecurityGroup);

        return (
          <GenericModal
            isOpen={!!selectedSecurityGroup}
            onClose={() => setSelectedSecurityGroup(null)}
            title={<span className="break-all font-mono">{`Security Group: ${selectedSecurityGroup}`}</span>}
            size="lg"
          >
            <div className="space-y-4">
              {sgData ? (
                <>
                  <div>
                    <h3 className="font-semibold text-semantic-text-primary mb-2">Basic Information</h3>
                    <div className="space-y-2 text-sm">
                      {sgData.id && (
                        <div className="flex justify-between">
                          <span className="text-semantic-text-secondary">ID:</span>
                          <span className="text-semantic-text-primary font-medium font-mono break-all text-right max-w-[70%]">
                            {sgData.id}
                          </span>
                        </div>
                      )}
                      {sgData.group_name && (
                        <div className="flex justify-between">
                          <span className="text-semantic-text-secondary">Name:</span>
                          <span className="text-semantic-text-primary font-medium break-all text-right max-w-[70%]">
                            {sgData.group_name}
                          </span>
                        </div>
                      )}
                      {sgData.description && (
                        <div className="flex justify-between">
                          <span className="text-semantic-text-secondary">Description:</span>
                          <span className="text-semantic-text-primary break-all text-right max-w-[70%]">
                            {sgData.description}
                          </span>
                        </div>
                      )}
                      {sgData.vpc_id && (
                        <div className="flex justify-between">
                          <span className="text-semantic-text-secondary">VPC ID:</span>
                          <span className="text-semantic-text-primary font-medium font-mono break-all text-right max-w-[70%]">
                            {sgData.vpc_id}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {sgData.ip_permissions && sgData.ip_permissions.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-semantic-text-primary mb-2">Inbound Rules</h3>
                      <div className="space-y-2 text-sm max-h-96 overflow-y-auto">
                        {sgData.ip_permissions.map((rule: any, idx: number) => (
                          <div key={idx} className="border border-slate-200 rounded p-3 bg-slate-50">
                            <div className="font-mono text-xs space-y-1">
                              <div className="flex justify-between">
                                <span className="text-semantic-text-secondary">Protocol:</span>
                                <span className="text-semantic-text-primary font-medium">{rule.IpProtocol || rule.protocol || '-'}</span>
                              </div>
                              {(rule.FromPort !== undefined || rule.ToPort !== undefined) && (
                                <div className="flex justify-between">
                                  <span className="text-semantic-text-secondary">Port Range:</span>
                                  <span className="text-semantic-text-primary font-medium">
                                    {rule.FromPort === rule.ToPort ? rule.FromPort : `${rule.FromPort || 'Any'}-${rule.ToPort || 'Any'}`}
                                  </span>
                                </div>
                              )}
                              {rule.IpRanges && rule.IpRanges.length > 0 && (
                                <div className="flex flex-col gap-1">
                                  <span className="text-semantic-text-secondary">IP Ranges:</span>
                                  {rule.IpRanges.map((range: any, rIdx: number) => (
                                    <span key={rIdx} className="text-semantic-text-primary font-medium">
                                      {range.CidrIp || range.cidr || range}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {rule.UserIdGroupPairs && rule.UserIdGroupPairs.length > 0 && (
                                <div className="flex flex-col gap-1">
                                  <span className="text-semantic-text-secondary">Security Groups:</span>
                                  {rule.UserIdGroupPairs.map((pair: any, pIdx: number) => (
                                    <span key={pIdx} className="text-semantic-text-primary font-medium font-mono">
                                      {pair.GroupId || pair.group_id || pair}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {sgData.ip_permissions_egress && sgData.ip_permissions_egress.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-semantic-text-primary mb-2">Outbound Rules</h3>
                      <div className="space-y-2 text-sm max-h-96 overflow-y-auto">
                        {sgData.ip_permissions_egress.map((rule: any, idx: number) => (
                          <div key={idx} className="border border-slate-200 rounded p-3 bg-slate-50">
                            <div className="font-mono text-xs space-y-1">
                              <div className="flex justify-between">
                                <span className="text-semantic-text-secondary">Protocol:</span>
                                <span className="text-semantic-text-primary font-medium">{rule.IpProtocol || rule.protocol || '-'}</span>
                              </div>
                              {(rule.FromPort !== undefined || rule.ToPort !== undefined) && (
                                <div className="flex justify-between">
                                  <span className="text-semantic-text-secondary">Port Range:</span>
                                  <span className="text-semantic-text-primary font-medium">
                                    {rule.FromPort === rule.ToPort ? rule.FromPort : `${rule.FromPort || 'Any'}-${rule.ToPort || 'Any'}`}
                                  </span>
                                </div>
                              )}
                              {rule.IpRanges && rule.IpRanges.length > 0 && (
                                <div className="flex flex-col gap-1">
                                  <span className="text-semantic-text-secondary">IP Ranges:</span>
                                  {rule.IpRanges.map((range: any, rIdx: number) => (
                                    <span key={rIdx} className="text-semantic-text-primary font-medium">
                                      {range.CidrIp || range.cidr || range}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {rule.UserIdGroupPairs && rule.UserIdGroupPairs.length > 0 && (
                                <div className="flex flex-col gap-1">
                                  <span className="text-semantic-text-secondary">Security Groups:</span>
                                  {rule.UserIdGroupPairs.map((pair: any, pIdx: number) => (
                                    <span key={pIdx} className="text-semantic-text-primary font-medium font-mono">
                                      {pair.GroupId || pair.group_id || pair}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-semantic-text-secondary p-4 bg-yellow-50 border border-yellow-200 rounded">
                  <strong className="text-red-600 block mb-2">Security group details not found in discovery data.</strong>
                  <div className="mt-2 p-2 bg-white rounded border border-red-200">
                    <div className="font-mono text-xs">
                      <div className="font-semibold mb-1">Searching for:</div>
                      <div className="break-all">{selectedSecurityGroup}</div>
                      <div className="text-slate-500 mt-1">Length: {selectedSecurityGroup?.length || 0} characters</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs">
                    <strong>Debug Info:</strong>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Discovery data loaded: {discoveryData ? 'Yes' : 'No'}</li>
                      {discoveryData && (
                        <>
                          <li>Has vpcs: {discoveryData.vpcs ? 'Yes' : 'No'}</li>
                          <li>Has vcns: {discoveryData.vcns ? 'Yes' : 'No'}</li>
                          <li>VPCs type: {typeof discoveryData.vpcs}</li>
                          {discoveryData.vpcs && typeof discoveryData.vpcs === 'object' && !Array.isArray(discoveryData.vpcs) && (
                            <li>VPC count: {Object.keys(discoveryData.vpcs).length}</li>
                          )}
                          {discoveryData.vpcs && Array.isArray(discoveryData.vpcs) && (
                            <li>VPC count: {discoveryData.vpcs.length}</li>
                          )}
                          {discoveryData.vcns && typeof discoveryData.vcns === 'object' && !Array.isArray(discoveryData.vcns) && (
                            <li>VCN count: {Object.keys(discoveryData.vcns).length}</li>
                          )}
                        </>
                      )}
                    </ul>
                    <div className="mt-3 p-2 bg-slate-100 rounded text-xs">
                      <strong className="block mb-1">Troubleshooting:</strong>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Open browser console (F12) and look for logs starting with &quot;🔍 findSecurityGroup&quot;</li>
                        <li>Check if the security group ID in the console matches the ID shown above</li>
                        <li>Verify discovery.json contains security groups for this VPC/VCN</li>
                        <li>Check if discovery.json is from the same account/region as the topology</li>
                      </ol>
                    </div>
                    <div className="mt-2 text-xs text-slate-600">
                      <strong>Note:</strong> The console will show all security group IDs found in discovery data and detailed search logs.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </GenericModal>
        );
      })()}

      {/* Network Security Group Details Modal (OCI) */}
      {selectedNetworkSecurityGroup && (
        <GenericModal
          isOpen={!!selectedNetworkSecurityGroup}
          onClose={() => setSelectedNetworkSecurityGroup(null)}
          title={
            <span className="break-all font-mono">
              {`Network Security Group: ${
                selectedNetworkSecurityGroup?.display_name ||
                selectedNetworkSecurityGroup?.displayName ||
                selectedNetworkSecurityGroup?.id ||
                'unknown'
              }`}
            </span>
          }
          size="lg"
        >
          {(() => {
            const formatProtocol = (proto: any) => {
              const p = String(proto ?? '').trim();
              if (p === '' || p === 'all' || p === '-1') return 'ALL';
              if (p === '6' || p.toLowerCase() === 'tcp') return 'TCP';
              if (p === '17' || p.toLowerCase() === 'udp') return 'UDP';
              if (p === '1' || p.toLowerCase() === 'icmp') return 'ICMP';
              return p.toUpperCase();
            };

            const formatPortRange = (range: any) => {
              if (!range || typeof range !== 'object') return null;
              const min = range.min ?? range.minimum;
              const max = range.max ?? range.maximum;
              if (min == null && max == null) return null;
              if (min != null && max != null && String(min) === String(max)) return String(min);
              return `${min ?? '—'}-${max ?? '—'}`;
            };

            const extractPorts = (rule: any) => {
              const tcp = rule?.tcp_options || rule?.tcpOptions || null;
              const udp = rule?.udp_options || rule?.udpOptions || null;
              const dest =
                formatPortRange(tcp?.destination_port_range || tcp?.destinationPortRange) ||
                formatPortRange(udp?.destination_port_range || udp?.destinationPortRange) ||
                null;
              const src =
                formatPortRange(tcp?.source_port_range || tcp?.sourcePortRange) ||
                formatPortRange(udp?.source_port_range || udp?.sourcePortRange) ||
                null;
              return { dest, src };
            };

            const extractIcmp = (rule: any) => {
              const icmp = rule?.icmp_options || rule?.icmpOptions || null;
              if (!icmp || typeof icmp !== 'object') return null;
              const type = icmp.type ?? icmp.icmpType;
              const code = icmp.code ?? icmp.icmpCode;
              if (type == null && code == null) return null;
              if (type != null && code != null) return `type ${type}, code ${code}`;
              if (type != null) return `type ${type}`;
              return `code ${code}`;
            };

            const isStateless = (rule: any) => {
              const v = rule?.is_stateless ?? rule?.isStateless ?? rule?.stateless;
              return v === true;
            };

            const renderRule = (rule: any, idx: number, direction: string) => {
              const protocol = formatProtocol(rule?.protocol);
              const desc = rule?.description || rule?.display_name || rule?.name || null;
              const endpoint = direction === 'INGRESS' ? (rule?.source || null) : (rule?.destination || null);
              const endpointType = direction === 'INGRESS' ? (rule?.source_type || null) : (rule?.destination_type || null);
              const { dest, src } = extractPorts(rule);
              const icmp = extractIcmp(rule);

              return (
                <div key={idx} className="p-3 bg-slate-50 rounded border">
                  <div className="flex justify-between">
                    <span className="text-semantic-text-secondary">Direction:</span>
                    <span className="text-semantic-text-primary font-medium">{direction}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-semantic-text-secondary">Protocol:</span>
                    <span className="text-semantic-text-primary font-medium font-mono">{protocol}</span>
                  </div>
                  {endpoint && (
                    <div className="flex justify-between">
                      <span className="text-semantic-text-secondary">
                        {direction === 'INGRESS' ? 'Source:' : 'Destination:'}
                      </span>
                      <span className="text-semantic-text-primary font-medium font-mono break-all text-right max-w-[70%]">
                        {endpoint}
                        {endpointType ? <span className="text-slate-500"> ({endpointType})</span> : null}
                      </span>
                    </div>
                  )}
                  {(dest || src) && (
                    <>
                      {dest && (
                        <div className="flex justify-between">
                          <span className="text-semantic-text-secondary">Dest Port(s):</span>
                          <span className="text-semantic-text-primary font-medium font-mono">{dest}</span>
                        </div>
                      )}
                      {src && (
                        <div className="flex justify-between">
                          <span className="text-semantic-text-secondary">Source Port(s):</span>
                          <span className="text-semantic-text-primary font-medium font-mono">{src}</span>
                        </div>
                      )}
                    </>
                  )}
                  {icmp && (
                    <div className="flex justify-between">
                      <span className="text-semantic-text-secondary">ICMP:</span>
                      <span className="text-semantic-text-primary font-medium font-mono">{icmp}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-semantic-text-secondary">Stateless:</span>
                    <span className="text-semantic-text-primary font-medium">{isStateless(rule) ? 'Yes' : 'No'}</span>
                  </div>
                  {desc && (
                    <div className="mt-2 text-xs text-slate-700">
                      <span className="font-semibold text-slate-600">Description:</span> {String(desc)}
                    </div>
                  )}
                  <details className="mt-2 border-t border-slate-200 pt-2">
                    <summary className="text-semantic-text-secondary cursor-pointer text-xs font-semibold hover:text-semantic-text-primary">
                      View raw rule (JSON)
                    </summary>
                    <pre className="mt-2 text-xs bg-white p-3 rounded border overflow-auto max-h-48">
                      {JSON.stringify(rule, null, 2)}
                    </pre>
                  </details>
                </div>
              );
            };

            const rules = Array.isArray(selectedNetworkSecurityGroup?.security_rules)
              ? selectedNetworkSecurityGroup.security_rules
              : [];
            const ingressRules = rules.filter((r: any) => String(r?.direction || '').toUpperCase() === 'INGRESS');
            const egressRules = rules.filter((r: any) => String(r?.direction || '').toUpperCase() === 'EGRESS');

            return (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-semantic-text-primary mb-2">Basic Information</h3>
                  <div className="space-y-2 text-sm">
                    {selectedNetworkSecurityGroup?.id && (
                      <div className="flex justify-between">
                        <span className="text-semantic-text-secondary">OCID:</span>
                        <span className="text-semantic-text-primary font-medium font-mono break-all text-right max-w-[70%]">
                          {selectedNetworkSecurityGroup.id}
                        </span>
                      </div>
                    )}
                    {(selectedNetworkSecurityGroup?.display_name || selectedNetworkSecurityGroup?.displayName) && (
                      <div className="flex justify-between">
                        <span className="text-semantic-text-secondary">Name:</span>
                        <span className="text-semantic-text-primary font-medium break-all text-right max-w-[70%]">
                          {selectedNetworkSecurityGroup.display_name || selectedNetworkSecurityGroup.displayName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-semantic-text-primary mb-2">Ingress Rules</h3>
                  {ingressRules.length > 0 ? (
                    <div className="space-y-3 text-sm max-h-80 overflow-y-auto">
                      {ingressRules.map((r: any, i: number) => renderRule(r, i, 'INGRESS'))}
                    </div>
                  ) : (
                    <div className="text-sm text-semantic-text-secondary italic p-4 bg-yellow-50 border border-yellow-200 rounded">
                      No ingress rules found.
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="font-semibold text-semantic-text-primary mb-2">Egress Rules</h3>
                  {egressRules.length > 0 ? (
                    <div className="space-y-3 text-sm max-h-80 overflow-y-auto">
                      {egressRules.map((r: any, i: number) => renderRule(r, i, 'EGRESS'))}
                    </div>
                  ) : (
                    <div className="text-sm text-semantic-text-secondary italic p-4 bg-yellow-50 border border-yellow-200 rounded">
                      No egress rules found.
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </GenericModal>
      )}

      {/* Security List Details Modal (OCI) */}
      {selectedSecurityLists && (
        <GenericModal
          isOpen={!!selectedSecurityLists}
          onClose={() => setSelectedSecurityLists(null)}
          title={<span className="break-all font-mono">Security List</span>}
          size="lg"
        >
          {(() => {
            const formatProtocol = (proto: any) => {
              const p = String(proto ?? '').trim();
              if (p === '' || p === 'all' || p === '-1') return 'ALL';
              if (p === '6' || p.toLowerCase() === 'tcp') return 'TCP';
              if (p === '17' || p.toLowerCase() === 'udp') return 'UDP';
              if (p === '1' || p.toLowerCase() === 'icmp') return 'ICMP';
              return p.toUpperCase();
            };

            const formatPortRange = (range: any) => {
              if (!range || typeof range !== 'object') return null;
              const min = range.min ?? range.minimum;
              const max = range.max ?? range.maximum;
              if (min == null && max == null) return null;
              if (min != null && max != null && String(min) === String(max)) return String(min);
              return `${min ?? '—'}-${max ?? '—'}`;
            };

            const extractPorts = (rule: any) => {
              const tcp = rule?.tcp_options || rule?.tcpOptions || null;
              const udp = rule?.udp_options || rule?.udpOptions || null;
              const dest =
                formatPortRange(tcp?.destination_port_range || tcp?.destinationPortRange) ||
                formatPortRange(udp?.destination_port_range || udp?.destinationPortRange) ||
                null;
              const src =
                formatPortRange(tcp?.source_port_range || tcp?.sourcePortRange) ||
                formatPortRange(udp?.source_port_range || udp?.sourcePortRange) ||
                null;
              return { dest, src };
            };

            const extractIcmp = (rule: any) => {
              const icmp = rule?.icmp_options || rule?.icmpOptions || null;
              if (!icmp || typeof icmp !== 'object') return null;
              const type = icmp.type ?? icmp.icmpType;
              const code = icmp.code ?? icmp.icmpCode;
              if (type == null && code == null) return null;
              if (type != null && code != null) return `type ${type}, code ${code}`;
              if (type != null) return `type ${type}`;
              return `code ${code}`;
            };

            const isStateless = (rule: any) => {
              const v = rule?.is_stateless ?? rule?.isStateless ?? rule?.stateless;
              return v === true;
            };

            const renderRule = (rule: any, idx: number, direction: 'INGRESS' | 'EGRESS') => {
              const protocol = formatProtocol(rule?.protocol);
              const desc = rule?.description || null;
              const endpoint = direction === 'INGRESS' ? (rule?.source || null) : (rule?.destination || null);
              const endpointType = direction === 'INGRESS' ? (rule?.source_type || null) : (rule?.destination_type || null);
              const { dest, src } = extractPorts(rule);
              const icmp = extractIcmp(rule);

              return (
                <div key={idx} className="p-3 bg-slate-50 rounded border">
                  <div className="flex justify-between">
                    <span className="text-semantic-text-secondary">Protocol:</span>
                    <span className="text-semantic-text-primary font-medium font-mono">{protocol}</span>
                  </div>
                  {endpoint && (
                    <div className="flex justify-between">
                      <span className="text-semantic-text-secondary">
                        {direction === 'INGRESS' ? 'Source:' : 'Destination:'}
                      </span>
                      <span className="text-semantic-text-primary font-medium font-mono break-all text-right max-w-[70%]">
                        {endpoint}
                        {endpointType ? <span className="text-slate-500"> ({endpointType})</span> : null}
                      </span>
                    </div>
                  )}
                  {(dest || src) && (
                    <>
                      {dest && (
                        <div className="flex justify-between">
                          <span className="text-semantic-text-secondary">Dest Port(s):</span>
                          <span className="text-semantic-text-primary font-medium font-mono">{dest}</span>
                        </div>
                      )}
                      {src && (
                        <div className="flex justify-between">
                          <span className="text-semantic-text-secondary">Source Port(s):</span>
                          <span className="text-semantic-text-primary font-medium font-mono">{src}</span>
                        </div>
                      )}
                    </>
                  )}
                  {icmp && (
                    <div className="flex justify-between">
                      <span className="text-semantic-text-secondary">ICMP:</span>
                      <span className="text-semantic-text-primary font-medium font-mono">{icmp}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-semantic-text-secondary">Stateless:</span>
                    <span className="text-semantic-text-primary font-medium">{isStateless(rule) ? 'Yes' : 'No'}</span>
                  </div>
                  {desc && (
                    <div className="mt-2 text-xs text-slate-700">
                      <span className="font-semibold text-slate-600">Description:</span> {String(desc)}
                    </div>
                  )}
                  <details className="mt-2 border-t border-slate-200 pt-2">
                    <summary className="text-semantic-text-secondary cursor-pointer text-xs font-semibold hover:text-semantic-text-primary">
                      View raw rule (JSON)
                    </summary>
                    <pre className="mt-2 text-xs bg-white p-3 rounded border overflow-auto max-h-48">
                      {JSON.stringify(rule, null, 2)}
                    </pre>
                  </details>
                </div>
              );
            };

            const renderRulesSection = (title: string, rules: any[], direction: 'INGRESS' | 'EGRESS') => (
              <div className="mt-3">
                <div className="font-semibold text-slate-700 mb-2">{title}</div>
                {rules.length > 0 ? (
                  <div className="space-y-3 text-sm max-h-72 overflow-y-auto">
                    {rules.map((r: any, i: number) => renderRule(r, i, direction))}
                  </div>
                ) : (
                  <div className="text-sm text-semantic-text-secondary italic p-3 bg-yellow-50 border border-yellow-200 rounded">
                    No {direction.toLowerCase()} rules.
                  </div>
                )}
              </div>
            );

            return (
              <div className="space-y-4">
                {selectedSecurityLists.length === 0 ? (
                  <div className="text-sm text-semantic-text-secondary italic p-4 bg-yellow-50 border border-yellow-200 rounded">
                    No security lists found for this subnet.
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                    {selectedSecurityLists.map((sl: any, idx: number) => {
                      const slData = sl?.sl_data || sl;
                      const ingress = Array.isArray(slData?.ingress_security_rules) ? slData.ingress_security_rules : [];
                      const egress = Array.isArray(slData?.egress_security_rules) ? slData.egress_security_rules : [];
                      return (
                        <div key={idx} className="p-3 bg-white rounded border border-slate-200">
                          <div className="flex justify-between gap-3">
                            <div className="font-semibold text-slate-800 break-all">
                              {sl?.name || slData?.display_name || slData?.displayName || 'Security List'}
                            </div>
                            <div className="text-xs text-slate-500 font-mono break-all text-right">
                              {sl?.oci_id || slData?.id || ''}
                            </div>
                          </div>

                          {/* Vertical layout: Ingress then Egress */}
                          {renderRulesSection('Ingress Rules', ingress, 'INGRESS')}
                          {renderRulesSection('Egress Rules', egress, 'EGRESS')}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </GenericModal>
      )}

      {/* Network ACL Details Modal (AWS) */}
      {selectedNetworkAcl && (
        <GenericModal
          isOpen={!!selectedNetworkAcl}
          onClose={() => setSelectedNetworkAcl(null)}
          title={
            <span className="break-all font-mono">
              {`Network ACL: ${selectedNetworkAcl?.name || selectedNetworkAcl?.id || 'unknown'}`}
            </span>
          }
          size="lg"
        >
          {(() => {
            const formatProtocol = (proto: any) => {
              const p = String(proto ?? '').trim();
              if (p === '' || p === 'all' || p === '-1') return 'ALL';
              if (p === '6' || p.toLowerCase() === 'tcp') return 'TCP';
              if (p === '17' || p.toLowerCase() === 'udp') return 'UDP';
              if (p === '1' || p.toLowerCase() === 'icmp') return 'ICMP';
              return p.toUpperCase();
            };

            const formatPortRange = (range: any) => {
              if (!range || typeof range !== 'object') return null;
              const from = range.From ?? range.from ?? range.min ?? range.minimum;
              const to = range.To ?? range.to ?? range.max ?? range.maximum;
              if (from == null && to == null) return null;
              if (from != null && to != null && String(from) === String(to)) return String(from);
              return `${from ?? '—'}-${to ?? '—'}`;
            };

            const formatIcmp = (icmp: any) => {
              if (!icmp || typeof icmp !== 'object') return null;
              const type = icmp.Type ?? icmp.type;
              const code = icmp.Code ?? icmp.code;
              if (type == null && code == null) return null;
              if (type != null && code != null) return `type ${type}, code ${code}`;
              if (type != null) return `type ${type}`;
              return `code ${code}`;
            };

            const entries = Array.isArray(selectedNetworkAcl?.entries) ? selectedNetworkAcl.entries : [];
            const inbound = entries
              .filter((e: any) => e && (e.Egress === false || e.egress === false))
              .sort((a: any, b: any) => Number(a.RuleNumber ?? a.ruleNumber ?? 0) - Number(b.RuleNumber ?? b.ruleNumber ?? 0));
            const outbound = entries
              .filter((e: any) => e && (e.Egress === true || e.egress === true))
              .sort((a: any, b: any) => Number(a.RuleNumber ?? a.ruleNumber ?? 0) - Number(b.RuleNumber ?? b.ruleNumber ?? 0));

            const renderEntry = (entry: any, idx: number, dir: 'INBOUND' | 'OUTBOUND') => {
              const ruleNumber = entry.RuleNumber ?? entry.ruleNumber ?? '—';
              const action = String(entry.RuleAction ?? entry.ruleAction ?? '—').toUpperCase();
              const protocol = formatProtocol(entry.Protocol ?? entry.protocol);
              const cidr = entry.CidrBlock || entry.cidrBlock || entry.Ipv6CidrBlock || entry.ipv6CidrBlock || '—';
              const port = formatPortRange(entry.PortRange || entry.portRange);
              const icmp = formatIcmp(entry.IcmpTypeCode || entry.icmpTypeCode);

              return (
                <div key={idx} className="p-3 bg-slate-50 rounded border dark:bg-semantic-bg-tertiary dark:border-semantic-border-primary">
                  <div className="flex justify-between">
                    <span className="text-semantic-text-secondary">Direction:</span>
                    <span className="text-semantic-text-primary font-medium">{dir}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-semantic-text-secondary">Rule #:</span>
                    <span className="text-semantic-text-primary font-medium font-mono">{String(ruleNumber)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-semantic-text-secondary">Action:</span>
                    <span className="text-semantic-text-primary font-medium">{action}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-semantic-text-secondary">Protocol:</span>
                    <span className="text-semantic-text-primary font-medium font-mono">{protocol}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-semantic-text-secondary">CIDR:</span>
                    <span className="text-semantic-text-primary font-medium font-mono break-all text-right max-w-[70%]">
                      {cidr}
                    </span>
                  </div>
                  {port && (
                    <div className="flex justify-between">
                      <span className="text-semantic-text-secondary">Port(s):</span>
                      <span className="text-semantic-text-primary font-medium font-mono">{port}</span>
                    </div>
                  )}
                  {icmp && (
                    <div className="flex justify-between">
                      <span className="text-semantic-text-secondary">ICMP:</span>
                      <span className="text-semantic-text-primary font-medium font-mono">{icmp}</span>
                    </div>
                  )}
                  <details className="mt-2 border-t border-slate-200 pt-2 dark:border-semantic-border-primary">
                    <summary className="text-semantic-text-secondary cursor-pointer text-xs font-semibold hover:text-semantic-text-primary">
                      View raw entry (JSON)
                    </summary>
                    <pre className="mt-2 text-xs bg-white p-3 rounded border overflow-auto max-h-48 dark:bg-semantic-bg-tertiary dark:border-semantic-border-primary dark:text-semantic-text-primary">
                      {JSON.stringify(entry, null, 2)}
                    </pre>
                  </details>
                </div>
              );
            };

            const renderSection = (title: string, list: any[], dir: 'INBOUND' | 'OUTBOUND') => (
              <div>
                <h3 className="font-semibold text-semantic-text-primary mb-2">{title}</h3>
                {list.length > 0 ? (
                  <div className="space-y-3 text-sm max-h-80 overflow-y-auto">
                    {list.map((e: any, i: number) => renderEntry(e, i, dir))}
                  </div>
                ) : (
                  <div className="text-sm text-semantic-text-secondary italic p-4 bg-yellow-50 border border-yellow-200 rounded dark:bg-semantic-bg-warning-secondary/20 dark:border-semantic-border-warning-primary">
                    No {dir.toLowerCase()} entries found.
                  </div>
                )}
              </div>
            );

            return (
              <div className="space-y-4">
                {selectedNetworkAcl?.id && (
                  <div className="text-sm">
                    <span className="text-semantic-text-secondary">NACL ID: </span>
                    <span className="font-mono text-semantic-text-primary break-all">{selectedNetworkAcl.id}</span>
                  </div>
                )}
                {renderSection('Inbound Entries', inbound, 'INBOUND')}
                {renderSection('Outbound Entries', outbound, 'OUTBOUND')}
              </div>
            );
          })()}
        </GenericModal>
      )}

      {/* =============================================================================
          TOPOLOGY SEARCH DIALOG
          ============================================================================= */}
      {topologySearchOpen && (
        <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh]">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setTopologySearchOpen(false)}
          />
          
          {/* Search Dialog */}
          <div className="relative w-full max-w-2xl bg-semantic-bg-secondary rounded-2xl shadow-2xl overflow-hidden border border-semantic-border-primary">
            {/* Input */}
            <div className="flex items-center px-4 py-3 border-b border-semantic-border-primary">
              <Search className="w-5 h-5 text-semantic-text-secondary mr-3" />
              <input
                ref={topologySearchInputRef}
                type="text"
                placeholder="Search subnets, instances, IPs, CIDRs..."
                value={topologySearchQuery}
                onChange={(e) => setTopologySearchQuery(e.target.value)}
                className="flex-1 text-lg outline-none bg-transparent text-semantic-text-primary placeholder:text-semantic-text-secondary"
                autoFocus
              />
              <div className="flex items-center gap-2 text-xs text-semantic-text-secondary">
                <kbd className="px-1.5 py-0.5 bg-semantic-bg-tertiary rounded text-semantic-text-primary">up/down</kbd>
                <span>navigate</span>
                <kbd className="px-1.5 py-0.5 bg-semantic-bg-tertiary rounded text-semantic-text-primary">enter</kbd>
                <span>select</span>
                <kbd className="px-1.5 py-0.5 bg-semantic-bg-tertiary rounded text-semantic-text-primary">esc</kbd>
                <span>close</span>
              </div>
            </div>
            
            {/* Results */}
            <div className="max-h-[400px] overflow-y-auto">
              {topologySearchQuery && topologySearchResults.length === 0 && (
                <div className="px-4 py-8 text-center text-semantic-text-secondary">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No results found for &quot;{topologySearchQuery}&quot;</p>
                </div>
              )}
              
              {topologySearchResults.map((result, index) => {
                // Icons by result type
                const TypeIcon = () => {
                  const icons: Record<SearchResultType, React.ReactNode> = {
                    subnet: <Layers className="w-4 h-4 text-semantic-text-brand" />,
                    eni: <Network className="w-4 h-4 text-semantic-text-success" />,
                    instance: <Server className="w-4 h-4 text-semantic-text-brand" />,
                    sg: <Shield className="w-4 h-4 text-semantic-text-warning" />,
                    rtb: <GitBranch className="w-4 h-4 text-semantic-text-brand" />,
                    natgw: <ArrowUpRight className="w-4 h-4 text-semantic-text-warning" />,
                    igw: <Globe className="w-4 h-4 text-semantic-text-brand" />,
                    vpc: <Cloud className="w-4 h-4 text-semantic-text-secondary" />,
                    az: <MapPin className="w-4 h-4 text-semantic-text-secondary" />,
                  };
                  return <>{icons[result.type] || <Search className="w-4 h-4" />}</>;
                };
                
                return (
                  <div
                    key={result.nodeId}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                      index === topologySearchIndex 
                        ? 'bg-semantic-bg-brand-secondary border-l-2 border-semantic-border-brand-secondary' 
                        : 'hover:bg-semantic-bg-tertiary'
                    }`}
                    onClick={() => {
                      zoomToNode(result.nodeId);
                      setTopologySearchOpen(false);
                    }}
                    onMouseEnter={() => setTopologySearchIndex(index)}
                  >
                    <TypeIcon />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-semantic-text-primary truncate">{result.label}</p>
                      {result.sublabel && (
                        <p className="text-sm text-semantic-text-secondary font-mono">{result.sublabel}</p>
                      )}
                    </div>
                    <span className="text-xs px-2 py-0.5 bg-semantic-bg-tertiary text-semantic-text-secondary rounded-full capitalize">
                      {result.type}
                    </span>
                    <span className="text-xs text-semantic-text-secondary">
                      matched: {result.matchField}
                    </span>
                  </div>
                );
              })}
            </div>
            
            {/* Footer hint */}
            {!topologySearchQuery && (
              <div className="px-4 py-3 bg-semantic-bg-tertiary text-sm text-semantic-text-secondary border-t border-semantic-border-secondary">
                <p>Search by name, CIDR, IP address, or resource ID</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mini navigation bar when there are search results and dialog is closed */}
      {!topologySearchOpen && highlightedSearchNodeId && topologySearchResults.length > 1 && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[150] flex items-center gap-2 px-4 py-2 bg-semantic-bg-secondary rounded-full shadow-lg border border-semantic-border-primary">
          <span className="text-sm text-semantic-text-primary max-w-[200px] truncate">
            {topologySearchResults[topologySearchIndex]?.label}
          </span>
          <span className="text-xs text-semantic-text-secondary">
            {topologySearchIndex + 1} / {topologySearchResults.length}
          </span>
          <div className="flex items-center gap-1 ml-2">
            <button
              onClick={() => {
                const newIndex = Math.max(0, topologySearchIndex - 1);
                setTopologySearchIndex(newIndex);
                zoomToNode(topologySearchResults[newIndex].nodeId);
              }}
              disabled={topologySearchIndex === 0}
              className="p-1 rounded hover:bg-semantic-bg-tertiary disabled:opacity-30 text-semantic-text-primary"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                const newIndex = Math.min(topologySearchResults.length - 1, topologySearchIndex + 1);
                setTopologySearchIndex(newIndex);
                zoomToNode(topologySearchResults[newIndex].nodeId);
              }}
              disabled={topologySearchIndex === topologySearchResults.length - 1}
              className="p-1 rounded hover:bg-semantic-bg-tertiary disabled:opacity-30 text-semantic-text-primary"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={() => {
              setHighlightedSearchNodeId(null);
              setTopologySearchResults([]);
            }}
            className="p-1 rounded hover:bg-semantic-bg-tertiary ml-2 text-semantic-text-primary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* AI Assistant Panel */}
      <AIAssistantPanel
        isOpen={isAIOpen}
        isLoading={isAILoading}
        messages={aiMessages}
        error={aiError}
        onSendMessage={sendMessage}
        onToggle={toggleAI}
        onClear={clearChat}
        globalMode={globalMode}
        onToggleGlobalMode={toggleGlobalMode}
        loadingGlobal={loadingGlobal}
        vpcCount={vpcCount}
      />
    </div>
  );
}
